/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect, useCallback } from 'react';
import {
  MdDashboard, MdLocalHospital, MdCalendarToday, MdPeople, MdPersonAdd,
  MdTrendingUp, MdWarning, MdMedicalServices, MdAdd, MdDelete,
  MdLockReset, MdMarkEmailRead, MdToggleOn, MdToggleOff, MdRefresh, MdSearch,
  MdClose, MdAdminPanelSettings, MdLogout, MdEdit, MdSave,
  MdCheckCircle, MdCancel, MdPhone, MdEmail, MdLocationCity,
  MdArrowBack, MdAccountBalance, MdLocalAtm, MdVerified,
  MdShield, MdSupportAgent, MdCampaign, MdThumbUp, MdThumbDown,
  MdPublic, MdPublicOff, MdInsights, MdBarChart,
  MdAccessTime, MdLocationOn, MdPayments, MdSettings,
  MdCardGiftcard, MdImageNotSupported, MdAssignmentReturn, MdContactMail,
  MdManageAccounts, MdVpnKey, MdSecurity, MdContentCopy,
} from 'react-icons/md';
import { adminAPI, adminSettlementAPI, adminApplicationsAPI, adminOffersAPI, adminTicketsAPI, adminReferralsAPI, adminContactInquiriesAPI } from '../../services/api';
import LocationMapPicker from '../../components/LocationMapPicker';
import AdminTransactions from './AdminTransactions';
import AdminRefunds      from './AdminRefunds';
import RefundPolicyCard  from './RefundPolicyCard';
import AdminApplicationReview from './AdminApplicationReview';
import AdminTickets   from './AdminTickets';
import AdminReferrals from './AdminReferrals';
import AdminContactInquiries from './AdminContactInquiries';
import { getAdminReviewerName, setAdminReviewerName } from '../../utils/adminReviewer';

// ── Helpers ───────────────────────────────────────────────────────
const fmt  = (n) => (Number(n)||0).toLocaleString('en-IN');
const fmtR = (n) => `₹${fmt(n)}`;

// Unified doctor on/off-duty resolver. Different writers store the flag
// under either `isAvailable` (admin lambda) or `available` (hospital lambda
// + patient app). A doctor created via the hospital portal would only have
// `available=true` and used to show "unavailable" in admin, while a doctor
// toggled via admin would only have `isAvailable=false` and still show
// "On duty" in the hospital portal. Prefer whichever flag is explicitly
// present and default to TRUE so legacy rows missing both flags are not
// punished.
const isDoctorAvailable = (d) => {
  if (!d) return false;
  if (typeof d.isAvailable === 'boolean') return d.isAvailable;
  if (typeof d.available   === 'boolean') return d.available;
  return true;
};

// Prefer the doctor's CURRENT consultation fee over the historical fee that
// was baked into the booking record. This way the admin revenue figures match
// what hospitals see on the doctor card right now (e.g. doctor card shows ₹3
// → 1 patient → revenue ₹3, not ₹1 from when fee was originally ₹1).
const bookingFee = (b, doctorsList = []) => {
  if (b && b.doctorId && Array.isArray(doctorsList) && doctorsList.length) {
    const d = doctorsList.find(x => x && x.doctorId === b.doctorId);
    const cur = d ? Number(d.consultationFee || d.fee || 0) : 0;
    if (cur > 0) return cur;
  }
  return Number((b && (b.consultationFee || b.fee)) || 0);
};
const fmtD = (s) => s ? new Date(s).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

// Predefined lists for hospital creation (prevents spelling mistakes, standardises data)
const ALL_DEPTS = [
  'General Medicine','Cardiology','Neurology','Orthopedics & Spine',
  'Pediatrics & Neonatology','Gynecology & Obstetrics','Ophthalmology','ENT',
  'Dermatology','Psychiatry & Mental Health','Oncology','Nephrology & Dialysis',
  'Gastroenterology','Pulmonology','Endocrinology & Diabetes','Urology',
  'Rheumatology','Hematology','Emergency & Trauma','General Surgery',
  'Dental & Oral Health','Radiology & Imaging','Pathology & Lab',
  'Physiotherapy & Rehabilitation','Anesthesiology','Nutrition & Dietetics',
];
const ALL_EQUIP = [
  'MRI Scanner','CT Scanner','X-Ray','Ultrasound','PET Scan','Ventilator',
  'ICU','NICU','Dialysis Unit','Operation Theatre','Ambulance Service',
  'Blood Bank','Pharmacy','Cath Lab','Lab & Pathology','Burns Unit',
  'Endoscopy Unit','24/7 Emergency','Physiotherapy Unit','Laparoscopy',
];

// CRITICAL FIX: handle DynamoDB Set type + Array + undefined
const toArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (v instanceof Set) return Array.from(v);
  if (typeof v === 'string') return [v];
  return [];
};

// DynamoDB stores bankVerified as BOOL (true) or legacy string "true".
// UI previously checked only === 'true', so Verify succeeded but stayed Pending.
const isBankVerified = (h) => {
  if (!h) return false;
  const v = h.bankVerified;
  return v === true || v === 'true' || v === 1 || v === '1';
};
const sc = (s='') => {
  s = String(s).toLowerCase();
  if (['confirmed','accepted','active','handled','available','verified','completed','disbursed'].includes(s)) return '#10b981';
  if (['cancelled','canceled','rejected','inactive','unavailable'].includes(s)) return '#ef4444';
  if (['pending','new','unverified','pending_verification','no_show','processing'].includes(s)) return '#f59e0b';
  if (s === 'acknowledged') return '#6366f1';
  return '#94a3b8';
};
const Badge = ({s,text}) => <span style={{padding:'2px 9px',borderRadius:20,fontSize:11,fontWeight:700,background:`${sc(s)}22`,color:sc(s),whiteSpace:'nowrap'}}>{text||s||'—'}</span>;
const StatCard = ({icon,label,value,color,sub}) => (
  <div style={{background:'var(--surface-subtle)',border:`1px solid ${color}22`,borderRadius:13,padding:'16px 18px'}}>
    <div style={{fontSize:26,marginBottom:4}}>{icon}</div>
    <div style={{fontSize:22,fontWeight:800,color,lineHeight:1}}>{value}</div>
    <div style={{fontSize:12,color:'var(--text-muted)',marginTop:3}}>{label}</div>
    {sub&&<div style={{fontSize:11,color:'var(--text-muted)',marginTop:1}}>{sub}</div>}
  </div>
);
const Toast = ({t}) => !t ? null : (
  <div style={{position:'fixed',top:20,right:20,zIndex:9999,padding:'11px 18px',background:t.e?'#7f1d1d':'#064e3b',border:`1px solid ${t.e?'#ef4444':'#10b981'}`,borderRadius:12,color:'#fff',fontSize:13,boxShadow:'0 4px 24px rgba(0,0,0,0.5)',display:'flex',alignItems:'center',gap:9,maxWidth:340}}>
    {t.e?<MdCancel style={{color:'#f87171',fontSize:18}}/>:<MdCheckCircle style={{color:'#34d399',fontSize:18}}/>}
    {t.m}
  </div>
);

// Inline editable field
const EditField = ({ label, value, onSave, type='text' }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(value||''));
  useEffect(()=>{ if(!editing) setVal(String(value||'')); },[value,editing]);
  return editing ? (
    <div style={{display:'flex',gap:6,alignItems:'center'}}>
      <input value={val} onChange={e=>setVal(e.target.value)} type={type}
        style={{flex:1,padding:'6px 10px',background:'var(--border-color)',border:'1px solid rgba(245,158,11,0.4)',borderRadius:8,color:'#f1f5f9',fontSize:13,outline:'none',fontFamily:'inherit'}}/>
      <button onClick={()=>{onSave(val);setEditing(false);}} style={{padding:'6px 10px',background:'rgba(16,185,129,0.2)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:7,color:'#34d399',cursor:'pointer',fontSize:14,display:'flex',alignItems:'center'}}><MdSave/></button>
      <button onClick={()=>setEditing(false)} style={{padding:'6px 10px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:7,color:'#f87171',cursor:'pointer',fontSize:14,display:'flex',alignItems:'center'}}><MdClose/></button>
    </div>
  ) : (
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,padding:'8px 0',borderBottom:'1px solid var(--surface-subtle)'}}>
      <div>
        <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:1}}>{label}</div>
        <div style={{fontSize:14,color:'var(--text-primary)',fontWeight:500}}>{value||'—'}</div>
      </div>
      <button onClick={()=>{setVal(String(value||''));setEditing(true);}} style={{padding:'5px 8px',background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:7,color:'#818cf8',cursor:'pointer',fontSize:14,display:'flex',alignItems:'center',flexShrink:0}}><MdEdit/></button>
    </div>
  );
};

const S = {background:'var(--surface-subtle)',border:'1px solid var(--surface-muted)',borderRadius:13,padding:18,marginBottom:14};
const H2 = {fontSize:15,fontWeight:700,marginBottom:14,color:'#f1f5f9',display:'flex',alignItems:'center',gap:8};
const TH = {padding:'9px 12px',textAlign:'left',color:'var(--text-muted)',fontWeight:600,fontSize:11,textTransform:'uppercase',letterSpacing:'0.05em',borderBottom:'1px solid var(--surface-muted)',whiteSpace:'nowrap'};
const TD = {padding:'9px 12px',color:'var(--text-primary)',borderBottom:'1px solid var(--surface-subtle)',fontSize:13};

// ── Hospital creation form sub-components (MUST be at module level to avoid
// focus-loss on every keystroke caused by React remounting inline components) ──
const SLabel = ({t}) => <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4,marginTop:12}}>{t}</div>;
const SInput = ({label,value,onChange,placeholder,type='text',note,color}) => (
  <div style={{marginBottom:10}}>
    <SLabel t={label}/>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{width:'100%',padding:'9px 12px',background:'var(--surface-subtle)',border:`1px solid ${color||'var(--surface-strong)'}`,borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
    {note&&<div style={{fontSize:11,color:'var(--text-muted)',marginTop:3}}>{note}</div>}
  </div>
);
const Chip = ({label, selected, onClick, color='#6366f1'}) => (
  <div onClick={onClick} style={{display:'inline-flex',alignItems:'center',gap:5,padding:'5px 11px',borderRadius:20,fontSize:12,cursor:'pointer',margin:'3px',
    background: selected ? `${color}28` : 'var(--surface-subtle)',
    border: `1px solid ${selected ? color+'88' : 'var(--border-color-hover)'}`,
    color: selected ? color : 'var(--text-muted)',
    fontWeight: selected ? 700 : 400, transition:'all 0.12s'}}>
    {selected && <span style={{fontSize:10}}>✓</span>} {label}
  </div>
);

// ── Facilities/Equipment/Departments tag renderer (module-level, not a component) ──
const FAC_TAG = (item, onDel) => (
  <span key={item} style={{display:'inline-flex',alignItems:'center',gap:5,padding:'4px 10px',background:'rgba(99,102,241,0.12)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:20,fontSize:12,color:'#a5b4fc',margin:'3px'}}>
    {item}
    <span onClick={onDel} style={{cursor:'pointer',color:'#f87171',fontWeight:800,fontSize:14,lineHeight:1}}>×</span>
  </span>
);

// ── FacRow: stable module-level component so inputs don't lose focus ──
const FacRow = ({label, icon, list, onAdd, onRemove, newVal, setNew, color, saving}) => (
  <div style={{background:'var(--surface-subtle)',border:'1px solid var(--surface-muted)',borderRadius:14,padding:18,marginBottom:14}}>
    <div style={{fontWeight:700,fontSize:15,marginBottom:12,display:'flex',alignItems:'center',gap:7}}>
      <span>{icon}</span> {label}
      <span style={{marginLeft:'auto',fontSize:11,color:'var(--text-muted)',fontWeight:400}}>{list.length} items</span>
    </div>
    <div style={{display:'flex',flexWrap:'wrap',marginBottom:10,minHeight:32}}>
      {list.length>0 ? list.map((item,i)=>FAC_TAG(item, ()=>onRemove(i))) :
        <span style={{fontSize:12,color:'var(--text-muted)'}}>None added yet</span>}
    </div>
    <div style={{display:'flex',gap:8}}>
      <input value={newVal} onChange={e=>setNew(e.target.value)}
        onKeyDown={e=>e.key==='Enter'&&onAdd()}
        placeholder={`Add ${label.toLowerCase()}...`}
        style={{flex:1,padding:'8px 12px',background:'var(--surface-subtle)',border:`1px solid ${color}40`,borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none'}}/>
      <button onClick={onAdd}
        style={{padding:'8px 14px',background:color,border:'none',borderRadius:8,color:'#000',fontWeight:700,fontSize:13,cursor:'pointer',opacity:saving?0.6:1}}>
        + Add
      </button>
    </div>
  </div>
);

// ── FacilitiesTab: stable module-level component to manage eq/facilities/depts ──
const FacilitiesTab = ({h, adminAPI, toast_, setHDetail, setHospitals}) => {
  const [eq,   setEq]   = React.useState(() => toArr(h.equipment));
  const [fac,  setFac]  = React.useState(() => toArr(h.facilities));
  const [dep,  setDep]  = React.useState(() => toArr(h.departments));
  const [newEq,  setNewEq]  = React.useState('');
  const [newFac, setNewFac] = React.useState('');
  const [newDep, setNewDep] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const save = React.useCallback(async (field, arr) => {
    setSaving(true);
    const r = await adminAPI.updateFacilities(h.hospitalId, { [field]: arr });
    if (r.error) { toast_('❌ ' + r.error); }
    else {
      toast_('✅ Saved! Synced to app.');
      setHDetail(p => p ? { ...p, hospital: { ...p.hospital, [field]: arr } } : p);
      setHospitals(p => p.map(x => x.hospitalId===h.hospitalId ? {...x,[field]:arr} : x));
    }
    setSaving(false);
  }, [h.hospitalId, adminAPI, toast_, setHDetail, setHospitals]);

  const makeAdd = (list, setList, newVal, setNew, field) => () => {
    if (!newVal.trim()) return;
    const updated = [...list, newVal.trim()];
    setList(updated); setNew('');
    save(field, updated);
  };
  const makeRemove = (list, setList, field) => (idx) => {
    const updated = list.filter((_,i)=>i!==idx);
    setList(updated);
    save(field, updated);
  };

  return (
    <div>
      <div style={{fontSize:19,fontWeight:800,marginBottom:16}}>🔬 Equipment &amp; Facilities Management</div>
      <FacRow label="Equipment"   icon="🔬" list={eq}  onAdd={makeAdd(eq,setEq,newEq,setNewEq,'equipment')}   onRemove={makeRemove(eq,setEq,'equipment')}   newVal={newEq}  setNew={setNewEq}  color="#6366f1" saving={saving}/>
      <FacRow label="Facilities"  icon="🏥" list={fac} onAdd={makeAdd(fac,setFac,newFac,setNewFac,'facilities')} onRemove={makeRemove(fac,setFac,'facilities')} newVal={newFac} setNew={setNewFac} color="#00d4aa" saving={saving}/>
      <FacRow label="Departments" icon="🏢" list={dep} onAdd={makeAdd(dep,setDep,newDep,setNewDep,'departments')} onRemove={makeRemove(dep,setDep,'departments')} newVal={newDep} setNew={setNewDep} color="#fbbf24" saving={saving}/>
      <div style={{fontSize:11,color:'var(--border-color-hover)',marginTop:8}}>Changes sync to the patient HCEN app immediately after saving.</div>
    </div>
  );
};

const NAVS = [
  {id:'dashboard',     icon:<MdDashboard/>,       label:'Dashboard'},
  {id:'applications',  icon:<MdPersonAdd/>,       label:'Applications', badge:true},
  {id:'contact-leads', icon:<MdContactMail/>,     label:'Get in Touch', badge:true},
  {id:'hospitals',     icon:<MdLocalHospital/>,   label:'Hospitals'},
  {id:'offers',        icon:<MdCampaign/>,         label:'Health Offers'},
  {id:'bookings',      icon:<MdCalendarToday/>,   label:'Bookings'},
  {id:'patients',      icon:<MdPeople/>,           label:'Patients'},
  {id:'analytics',     icon:<MdBarChart/>,         label:'Analytics'},
  {id:'intelligence',  icon:<MdInsights/>,         label:'Intelligence'},
  {id:'doctors',       icon:<MdMedicalServices/>,  label:'Doctors'},
  {id:'revenue',       icon:<MdTrendingUp/>,       label:'Revenue'},
  {id:'transactions',  icon:<MdPayments/>,         label:'Transactions'},
  {id:'refunds',       icon:<MdAssignmentReturn/>, label:'Refunds'},
  {id:'settlements',   icon:<MdLocalAtm/>,         label:'Settlements'},
  {id:'emergencies',   icon:<MdWarning/>,           label:'Emergencies'},
  {id:'tickets',       icon:<MdSupportAgent/>,     label:'Support Tickets', badge:true},
  {id:'referrals',     icon:<MdThumbUp/>,           label:'Refer & Earn'},
  {id:'support',       icon:<MdSupportAgent/>,     label:'Support Lookup'},
  {id:'campaigns',     icon:<MdCampaign/>,          label:'Campaigns'},
  {id:'promos',        icon:<MdCardGiftcard/>,     label:'Promo Codes'},
  {id:'settings',      icon:<MdSettings/>,         label:'Platform Settings'},
  {id:'team',          icon:<MdManageAccounts/>,   label:'Team Logins',  ownerOnly:true},
  {id:'audit',         icon:<MdShield/>,           label:'Audit Log',    ownerOnly:true},
  {id:'security',      icon:<MdVpnKey/>,           label:'My Security'},
];

// ── My Security · self-service TOTP MFA for the signed-in admin ─────
// The break-glass env owner is not a Cognito user, so MFA is not available
// to it (it is protected by the ADMIN_PASSWORD + ADMIN_TOKEN_SECRET instead).
const SecurityTab = ({ S, admin, toast }) => {
  const isBreakGlass = String(admin?.role || '').toLowerCase() === 'owner';
  const [busy,   setBusy]   = useState(false);
  const [setup,  setSetup]  = useState(null); // { secret, otpauth }
  const [code,   setCode]   = useState('');
  const [enabled,setEnabled]= useState(false);

  const begin = async () => {
    setBusy(true);
    try {
      const r = await adminAPI.mfaSetup();
      if (r.error) { toast?.(r.error, 'error'); return; }
      const secret = r.data?.secret;
      const otpauth = r.data?.otpauth;
      if (!secret) { toast?.('Could not start MFA setup', 'error'); return; }
      setSetup({ secret, otpauth });
    } catch (e) { toast?.(e.message || 'Could not start MFA setup', 'error'); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    if (code.trim().length < 6) return toast?.('Enter the 6-digit code', 'error');
    setBusy(true);
    try {
      const r = await adminAPI.mfaVerify(code.trim());
      if (r.error) { toast?.(r.error, 'error'); return; }
      setEnabled(true); setSetup(null); setCode('');
      toast?.('Two-factor authentication is now ON', 'success');
    } catch (e) { toast?.(e.message || 'Invalid code', 'error'); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    if (!window.confirm('Turn OFF two-factor authentication for your login?')) return;
    setBusy(true);
    try {
      const r = await adminAPI.mfaDisable();
      if (r.error) { toast?.(r.error, 'error'); return; }
      setEnabled(false);
      toast?.('Two-factor turned off', 'success');
    } catch (e) { toast?.(e.message || 'Could not disable', 'error'); }
    finally { setBusy(false); }
  };
  const copy = (t) => { try { navigator.clipboard.writeText(t); toast?.('Copied', 'success'); } catch {} };

  const card = { background:'var(--surface-1,#12151c)', border:'1px solid var(--border-color)', borderRadius:16, padding:24, maxWidth:560 };
  const btn  = { display:'inline-flex', alignItems:'center', gap:8, padding:'11px 18px', borderRadius:10, border:'none', cursor:'pointer', fontWeight:700, fontSize:14 };

  return (
    <div>
      <h2 style={{ fontSize:22, fontWeight:800, marginBottom:6, display:'flex', alignItems:'center', gap:10 }}>
        <MdSecurity style={{ color:'var(--color-primary)' }}/> My Security
      </h2>
      <p style={{ color:'var(--text-muted)', fontSize:14, marginBottom:22 }}>
        Add a second layer to your admin login using an authenticator app (Google Authenticator, Authy, Microsoft Authenticator).
      </p>

      {isBreakGlass ? (
        <div style={{ ...card, borderColor:'rgba(245,158,11,0.35)' }}>
          <div style={{ fontWeight:700, marginBottom:6, color:'#fbbf24' }}>You are signed in as the break-glass owner</div>
          <div style={{ fontSize:13, color:'var(--text-muted)', lineHeight:1.6 }}>
            This permanent Director account is protected by its password and the server secret, and is intentionally kept
            independent of Cognito so it can never be locked out. Two-factor is available on the CTO / COO / Manager
            Cognito logins you create under <strong>Team Logins</strong>.
          </div>
        </div>
      ) : enabled ? (
        <div style={{ ...card, borderColor:'rgba(52,211,153,0.4)' }}>
          <div style={{ fontWeight:700, color:'#34d399', display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <MdCheckCircle/> Two-factor is ON
          </div>
          <p style={{ fontSize:13, color:'var(--text-muted)', marginBottom:16 }}>You will be asked for a 6-digit code the next time you sign in.</p>
          <button onClick={disable} disabled={busy} style={{ ...btn, background:'rgba(239,68,68,0.15)', color:'#f87171' }}>Turn off two-factor</button>
        </div>
      ) : !setup ? (
        <div style={card}>
          <div style={{ fontWeight:700, marginBottom:8 }}>Two-factor is off</div>
          <p style={{ fontSize:13, color:'var(--text-muted)', marginBottom:18 }}>Recommended for all admin accounts. Takes about a minute to set up.</p>
          <button onClick={begin} disabled={busy} style={{ ...btn, background:'var(--color-primary)', color:'#0b0d12' }}>
            <MdVpnKey/> {busy ? 'Starting…' : 'Enable two-factor'}
          </button>
        </div>
      ) : (
        <div style={card}>
          <div style={{ fontWeight:700, marginBottom:12 }}>Step 1 — Add this key to your authenticator app</div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
            <code style={{ flex:1, background:'#0b0d12', border:'1px solid var(--border-color)', borderRadius:8, padding:'10px 12px', fontSize:15, letterSpacing:1, wordBreak:'break-all' }}>{setup.secret}</code>
            <button onClick={() => copy(setup.secret)} style={{ ...btn, background:'rgba(255,255,255,0.08)', color:'var(--text-primary)', padding:'10px' }} title="Copy key"><MdContentCopy/></button>
          </div>
          <a href={setup.otpauth} style={{ fontSize:12, color:'var(--color-primary)', textDecoration:'none' }}>Open in authenticator app →</a>

          <div style={{ fontWeight:700, margin:'20px 0 10px' }}>Step 2 — Enter the 6-digit code it shows</div>
          <div style={{ display:'flex', gap:10 }}>
            <input value={code} onChange={e => setCode(e.target.value.replace(/[^\d]/g,'').slice(0,6))} placeholder="123456"
              inputMode="numeric" style={{ flex:1, background:'#0b0d12', border:'1px solid var(--border-color)', borderRadius:10, padding:'12px 14px', color:'var(--text-primary)', fontSize:18, letterSpacing:6, textAlign:'center' }}/>
            <button onClick={verify} disabled={busy || code.length<6} style={{ ...btn, background:'var(--color-primary)', color:'#0b0d12' }}>{busy ? 'Verifying…' : 'Verify'}</button>
          </div>
          <button onClick={() => { setSetup(null); setCode(''); }} style={{ marginTop:14, background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:13 }}>Cancel</button>
        </div>
      )}
    </div>
  );
};

// ── Mini bar chart rendered purely in CSS/JSX (no external lib) ──
const MiniBar = ({ data, color = '#6366f1', maxVal, height = 40, labels }) => {
  const max = maxVal || Math.max(...data, 1);
  return (
    <div style={{display:'flex',alignItems:'flex-end',gap:2,height}}>
      {data.map((v, i) => (
        <div key={i} title={labels?.[i]||String(v)} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:1}}>
          <div style={{width:'100%',background:`${color}33`,borderRadius:'3px 3px 0 0',height:height,display:'flex',alignItems:'flex-end'}}>
            <div style={{width:'100%',background:color,borderRadius:'3px 3px 0 0',height:`${Math.max(3,Math.round(v/max*height))}px`,transition:'height 0.3s'}}></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Time ago helper
const timeAgo = (ts) => {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  if (diff < 60000) return 'just now';
  if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff/86400000)}d ago`;
  return new Date(ts).toLocaleDateString('en-IN',{day:'2-digit',month:'short'});
};

// ── Input helper ──────────────────────────────────────────────────
const Inp = ({label,value,onChange,type='text',placeholder,required}) => (
  <div style={{marginBottom:11}}>
    <label style={{fontSize:10,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:4}}>{label}{required&&' *'}</label>
    <input type={type} value={value} placeholder={placeholder} required={required} onChange={onChange}
      style={{width:'100%',padding:'9px 11px',background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:8,color:'#f1f5f9',fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit'}}/>
  </div>
);

// ── RevenueTab: extracted to avoid hooks-in-IIFE runtime crash ──
const RevenueTab = ({ bookings, doctors, hospitals, analytics, bookingFee, setTab, loadDetail, S, H2, TH, TD }) => {
  const [revCfg, setRevCfg] = React.useState({ defaultCommissionPct: 2, defaultGstPct: 18 });
  const [revHospComm, setRevHospComm] = React.useState([]);
  React.useEffect(()=>{
    (async()=>{
      const [cfgR, hcR] = await Promise.all([
        adminAPI.getPlatformSettings().catch(()=>({})),
        adminAPI.getHospitalCommissions().catch(()=>({})),
      ]);
      if(cfgR.data) setRevCfg(cfgR.data);
      if(hcR.data?.hospitals) setRevHospComm(hcR.data.hospitals);
    })();
  },[]);
  const PLATFORM_FEE_PCT = Number(revCfg.defaultCommissionPct)||2;
  const GST_PCT          = Number(revCfg.defaultGstPct)||18;
  const round2 = (n) => Math.round((Number(n)||0) * 100) / 100;
  const isOp = b => { const t = String(b?.type || '').toLowerCase(); return t !== 'sos' && t !== 'emergency'; };
  const isEarned = b => isOp(b) && ['checked_in','completed'].includes(String(b?.status || '').toLowerCase());
  const earned   = bookings.filter(isEarned);
  const cancelled  = bookings.filter(b=>b.status==='cancelled');
  const noShows    = bookings.filter(b=>b.status==='no_show');
  const totalRev   = earned.reduce((s,b)=>s+bookingFee(b,doctors),0);
  const totalLost  = cancelled.reduce((s,b)=>s+bookingFee(b,doctors),0);
  const platformFee= round2(totalRev * PLATFORM_FEE_PCT / 100);
  const gstOnFee   = round2(platformFee * GST_PCT / 100);
  const totalDed   = round2(platformFee + gstOnFee);
  const payoutTotal= round2(totalRev - totalDed);
  const getHospRate = (hospitalId) => {
    const hc = revHospComm.find(x=>x.hospitalId===hospitalId);
    if(!hc) return { cPct: PLATFORM_FEE_PCT, gPct: GST_PCT };
    return { cPct: hc.isWaived ? 0 : (hc.commissionPct ?? PLATFORM_FEE_PCT), gPct: hc.gstExempt ? 0 : (hc.gstPct ?? GST_PCT) };
  };
  const hStats = hospitals.map(h=>{
    const hB  = earned.filter(b=>b.hospitalId===h.hospitalId);
    const hC  = cancelled.filter(b=>b.hospitalId===h.hospitalId);
    const hNS = noShows.filter(b=>b.hospitalId===h.hospitalId);
    const rev = hB.reduce((s,b)=>s+bookingFee(b,doctors),0);
    const lost= hC.reduce((s,b)=>s+bookingFee(b,doctors),0);
    const { cPct, gPct } = getHospRate(h.hospitalId);
    const fee = round2(rev * cPct / 100);
    const gst = round2(fee * gPct / 100);
    const ded = round2(fee + gst);
    const settled = Number(h.totalSettled||0);
    const due = Math.max(0, round2((rev - ded) - settled));
    return {...h, confirmedCount:hB.length, cancelledCount:hC.length, noShowCount:hNS.length, revenue:rev, lostRevenue:lost, platformFee:fee, gstOnFee:gst, commPct:cPct, gstPct:gPct, payout:round2(rev-ded), settled, due};
  }).sort((a,b)=>b.revenue-a.revenue);
  const dStats = doctors.map(d=>{
    const dB  = earned.filter(b=>b.doctorId===d.doctorId);
    const dC  = cancelled.filter(b=>b.doctorId===d.doctorId);
    const rev = dB.reduce((s,b)=>s+bookingFee(b,doctors),0);
    return {...d, confirmedCount:dB.length, cancelledCount:dC.length, revenue:rev,
      hospitalName: (hospitals.find(h=>h.hospitalId===d.hospitalId)?.name)
        || d.hospitalName
        || (d.hospitalId ? '(deleted hospital)' : '—')};
  }).filter(d=>d.confirmedCount>0).sort((a,b)=>b.revenue-a.revenue);
  const maxRev = Math.max(...hStats.map(h=>h.revenue),1);
  return (
    <div>
      <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>Revenue & Settlement 💰</h1>
      <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16}}>Platform financial overview · {PLATFORM_FEE_PCT}% commission + {GST_PCT}% GST on commission only</p>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:11,marginBottom:18}}>
        {[
          {icon:'💰',label:'Gross Revenue',val:fmtR(totalRev),c:'#fbbf24'},
          {icon:'🏦',label:`Platform Fee (${PLATFORM_FEE_PCT}%)`,val:fmtR(platformFee),c:'#6366f1'},
          {icon:'🧾',label:`GST (${GST_PCT}% on fee)`,val:fmtR(gstOnFee),c:'#a78bfa'},
          {icon:'🏥',label:'Hospital Payout',val:fmtR(payoutTotal),c:'#10b981'},
          {icon:'❌',label:'Lost (Cancelled)',val:fmtR(totalLost),c:'#ef4444'},
          {icon:'⚠️',label:'No-Shows',val:String(noShows.length),c:'#f59e0b'},
          {icon:'📅',label:'Today',val:fmtR(analytics?.todayRevenue||0),c:'#00d4aa'},
          {icon:'💳',label:'Outstanding Dues',val:fmtR(hStats.reduce((s,h)=>s+h.due,0)),c:'#f59e0b'},
        ].map(x=>(
          <div key={x.label} style={{background:'var(--surface-subtle)',border:`1px solid ${x.c}22`,borderRadius:12,padding:'12px 15px'}}>
            <div style={{fontSize:18}}>{x.icon}</div>
            <div style={{fontSize:20,fontWeight:800,color:x.c,marginTop:4}}>{x.val}</div>
            <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>{x.label}</div>
          </div>
        ))}
      </div>
      <div style={{...S,marginBottom:16}}>
        <div style={{...H2,marginBottom:12}}>🏥 Hospital Settlement — click hospital name to manage</div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Hospital','Confirmed','Cancelled','No-Shows','Gross Revenue','Comm %','Platform Fee','GST','Payout','Settled','Due',''].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
            <tbody>
              {hStats.map(h=>(
                <tr key={h.hospitalId}>
                  <td style={TD}><span style={{fontWeight:700,cursor:'pointer',color:'#60a5fa'}} onClick={()=>{setTab('hospitals');loadDetail(h);}}>{h.name}</span><div style={{fontSize:11,color:'var(--text-muted)'}}>{h.city}</div></td>
                  <td style={{...TD,color:'#10b981',fontWeight:700,textAlign:'center'}}>{h.confirmedCount}</td>
                  <td style={{...TD,color:'#f87171',fontWeight:700,textAlign:'center'}}>{h.cancelledCount}</td>
                  <td style={{...TD,color:'#f59e0b',textAlign:'center'}}>{h.noShowCount||0}</td>
                  <td style={{...TD,color:'#fbbf24',fontWeight:700}}>{fmtR(h.revenue)}</td>
                  <td style={TD}><span style={{fontSize:11,color:h.commPct===0?'#34d399':'#818cf8',fontWeight:h.commPct!==PLATFORM_FEE_PCT?700:400}}>{h.commPct===0?'Waived':h.commPct+'%'}{h.gstPct===0?' (GST-free)':''}</span></td>
                  <td style={{...TD,color:'#818cf8'}}>{fmtR(h.platformFee)}</td>
                  <td style={{...TD,color:'#c4b5fd'}}>{fmtR(h.gstOnFee)}</td>
                  <td style={{...TD,color:'#10b981',fontWeight:700}}>{fmtR(h.payout)}</td>
                  <td style={{...TD,color:'#34d399'}}>{fmtR(h.settled)}</td>
                  <td style={TD}><span style={{fontWeight:700,color:h.due>0?'#f59e0b':'#10b981'}}>{h.due>0?`⚠️ ${fmtR(h.due)}`:'✅ Settled'}</span></td>
                  <td style={TD}><button onClick={()=>{setTab('hospitals');loadDetail(h);}} style={{padding:'5px 10px',background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:7,color:'#818cf8',cursor:'pointer',fontSize:11,fontWeight:700}}>Settle →</button></td>
                </tr>
              ))}
              {!hStats.length&&<tr><td colSpan={10} style={{...TD,textAlign:'center',color:'var(--text-muted)',padding:24}}>No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {dStats.length>0&&(
        <div style={S}>
          <div style={{...H2,marginBottom:12}}>👨‍⚕️ Revenue by Doctor</div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Doctor','Specialization','Hospital','Fee/Visit','Bookings','Cancelled','Revenue Earned'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
              <tbody>
                {dStats.map(d=>(
                  <tr key={d.doctorId}>
                    <td style={{...TD,fontWeight:700}}>{d.name}</td>
                    <td style={TD}>{d.specialization}</td>
                    <td style={TD}>{d.hospitalName}</td>
                    <td style={{...TD,color:'#fbbf24'}}>{fmtR(d.consultationFee||d.fee||0)}</td>
                    <td style={{...TD,color:'#818cf8',fontWeight:700,textAlign:'center'}}>{d.confirmedCount}</td>
                    <td style={{...TD,color:'#f87171',textAlign:'center'}}>{d.cancelledCount}</td>
                    <td style={{...TD,color:'#10b981',fontWeight:700}}>{fmtR(d.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div style={S}>
        <div style={H2}>Revenue Distribution</div>
        {hStats.map(h=>(
          <div key={h.hospitalId} style={{marginBottom:14}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
              <span style={{fontSize:13,fontWeight:600,cursor:'pointer'}} onClick={()=>{setTab('hospitals');loadDetail(h);}}>{h.name}</span>
              <div style={{display:'flex',gap:14,fontSize:12}}>
                <span style={{color:'#10b981'}}>{fmtR(h.revenue)} earned</span>
                {h.lostRevenue>0&&<span style={{color:'#f87171'}}>-{fmtR(h.lostRevenue)} lost</span>}
              </div>
            </div>
            <div style={{height:7,background:'var(--surface-muted)',borderRadius:10,overflow:'hidden',position:'relative'}}>
              <div style={{position:'absolute',height:'100%',width:`${Math.round(h.revenue/maxRev*100)}%`,background:'linear-gradient(90deg,#f59e0b,#d97706)',borderRadius:10,transition:'width 0.4s'}}/>
            </div>
            <div style={{display:'flex',gap:14,marginTop:4,fontSize:11,color:'var(--text-muted)'}}>
              <span>✅ {h.confirmedCount} confirmed</span>
              <span>❌ {h.cancelledCount} cancelled</span>
              <span>Payout: {fmtR(h.payout)}</span>
              {h.due>0&&<span style={{color:'#f59e0b'}}>Due: {fmtR(h.due)}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── CampaignsTab ──
const CampaignsTab = ({ toast_, S, H2, TH, TD }) => {
  const [campForm, setCampForm] = React.useState({title:'',body:'',segment:'all',city:''});
  const [sending, setSending] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [histLoading, setHistLoading] = React.useState(true);
  React.useEffect(()=>{
    (async()=>{
      setHistLoading(true);
      const r = await adminAPI.getCampaignHistory();
      if(r.data?.campaigns) setHistory(r.data.campaigns);
      setHistLoading(false);
    })();
  },[]);
  const sendCampaign = async()=>{
    if(!campForm.title||!campForm.body) return toast_('Title and message body required',true);
    if(campForm.segment==='city'&&!campForm.city) return toast_('City name required for city segment',true);
    if(!window.confirm(`Send push notification to ${campForm.segment==='all'?'ALL users':campForm.segment==='city'?`users in "${campForm.city}"`:campForm.segment+' users'}?`)) return;
    setSending(true);
    const r = await adminAPI.sendCampaign(campForm);
    if(!r.error){
      toast_(`✅ Campaign sent! ${r.data?.sent||0} delivered, ${r.data?.failed||0} failed`);
      const r2 = await adminAPI.getCampaignHistory(); if(r2.data?.campaigns) setHistory(r2.data.campaigns);
    } else toast_(r.error,true);
    setSending(false);
  };
  return(
    <div>
      <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>📣 Push Campaigns</h1>
      <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16}}>Send targeted push notifications to patient segments</p>
      <div style={{...S,marginBottom:16,border:'1px solid rgba(99,102,241,0.2)'}}>
        <div style={{...H2,marginBottom:14}}>🚀 New Campaign</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
          <div style={{gridColumn:'span 2'}}><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Notification Title *</label><input value={campForm.title} onChange={e=>setCampForm(p=>({...p,title:e.target.value}))} placeholder="🎉 Special Offer!" style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:15,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
          <div style={{gridColumn:'span 2'}}><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Message Body *</label><textarea value={campForm.body} onChange={e=>setCampForm(p=>({...p,body:e.target.value}))} placeholder="Book your appointment today and get 20% off!" rows={3} style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box',resize:'vertical'}}/></div>
          <div><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Target Segment</label><select value={campForm.segment} onChange={e=>setCampForm(p=>({...p,segment:e.target.value}))} style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}><option value="all" style={{background:'#0F172A',color:'#fff'}}>All Users</option><option value="city" style={{background:'#0F172A',color:'#fff'}}>By City</option><option value="recent" style={{background:'#0F172A',color:'#fff'}}>Recent (active last 7 days)</option><option value="inactive" style={{background:'#0F172A',color:'#fff'}}>Inactive (30+ days)</option></select></div>
          {campForm.segment==='city'&&(<div><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>City Name *</label><input value={campForm.city} onChange={e=>setCampForm(p=>({...p,city:e.target.value}))} placeholder="Mumbai" style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>)}
        </div>
        <button onClick={sendCampaign} disabled={sending} style={{padding:'10px 24px',background:'linear-gradient(135deg,#6366f1,#4f46e5)',border:'none',borderRadius:10,color:'#fff',fontWeight:800,cursor:'pointer',fontSize:14,fontFamily:'inherit',opacity:sending?0.5:1}}>{sending?'Sending…':'📣 Send Campaign'}</button>
      </div>
      <div style={S}>
        <div style={{...H2,marginBottom:12}}>📜 Campaign History</div>
        {histLoading?<div style={{textAlign:'center',padding:30,color:'var(--text-muted)'}}>Loading…</div>:
        history.length===0?<div style={{textAlign:'center',padding:'30px 0',color:'var(--text-muted)'}}>No campaigns sent yet</div>:(
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Title','Segment','Sent','Failed','Targets','Sent By','Date'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
              <tbody>{history.map(c=>(
                <tr key={c.campaignId||c.sentAt}>
                  <td style={{...TD,fontWeight:700,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.title}</td>
                  <td style={TD}><span style={{padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:600,background:'var(--surface-muted)',color:'var(--text-secondary)'}}>{c.segment==='city'?`🏙️ ${c.city}`:c.segment}</span></td>
                  <td style={{...TD,color:'#10b981',fontWeight:700,textAlign:'center'}}>{c.sentCount||0}</td>
                  <td style={{...TD,color:'#f87171',textAlign:'center'}}>{c.failedCount||0}</td>
                  <td style={{...TD,textAlign:'center'}}>{c.targetCount||0}</td>
                  <td style={{...TD,fontSize:11,color:'var(--text-muted)'}}>{c.sentBy||'—'}</td>
                  <td style={{...TD,fontSize:11}}>{c.sentAt?new Date(c.sentAt).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}):'—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── PromosTab ──
const PromosTab = ({ toast_, S, H2, TH, TD }) => {
  const [promos, setPromos] = React.useState([]);
  const [promoLoading, setPromoLoading] = React.useState(true);
  const [showCreate, setShowCreate] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  // editingCode === null → create flow · non-null → patching an existing row.
  // We reuse the same form so the targeting fields (city/district/firstBookingOnly
  // /maxUsesPerUser) are accessible for existing codes too — without this,
  // legacy rows like HCENNEW (created before May-29 expansion) can't be
  // upgraded to first-booking-only from the UI.
  const [editingCode, setEditingCode] = React.useState(null);
  // ── Form schema (May 29, 2026 expansion: Swiggy/Zomato-style flags) ────
  //   maxUsesPerUser:    lifetime cap PER PHONE (default 1, 0 = unlimited)
  //   firstBookingOnly:  HCENNEW-style — only redeemable for users with no
  //                      prior bookings (HCEN_Patients row absent for phone)
  //   citiesCsv:         comma-separated city names; empty = all cities
  //   districtsCsv:      comma-separated district names; empty = all
  const blankForm = {
    code:'', discountType:'percentage', discountValue:'', maxUses:'',
    minOrderAmount:'', validFrom:'', validTo:'', description:'',
    maxUsesPerUser:'1', firstBookingOnly:false, citiesCsv:'', districtsCsv:'',
    // Phase 2 targeting:
    //   usersCsv       — paste phone numbers (any shape) for invite-only codes
    //   minLoyaltyTier — '' = anyone; otherwise 'silver'|'gold'|'platinum'
    usersCsv:'', minLoyaltyTier:'',
  };
  const [pForm, setPForm] = React.useState(blankForm);
  React.useEffect(()=>{(async()=>{setPromoLoading(true);const r=await adminAPI.getPromoCodes();if(r.data?.promoCodes)setPromos(r.data.promoCodes);setPromoLoading(false);})();},[]);
  const splitCsv = (s) => String(s||'').split(',').map(x=>x.trim()).filter(Boolean);
  const savePromo=async()=>{
    if(!pForm.code||!pForm.discountValue) return toast_('Code and discount value required',true);
    setCreating(true);
    const payload = {
      discountType:       pForm.discountType,
      discountValue:      Number(pForm.discountValue),
      maxUses:            Number(pForm.maxUses)||0,
      minOrderAmount:     Number(pForm.minOrderAmount)||0,
      validFrom:          pForm.validFrom,
      validTo:            pForm.validTo,
      description:        pForm.description,
      maxUsesPerUser:     pForm.maxUsesPerUser === '' ? 1 : Number(pForm.maxUsesPerUser),
      firstBookingOnly:   !!pForm.firstBookingOnly,
      applicableCities:   splitCsv(pForm.citiesCsv),
      applicableDistricts:splitCsv(pForm.districtsCsv),
      applicableUsers:    splitCsv(pForm.usersCsv),
      minLoyaltyTier:     pForm.minLoyaltyTier || '',
    };
    const r = editingCode
      ? await adminAPI.updatePromoCode(editingCode, payload)
      : await adminAPI.createPromoCode({ code: pForm.code, ...payload });
    if(!r.error){
      toast_(editingCode ? '✅ Promo code updated!' : '✅ Promo code created!');
      setShowCreate(false);
      setEditingCode(null);
      setPForm(blankForm);
      const r2 = await adminAPI.getPromoCodes();
      if(r2.data?.promoCodes) setPromos(r2.data.promoCodes);
    } else toast_(r.error,true);
    setCreating(false);
  };
  const startEdit=(p)=>{
    setPForm({
      code:               p.code || '',
      discountType:       p.discountType || 'percentage',
      discountValue:      String(p.discountValue ?? ''),
      maxUses:            String(p.maxUses ?? ''),
      minOrderAmount:     String(p.minOrderAmount ?? ''),
      validFrom:          p.validFrom || '',
      validTo:            p.validTo || '',
      description:        p.description || '',
      maxUsesPerUser:     String(p.maxUsesPerUser ?? 1),
      firstBookingOnly:   !!p.firstBookingOnly,
      citiesCsv:          Array.isArray(p.applicableCities)    ? p.applicableCities.join(', ')    : '',
      districtsCsv:       Array.isArray(p.applicableDistricts) ? p.applicableDistricts.join(', ') : '',
      usersCsv:           Array.isArray(p.applicableUsers)     ? p.applicableUsers.join(', ')     : '',
      minLoyaltyTier:     p.minLoyaltyTier || '',
    });
    setEditingCode(p.code);
    setShowCreate(true);
    setTimeout(()=>window.scrollTo({top:0,behavior:'smooth'}),50);
  };
  const cancelForm=()=>{ setShowCreate(false); setEditingCode(null); setPForm(blankForm); };
  const toggleActive=async(code,active)=>{await adminAPI.updatePromoCode(code,{active});setPromos(p=>p.map(x=>x.code===code?{...x,active}:x));toast_(active?'Activated':'Deactivated');};
  const deletePromo=async(code)=>{if(!window.confirm(`Delete promo code ${code}?`))return;await adminAPI.deletePromoCode(code);setPromos(p=>p.filter(x=>x.code!==code));toast_('Deleted');};
  if(promoLoading) return <div style={{textAlign:'center',padding:60,color:'var(--text-muted)'}}>Loading promo codes…</div>;
  return(
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
        <div><h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>🎟️ Promo Codes</h1><p style={{color:'var(--text-muted)',fontSize:13}}>Create and manage discount codes for patients</p></div>
        <button onClick={()=>{ if(showCreate){ cancelForm(); } else { setEditingCode(null); setPForm(blankForm); setShowCreate(true); } }} style={{padding:'10px 20px',background:'linear-gradient(135deg,#f59e0b,#d97706)',border:'none',borderRadius:10,color:'#000',fontWeight:800,fontSize:14,cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontFamily:'inherit'}}><MdAdd/> Create Code</button>
      </div>
      {showCreate&&(
        <div style={{...S,marginBottom:16,border:'1px solid rgba(245,158,11,0.3)'}}>
          <div style={{...H2,marginBottom:14}}>{editingCode ? `✏️ Edit ${editingCode}` : '✨ New Promo Code'}</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12,marginBottom:14}}>
            <div><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Code *{editingCode && <span style={{color:'var(--text-muted)',marginLeft:6,fontWeight:400}}>(immutable)</span>}</label><input value={pForm.code} disabled={!!editingCode} onChange={e=>setPForm(p=>({...p,code:e.target.value.toUpperCase()}))} placeholder="HCEN50" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:editingCode?'var(--text-muted)':'#fff',fontSize:14,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box',letterSpacing:'0.05em',cursor:editingCode?'not-allowed':'text'}}/></div>
            <div><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Discount Type</label><select value={pForm.discountType} onChange={e=>setPForm(p=>({...p,discountType:e.target.value}))} style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}><option value="percentage">Percentage (%)</option><option value="flat">Flat Amount (₹)</option></select></div>
            <div><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Discount Value *</label><input type="number" value={pForm.discountValue} onChange={e=>setPForm(p=>({...p,discountValue:e.target.value}))} placeholder={pForm.discountType==='percentage'?'10':'50'} style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
            <div><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Max Uses (0=unlimited)</label><input type="number" value={pForm.maxUses} onChange={e=>setPForm(p=>({...p,maxUses:e.target.value}))} placeholder="100" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
            <div><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Min Order ₹</label><input type="number" value={pForm.minOrderAmount} onChange={e=>setPForm(p=>({...p,minOrderAmount:e.target.value}))} placeholder="0" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
            <div><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Valid From</label><input type="date" value={pForm.validFrom} onChange={e=>setPForm(p=>({...p,validFrom:e.target.value}))} style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
            <div><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Valid To</label><input type="date" value={pForm.validTo} onChange={e=>setPForm(p=>({...p,validTo:e.target.value}))} style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
            <div style={{gridColumn:'span 2'}}><label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Description</label><input value={pForm.description} onChange={e=>setPForm(p=>({...p,description:e.target.value}))} placeholder="First booking discount" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
          </div>

          {/* ── Swiggy/Zomato-style targeting (May 29, 2026) ──────────────
              Per-user lifetime cap, "new users only" flag, and geo
              restriction by city/district. Empty city/district = available
              everywhere; the validate Lambda looks the hospital's city up
              from HCEN_Hospitals so a forged client request can't bypass. */}
          <div style={{padding:'12px 14px',marginBottom:14,background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.25)',borderRadius:10}}>
            <div style={{fontSize:12,fontWeight:800,color:'#a5b4fc',marginBottom:10,letterSpacing:0.4}}>🎯 TARGETING & ELIGIBILITY</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12,marginBottom:10}}>
              <div>
                <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Max Uses Per User (0=unlimited)</label>
                <input type="number" min="0" value={pForm.maxUsesPerUser} onChange={e=>setPForm(p=>({...p,maxUsesPerUser:e.target.value}))} placeholder="1" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                <div style={{fontSize:10,color:'var(--text-muted)',marginTop:3}}>Lifetime cap per phone number. <strong>1 = one-time per user</strong>.</div>
              </div>
              <div style={{display:'flex',alignItems:'center'}}>
                <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'var(--text-primary)',cursor:'pointer',userSelect:'none'}}>
                  <input type="checkbox" checked={pForm.firstBookingOnly} onChange={e=>setPForm(p=>({...p,firstBookingOnly:e.target.checked}))} style={{width:16,height:16,cursor:'pointer'}}/>
                  <span><strong>New users only</strong> <span style={{color:'var(--text-muted)'}}>(no prior bookings)</span></span>
                </label>
              </div>
              <div style={{gridColumn:'span 2'}}>
                <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Cities (comma-separated · empty = all cities)</label>
                <input value={pForm.citiesCsv} onChange={e=>setPForm(p=>({...p,citiesCsv:e.target.value}))} placeholder="Tirupati, Bangalore, Hyderabad" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
              </div>
              <div style={{gridColumn:'span 2'}}>
                <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Districts (comma-separated · empty = all)</label>
                <input value={pForm.districtsCsv} onChange={e=>setPForm(p=>({...p,districtsCsv:e.target.value}))} placeholder="Chittoor, Bangalore Urban" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
              </div>
              {/* Phase 2 targeting — invite-only user list + loyalty tier gate.
                  Empty user list = available to everyone (still subject to all
                  other gates). Tier dropdown defaults to "anyone". */}
              <div style={{gridColumn:'span 2'}}>
                <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Specific Users (phone numbers, comma-separated · empty = all users)</label>
                <input value={pForm.usersCsv} onChange={e=>setPForm(p=>({...p,usersCsv:e.target.value}))} placeholder="9876543210, +91 9123456789, 919000000001" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                <div style={{fontSize:10,color:'var(--text-muted)',marginTop:3}}>Invite-only — hidden from everyone else. Accepts any phone format.</div>
              </div>
              <div>
                <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Minimum Loyalty Tier</label>
                <select value={pForm.minLoyaltyTier} onChange={e=>setPForm(p=>({...p,minLoyaltyTier:e.target.value}))} style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}>
                  <option value="">Anyone (no tier gate)</option>
                  <option value="silver">Silver+ (≥200 pts earned)</option>
                  <option value="gold">Gold+ (≥500 pts earned)</option>
                  <option value="platinum">Platinum only (≥1000 pts)</option>
                </select>
              </div>
            </div>
          </div>

          <div style={{display:'flex',gap:8}}>
            <button onClick={savePromo} disabled={creating} style={{padding:'8px 20px',background:'linear-gradient(135deg,#10b981,#059669)',border:'none',borderRadius:8,color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit',opacity:creating?0.5:1}}>{creating?(editingCode?'Saving…':'Creating…'):(editingCode?'💾 Save Changes':'✅ Create Promo Code')}</button>
            <button onClick={cancelForm} style={{padding:'8px 16px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'var(--text-muted)',cursor:'pointer',fontSize:13,fontFamily:'inherit'}}>Cancel</button>
          </div>
        </div>
      )}
      <div style={S}>
        {promos.length===0?(<div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🎟️</div><div style={{fontWeight:700}}>No promo codes yet</div><div style={{fontSize:12,marginTop:6}}>Click "Create Code" to get started</div></div>):(
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr>{['Code','Type','Value','Used','Max Uses','Min Order','Valid From','Valid To','Status','Actions'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
              <tbody>{promos.map(p=>(
                <tr key={p.code}>
                  <td style={{...TD,fontWeight:700,fontFamily:'monospace',letterSpacing:'0.05em',color:'#fbbf24'}}>
                    <div>{p.code}</div>
                    {/* Targeting badges — Swiggy/Zomato style mini-flags so admins
                        can see at a glance which codes are restricted. */}
                    <div style={{display:'flex',gap:4,marginTop:4,flexWrap:'wrap'}}>
                      {p.firstBookingOnly && <span title="Only redeemable for users with no prior bookings" style={{padding:'1px 6px',borderRadius:3,fontSize:9,fontWeight:700,background:'rgba(168,85,247,0.15)',color:'#c4b5fd',letterSpacing:0.3}}>NEW USERS</span>}
                      {Number(p.maxUsesPerUser ?? 1) === 1 && <span title="One-time per phone" style={{padding:'1px 6px',borderRadius:3,fontSize:9,fontWeight:700,background:'rgba(59,130,246,0.15)',color:'#93c5fd',letterSpacing:0.3}}>1× PER USER</span>}
                      {Number(p.maxUsesPerUser) > 1 && <span title={`Up to ${p.maxUsesPerUser} times per phone`} style={{padding:'1px 6px',borderRadius:3,fontSize:9,fontWeight:700,background:'rgba(59,130,246,0.15)',color:'#93c5fd',letterSpacing:0.3}}>{p.maxUsesPerUser}× PER USER</span>}
                      {Array.isArray(p.applicableCities) && p.applicableCities.length > 0 && <span title={`Cities: ${p.applicableCities.join(', ')}`} style={{padding:'1px 6px',borderRadius:3,fontSize:9,fontWeight:700,background:'rgba(16,185,129,0.15)',color:'#6ee7b7',letterSpacing:0.3}}>📍 {p.applicableCities.length} CITY</span>}
                      {Array.isArray(p.applicableDistricts) && p.applicableDistricts.length > 0 && <span title={`Districts: ${p.applicableDistricts.join(', ')}`} style={{padding:'1px 6px',borderRadius:3,fontSize:9,fontWeight:700,background:'rgba(245,158,11,0.15)',color:'#fcd34d',letterSpacing:0.3}}>🗺️ {p.applicableDistricts.length} DIST</span>}
                      {Array.isArray(p.applicableUsers) && p.applicableUsers.length > 0 && <span title={`Invite-only: ${p.applicableUsers.length} phone(s)`} style={{padding:'1px 6px',borderRadius:3,fontSize:9,fontWeight:700,background:'rgba(236,72,153,0.15)',color:'#f9a8d4',letterSpacing:0.3}}>👥 {p.applicableUsers.length} USER</span>}
                      {p.minLoyaltyTier && <span title={`Requires ${p.minLoyaltyTier} loyalty tier`} style={{padding:'1px 6px',borderRadius:3,fontSize:9,fontWeight:700,background:'rgba(234,179,8,0.15)',color:'#fde68a',letterSpacing:0.3}}>⭐ {String(p.minLoyaltyTier).toUpperCase()}+</span>}
                    </div>
                  </td>
                  <td style={TD}><span style={{padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:600,background:'var(--surface-muted)',color:'var(--text-secondary)'}}>{p.discountType==='percentage'?'%':'₹ Flat'}</span></td>
                  <td style={{...TD,fontWeight:700,color:'#10b981'}}>{p.discountType==='percentage'?p.discountValue+'%':'₹'+p.discountValue}</td>
                  <td style={{...TD,textAlign:'center'}}>{p.usedCount||0}</td>
                  <td style={{...TD,textAlign:'center',color:'var(--text-muted)'}}>{p.maxUses>0?p.maxUses:'∞'}</td>
                  <td style={TD}>{p.minOrderAmount>0?'₹'+p.minOrderAmount:'—'}</td>
                  <td style={{...TD,fontSize:11}}>{p.validFrom||'—'}</td>
                  <td style={{...TD,fontSize:11}}>{p.validTo||'—'}</td>
                  <td style={TD}><span onClick={()=>toggleActive(p.code,!p.active)} style={{cursor:'pointer',padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:700,background:p.active?'rgba(16,185,129,0.12)':'rgba(239,68,68,0.12)',color:p.active?'#34d399':'#f87171'}}>{p.active?'Active':'Inactive'}</span></td>
                  <td style={TD}><div style={{display:'flex',gap:4,flexWrap:'wrap'}}><button onClick={()=>startEdit(p)} style={{padding:'3px 8px',background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.3)',borderRadius:6,color:'#93c5fd',cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>Edit</button><button onClick={()=>toggleActive(p.code,!p.active)} style={{padding:'3px 8px',background:p.active?'rgba(239,68,68,0.1)':'rgba(16,185,129,0.1)',border:`1px solid ${p.active?'rgba(239,68,68,0.3)':'rgba(16,185,129,0.3)'}`,borderRadius:6,color:p.active?'#f87171':'#34d399',cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>{p.active?'Disable':'Enable'}</button><button onClick={()=>deletePromo(p.code)} style={{padding:'3px 8px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:6,color:'#f87171',cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>Delete</button></div></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {promos.length>0&&<div style={{padding:'8px 14px',borderTop:'1px solid var(--surface-muted)',fontSize:11,color:'var(--border-color-hover)'}}>{promos.length} promo code(s) · {promos.filter(p=>p.active).length} active · {promos.reduce((s,p)=>s+(p.usedCount||0),0)} total uses</div>}
      </div>
    </div>
  );
};

// ── Inline commission editor (hospital detail — no tab redirect) ──
const HospitalCommissionModal = ({ open, hospital, commission, toast_, onClose, onSaved }) => {
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (!open || !commission) return;
    setForm({
      commissionPct: commission.commissionPct ?? 2,
      gstPct: commission.gstPct ?? 18,
      gstExempt: !!commission.gstExempt,
      commissionWaived: !!commission.isWaived,
      commissionNotes: commission.commissionNotes || '',
    });
  }, [open, commission]);

  if (!open || !hospital) return null;

  const save = async () => {
    setSaving(true);
    const r = await adminAPI.updateHospitalCommission(hospital.hospitalId, {
      commissionPct: form.commissionWaived ? undefined : Number(form.commissionPct),
      gstExempt: !!form.gstExempt,
      commissionWaived: !!form.commissionWaived,
      commissionNotes: form.commissionNotes || '',
    });
    setSaving(false);
    if (r.error) { toast_(r.error, true); return; }
    toast_('✅ Commission updated for ' + (hospital.name || hospital.hospitalId));
    onSaved?.();
    onClose?.();
  };

  const reset = async () => {
    if (!window.confirm('Reset this hospital to platform default commission rates?')) return;
    setSaving(true);
    const r = await adminAPI.updateHospitalCommission(hospital.hospitalId, {
      commissionWaived: false,
      gstExempt: false,
      commissionNotes: '',
    });
    setSaving(false);
    if (r.error) { toast_(r.error, true); return; }
    toast_('Reset to platform defaults');
    onSaved?.();
    onClose?.();
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.72)', backdropFilter:'blur(6px)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
      onClick={onClose}>
      <div className="glass-card" style={{ width:'100%', maxWidth:440, padding:24 }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:17, fontWeight:800 }}>💰 Edit Commission</div>
            <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>{hospital.name || hospital.hospitalId}</div>
          </div>
          <button type="button" onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:22 }}><MdClose /></button>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
          <div>
            <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:4 }}>Commission %</label>
            <input type="number" step="0.1" min="0" max="100" disabled={form.commissionWaived}
              value={form.commissionPct ?? ''} onChange={e => setForm(p => ({ ...p, commissionPct: e.target.value }))}
              style={{ width:'100%', padding:'10px 12px', background:'var(--surface-muted)', border:'1px solid var(--border-color)', borderRadius:8, color:'#fff', fontSize:15, fontWeight:700, boxSizing:'border-box', fontFamily:'inherit' }} />
          </div>
          <div>
            <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:4 }}>GST on commission %</label>
            <input type="number" step="0.1" min="0" max="100" disabled={form.gstExempt || form.commissionWaived}
              value={form.gstPct ?? ''} onChange={e => setForm(p => ({ ...p, gstPct: e.target.value }))}
              style={{ width:'100%', padding:'10px 12px', background:'var(--surface-muted)', border:'1px solid var(--border-color)', borderRadius:8, color:'#fff', fontSize:15, fontWeight:700, boxSizing:'border-box', fontFamily:'inherit' }} />
          </div>
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, marginBottom:8, cursor:'pointer' }}>
          <input type="checkbox" checked={!!form.commissionWaived} onChange={e => setForm(p => ({ ...p, commissionWaived: e.target.checked }))} />
          Waive all commission for this hospital
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, marginBottom:12, cursor:'pointer' }}>
          <input type="checkbox" checked={!!form.gstExempt} onChange={e => setForm(p => ({ ...p, gstExempt: e.target.checked }))} />
          GST exempt on platform fee
        </label>
        <div style={{ marginBottom:16 }}>
          <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:4 }}>Notes (optional)</label>
          <input value={form.commissionNotes || ''} onChange={e => setForm(p => ({ ...p, commissionNotes: e.target.value }))}
            placeholder="e.g. Launch partner rate"
            style={{ width:'100%', padding:'9px 12px', background:'var(--surface-muted)', border:'1px solid var(--border-color)', borderRadius:8, color:'#fff', fontSize:13, boxSizing:'border-box', fontFamily:'inherit' }} />
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button type="button" onClick={save} disabled={saving}
            style={{ flex:1, padding:'10px 16px', background:'linear-gradient(135deg,#f59e0b,#d97706)', border:'none', borderRadius:9, color:'#000', fontWeight:800, cursor:'pointer', fontFamily:'inherit' }}>
            {saving ? 'Saving…' : 'Save Commission'}
          </button>
          <button type="button" onClick={reset} disabled={saving}
            style={{ padding:'10px 14px', background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.3)', borderRadius:9, color:'#a5b4fc', cursor:'pointer', fontFamily:'inherit' }}>
            Reset
          </button>
        </div>
      </div>
    </div>
  );
};

// ── SettingsTab ──
const SettingsTab = ({ toast_, hospitals, S, H2, TH, TD }) => {
  const [platCfg, setPlatCfg] = React.useState(null);
  const [hospComms, setHospComms] = React.useState([]);
  const [platLoading, setPlatLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [editHosp, setEditHosp] = React.useState(null);
  const [hospSaving, setHospSaving] = React.useState(false);
  const [form, setForm] = React.useState({});
  React.useEffect(()=>{(async()=>{setPlatLoading(true);const [cfgRes,hcRes]=await Promise.all([adminAPI.getPlatformSettings(),adminAPI.getHospitalCommissions()]);if(cfgRes.data){setPlatCfg(cfgRes.data);setForm(cfgRes.data);}if(hcRes.data?.hospitals)setHospComms(hcRes.data.hospitals);setPlatLoading(false);})();},[]);
  const savePlatform=async()=>{setSaving(true);const r=await adminAPI.updatePlatformSettings(form);if(r.data?.config){setPlatCfg(r.data.config);setForm(r.data.config);toast_('✅ Platform settings saved!');}else toast_(r.error||'Failed',true);setSaving(false);};
  const saveHospComm=async(hId,updates)=>{setHospSaving(true);const r=await adminAPI.updateHospitalCommission(hId,updates);if(!r.error){setHospComms(p=>p.map(h=>h.hospitalId===hId?{...h,...updates,customRate:updates.commissionPct!=null}:h));setEditHosp(null);toast_('✅ Hospital commission updated!');}else toast_(r.error,true);setHospSaving(false);};
  const resetHospComm=async(hId)=>{setHospSaving(true);const r=await adminAPI.updateHospitalCommission(hId,{commissionWaived:false,gstExempt:false,commissionNotes:''});if(!r.error){setHospComms(p=>p.map(h=>h.hospitalId===hId?{...h,commissionPct:platCfg?.defaultCommissionPct||2,gstPct:platCfg?.defaultGstPct||18,gstExempt:false,isWaived:false,customRate:false,commissionNotes:''}:h));setEditHosp(null);toast_('Reset to platform defaults');}else toast_(r.error,true);setHospSaving(false);};
  if(platLoading) return <div style={{textAlign:'center',padding:60,color:'var(--text-muted)'}}>Loading platform settings…</div>;
  return(
    <div>
      <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>⚙️ Platform Settings</h1>
      <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:20}}>Global commission, GST, payout, and refund-policy configuration</p>

      {/* ── Refund policy (Auto / Semi-Auto / Manual) ─────────────────────
          Moved here from the Transactions page in May 2026 because it's a
          platform-wide policy, not a per-transaction action. The card has
          its own state + 2-step commit (preview → Save) so a misclick
          never silently re-routes future refund flows. */}
      <RefundPolicyCard onSaved={(m) => toast_(`✅ Refund policy is now ${String(m).toUpperCase()}`)} />

      <div style={S}>
        <div style={{...H2,marginBottom:16}}>💰 Global Commission Defaults</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:14,marginBottom:16}}>
          <div><label style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,display:'block'}}>Platform Commission %</label><input type="number" step="0.1" min="0" max="100" value={form.defaultCommissionPct??''} onChange={e=>setForm(p=>({...p,defaultCommissionPct:e.target.value}))} style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:16,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
          <div><label style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,display:'block'}}>GST on Commission %</label><input type="number" step="0.1" min="0" max="100" value={form.defaultGstPct??''} onChange={e=>setForm(p=>({...p,defaultGstPct:e.target.value}))} style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:16,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
          <div><label style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,display:'block'}}>Min Payout Amount (₹)</label><input type="number" min="0" value={form.minPayoutAmount??''} onChange={e=>setForm(p=>({...p,minPayoutAmount:e.target.value}))} style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:16,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
          <div><label style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,display:'block'}}>Commission Cap (₹, 0=none)</label><input type="number" min="0" value={form.commissionCap??''} onChange={e=>setForm(p=>({...p,commissionCap:e.target.value}))} style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:16,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/></div>
          <div><label style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,display:'block'}}>Payout Cycle</label><select value={form.payoutCycle||'on-demand'} onChange={e=>setForm(p=>({...p,payoutCycle:e.target.value}))} style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}><option value="on-demand">On-Demand</option><option value="weekly">Weekly</option><option value="biweekly">Bi-Weekly</option><option value="monthly">Monthly</option></select></div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <button onClick={savePlatform} disabled={saving} style={{padding:'10px 24px',background:'linear-gradient(135deg,#f59e0b,#d97706)',border:'none',borderRadius:10,color:'#000',fontWeight:800,cursor:'pointer',fontSize:14,fontFamily:'inherit',opacity:saving?0.5:1}}>{saving?'Saving…':'💾 Save Global Settings'}</button>
          <span style={{fontSize:12,color:'var(--text-muted)'}}>Effective deduction: {Number(form.defaultCommissionPct||0)}% + {Number(form.defaultGstPct||0)}% GST = {((Number(form.defaultCommissionPct||0))+(Number(form.defaultCommissionPct||0)*Number(form.defaultGstPct||0)/100)).toFixed(2)}%</span>
        </div>
      </div>
      <div style={{...S,marginTop:16}}>
        <div style={{...H2,marginBottom:12}}>📧 Scheduled Email Reports</div>
        <p style={{fontSize:12,color:'var(--text-muted)',marginBottom:14}}>Send revenue & booking summaries to admin emails.</p>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <button onClick={async()=>{toast_('Sending daily report…');const r=await adminAPI.sendDailyReport();if(!r.error)toast_('✅ Daily report sent!');else toast_(r.error,true);}} style={{padding:'10px 20px',background:'linear-gradient(135deg,#6366f1,#4f46e5)',border:'none',borderRadius:10,color:'#fff',fontWeight:700,cursor:'pointer',fontSize:13,fontFamily:'inherit'}}>📊 Send Daily Report Now</button>
          <button onClick={async()=>{toast_('Sending weekly report…');const r=await adminAPI.sendWeeklyReport();if(!r.error)toast_('✅ Weekly report sent!');else toast_(r.error,true);}} style={{padding:'10px 20px',background:'linear-gradient(135deg,#10b981,#059669)',border:'none',borderRadius:10,color:'#fff',fontWeight:700,cursor:'pointer',fontSize:13,fontFamily:'inherit'}}>📈 Send Weekly Report Now</button>
        </div>
      </div>
      <div style={{...S,marginTop:16}}>
        <div style={{...H2,marginBottom:12}}>🏥 Per-Hospital Commission Rates</div>
        <p style={{fontSize:12,color:'var(--text-muted)',marginBottom:14}}>Override commission for individual hospitals. Blank = uses global default ({platCfg?.defaultCommissionPct||2}%)</p>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{['Hospital','City','Status','Commission %','GST %','Waived','Custom','Notes','Actions'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
            <tbody>{hospComms.filter(h=>h.status==='active').map(h=>{
              const isEditing=editHosp?.hospitalId===h.hospitalId;
              return(<tr key={h.hospitalId} style={{background:isEditing?'rgba(245,158,11,0.06)':'transparent'}}>
                <td style={{...TD,fontWeight:600}}>{h.name||h.hospitalId}</td>
                <td style={TD}>{h.city||'—'}</td>
                <td style={TD}><span style={{padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:600,background:'rgba(16,185,129,0.12)',color:'#34d399'}}>{h.status}</span></td>
                <td style={TD}>{isEditing?<input type="number" step="0.1" min="0" max="100" value={editHosp.commissionPct??''} onChange={e=>setEditHosp(p=>({...p,commissionPct:e.target.value}))} style={{width:60,padding:'4px 6px',background:'var(--border-color-hover)',border:'1px solid rgba(245,158,11,0.4)',borderRadius:6,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none'}}/>:<span style={{color:h.isWaived?'#34d399':h.customRate?'#818cf8':'var(--text-secondary)',fontWeight:h.customRate?700:400}}>{h.isWaived?'0 (waived)':h.commissionPct+'%'}</span>}</td>
                <td style={TD}>{isEditing?<label style={{display:'flex',alignItems:'center',gap:4,fontSize:12}}><input type="checkbox" checked={editHosp.gstExempt||false} onChange={e=>setEditHosp(p=>({...p,gstExempt:e.target.checked}))}/> Exempt</label>:<span style={{color:h.gstExempt?'#34d399':'var(--text-muted)'}}>{h.gstExempt?'Exempt':h.gstPct+'%'}</span>}</td>
                <td style={TD}>{isEditing?<label style={{display:'flex',alignItems:'center',gap:4,fontSize:12}}><input type="checkbox" checked={editHosp.commissionWaived||false} onChange={e=>setEditHosp(p=>({...p,commissionWaived:e.target.checked}))}/> Waive</label>:h.isWaived?<span style={{color:'#34d399',fontWeight:700}}>✓ Waived</span>:'—'}</td>
                <td style={TD}>{h.customRate?<span style={{color:'#818cf8',fontSize:11}}>Custom</span>:<span style={{color:'var(--text-muted)',fontSize:11}}>Default</span>}</td>
                <td style={TD}>{isEditing?<input value={editHosp.commissionNotes||''} onChange={e=>setEditHosp(p=>({...p,commissionNotes:e.target.value}))} placeholder="Reason…" style={{width:120,padding:'4px 6px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:6,color:'#fff',fontSize:12,fontFamily:'inherit',outline:'none'}}/>:<span style={{fontSize:11,color:'var(--text-muted)'}}>{h.commissionNotes||'—'}</span>}</td>
                <td style={TD}>{isEditing?(<div style={{display:'flex',gap:4}}><button onClick={()=>saveHospComm(h.hospitalId,{commissionPct:editHosp.commissionWaived?undefined:Number(editHosp.commissionPct),gstExempt:editHosp.gstExempt,commissionWaived:editHosp.commissionWaived,commissionNotes:editHosp.commissionNotes||''})} disabled={hospSaving} style={{padding:'4px 8px',background:'rgba(16,185,129,0.15)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:6,color:'#34d399',cursor:'pointer',fontSize:11,fontWeight:700,fontFamily:'inherit'}}>Save</button><button onClick={()=>resetHospComm(h.hospitalId)} disabled={hospSaving} style={{padding:'4px 8px',background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:6,color:'#818cf8',cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>Reset</button><button onClick={()=>setEditHosp(null)} style={{padding:'4px 8px',background:'var(--surface-subtle)',border:'1px solid var(--border-color-hover)',borderRadius:6,color:'var(--text-muted)',cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>Cancel</button></div>):(<button onClick={()=>setEditHosp({...h})} style={{padding:'4px 10px',background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:6,color:'#fbbf24',cursor:'pointer',fontSize:11,fontWeight:600,fontFamily:'inherit'}}><MdEdit style={{fontSize:13}}/> Edit</button>)}</td>
              </tr>);
            })}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── AuditTab ──
const AuditTab = ({ S }) => {
  const [auditLogs, setAuditLogs] = React.useState([]);
  const [auditLoading, setAuditLoading] = React.useState(true);
  const [auditFilter, setAuditFilter] = React.useState('');
  const [auditFrom, setAuditFrom] = React.useState('');
  React.useEffect(()=>{(async()=>{setAuditLoading(true);const params={};if(auditFilter)params.action=auditFilter;if(auditFrom)params.from=auditFrom;const r=await adminAPI.getAuditLog(params);if(r.data?.logs)setAuditLogs(r.data.logs);setAuditLoading(false);})();},[auditFilter,auditFrom]);
  const ACTION_TYPES=['LOGIN_SUCCESS','LOGIN_FAILED','ADMIN_USER_CREATED','ADMIN_USER_UPDATED','ADMIN_USER_DELETED','HOSPITAL_CREATED','HOSPITAL_UPDATED','HOSPITAL_DELETED','SETTLEMENT_RECORDED','SETTLEMENT_REQUEST_DISBURSED','SETTLEMENT_REQUEST_REJECTED','SETTLEMENT_REQUEST_PROCESSING','PLATFORM_SETTINGS_UPDATED','REFUND_MODE_CHANGED'];
  const actionColor=(a)=>{if(a.includes('FAILED')||a.includes('DELETED')||a.includes('REJECTED'))return'#f87171';if(a.includes('SUCCESS')||a.includes('CREATED')||a.includes('DISBURSED'))return'#34d399';if(a.includes('UPDATED')||a.includes('CHANGED')||a.includes('RECORDED'))return'#818cf8';return'#fbbf24';};
  const fmtDT=s=>s?new Date(s).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true}):'—';
  const parseDetails=d=>{try{return typeof d==='string'?JSON.parse(d):d;}catch{return d;}};
  return(
    <div>
      <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>🛡️ Audit Log</h1>
      <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:18}}>Complete history of all admin actions for accountability and security</p>
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <select value={auditFilter} onChange={e=>setAuditFilter(e.target.value)} style={{padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none'}}><option value="">All Actions</option>{ACTION_TYPES.map(a=><option key={a} value={a}>{a.replace(/_/g,' ')}</option>)}</select>
        <input type="date" value={auditFrom} onChange={e=>setAuditFrom(e.target.value)} style={{padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none'}}/>
        {(auditFilter||auditFrom)&&<button onClick={()=>{setAuditFilter('');setAuditFrom('');}} style={{padding:'8px 12px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,color:'#f87171',cursor:'pointer',fontSize:12,fontFamily:'inherit'}}>Clear Filters</button>}
      </div>
      {auditLoading?<div style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}>Loading audit logs…</div>:(
        <div style={S}>
          {auditLogs.length===0?(<div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)'}}><div style={{fontSize:48,marginBottom:12}}>🛡️</div><div style={{fontWeight:700,fontSize:15}}>No audit logs yet</div><div style={{fontSize:12,marginTop:6}}>Admin actions will appear here once the HCEN_AuditLog table is created in DynamoDB</div></div>):(
            <div style={{maxHeight:'70vh',overflowY:'auto'}}>
              {auditLogs.map((log,i)=>{const details=parseDetails(log.details);return(
                <div key={log.logId||i} style={{padding:'12px 14px',borderBottom:'1px solid var(--surface-subtle)',display:'flex',gap:14,alignItems:'flex-start'}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:actionColor(log.actionType||''),flexShrink:0,marginTop:6}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                      <span style={{padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:700,background:`${actionColor(log.actionType||'')}15`,color:actionColor(log.actionType||''),fontFamily:'monospace'}}>{(log.actionType||'UNKNOWN').replace(/_/g,' ')}</span>
                      <span style={{fontSize:11,color:'var(--text-muted)'}}>{fmtDT(log.createdAt)}</span>
                      <span style={{fontSize:11,color:'var(--border-color-hover)',marginLeft:'auto'}}>{log.adminEmail||'admin'}</span>
                    </div>
                    {details&&typeof details==='object'&&(<div style={{marginTop:6,fontSize:12,color:'var(--text-muted)',fontFamily:'monospace',lineHeight:1.5}}>{Object.entries(details).slice(0,6).map(([k,v])=>(<span key={k} style={{marginRight:14}}><span style={{color:'var(--text-muted)'}}>{k}:</span> <span style={{color:'#a5b4fc'}}>{typeof v==='object'?JSON.stringify(v):String(v)}</span></span>))}</div>)}
                    {details&&typeof details==='string'&&<div style={{marginTop:4,fontSize:12,color:'var(--text-muted)'}}>{details}</div>}
                  </div>
                </div>
              );})}
            </div>
          )}
          <div style={{padding:'10px 14px',borderTop:'1px solid var(--surface-muted)',fontSize:11,color:'var(--border-color-hover)'}}>Showing {auditLogs.length} entries{auditFilter?` filtered by ${auditFilter}`:''}</div>
        </div>
      )}
    </div>
  );
};

// ── TeamTab — owner-only management of admin logins (CTO / COO / Manager) ──
const ROLE_LABELS = { owner: 'Owner / Director', cto: 'CTO', coo: 'COO', manager: 'Manager' };
const ROLE_COLORS = { owner: '#fbbf24', cto: '#818cf8', coo: '#34d399', manager: '#22d3ee' };
// Built-in suggestions; you can also type any custom role (e.g. "Finance Head").
const PRESET_ROLES = ['cto', 'coo', 'manager'];
const CUSTOM_ROLE_COLORS = ['#f472b6', '#fb923c', '#a78bfa', '#38bdf8', '#4ade80', '#facc15', '#f87171', '#2dd4bf'];
// Nice display label for any role string (presets get a fixed label, custom
// roles are shown as typed).
const roleLabel = (r) => ROLE_LABELS[String(r || '').toLowerCase()] || String(r || '').trim() || 'Member';
// Stable color for any role (presets fixed, custom roles hashed to a palette).
const roleColor = (r) => {
  const key = String(r || '').toLowerCase();
  if (ROLE_COLORS[key]) return ROLE_COLORS[key];
  let h = 0; for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return CUSTOM_ROLE_COLORS[h % CUSTOM_ROLE_COLORS.length];
};
const TeamTab = ({ S, toast }) => {
  const [users, setUsers]     = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy]       = React.useState(false);
  const [form, setForm]       = React.useState({ name: '', email: '', role: 'Manager', password: '' });
  const [resetFor, setResetFor] = React.useState(null); // email being password-reset
  const [resetPwd, setResetPwd] = React.useState('');

  // Load once on mount. Deps are intentionally empty: `toast` is an inline
  // function from the parent that changes identity on every render, so keeping
  // it in the dep array would re-create `load` → re-fire the effect → refetch
  // in an endless loop (the "glitching" + stuck "Loading team…").
  const load = React.useCallback(async () => {
    setLoading(true);
    const r = await adminAPI.getAdminUsers();
    if (r.data?.users) setUsers(r.data.users);
    else if (r.error) toast?.(r.error, 'error');
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { load(); }, [load]);

  const fmtDT = s => s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

  const createUser = async () => {
    if (!form.name.trim())  return toast?.('Enter a name', 'error');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast?.('Enter a valid email', 'error');
    const role = String(form.role || '').trim().replace(/\s+/g, ' ');
    if (!role) return toast?.('Pick or type a role', 'error');
    if (role.toLowerCase() === 'owner') return toast?.('"Owner" is reserved for your Director account', 'error');
    if (form.password.length < 8) return toast?.('Password must be at least 8 characters', 'error');
    setBusy(true);
    const r = await adminAPI.createAdminUser({ name: form.name.trim(), email: form.email.trim().toLowerCase(), role, password: form.password });
    setBusy(false);
    if (r.error) return toast?.(r.error, 'error');
    toast?.(`${roleLabel(role)} login created`, 'success');
    setForm({ name: '', email: '', role: 'Manager', password: '' });
    load();
  };

  const toggleActive = async (u) => {
    const r = await adminAPI.updateAdminUser(u.email, { active: !u.active });
    if (r.error) return toast?.(r.error, 'error');
    toast?.(u.active ? `${u.email} disabled` : `${u.email} enabled`, 'success');
    load();
  };

  const changeRole = async (u, role) => {
    const r = await adminAPI.updateAdminUser(u.email, { role });
    if (r.error) return toast?.(r.error, 'error');
    toast?.('Role updated', 'success');
    load();
  };

  const doReset = async () => {
    if (resetPwd.length < 8) return toast?.('Password must be at least 8 characters', 'error');
    const r = await adminAPI.updateAdminUser(resetFor, { password: resetPwd });
    if (r.error) return toast?.(r.error, 'error');
    toast?.('Password reset', 'success');
    setResetFor(null); setResetPwd('');
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove admin login for ${u.email}? They will no longer be able to sign in.`)) return;
    const r = await adminAPI.deleteAdminUser(u.email);
    if (r.error) return toast?.(r.error, 'error');
    toast?.('Admin login removed', 'success');
    load();
  };

  // Preset roles + any custom roles already in use → shown as suggestions.
  const knownRoles = React.useMemo(() => {
    const map = new Map();
    PRESET_ROLES.forEach(r => map.set(r.toLowerCase(), roleLabel(r)));
    users.filter(u => !u.permanent).forEach(u => {
      const r = String(u.role || '').trim();
      if (r && !map.has(r.toLowerCase())) map.set(r.toLowerCase(), r);
    });
    return Array.from(map.values());
  }, [users]);

  const inp = { padding: '9px 12px', background: 'var(--surface-muted)', border: '1px solid var(--surface-strong)', borderRadius: 8, color: '#fff', fontSize: 13, fontFamily: 'inherit', outline: 'none' };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 3 }}>👥 Team Logins</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 18 }}>
        Create a separate login for each person. Pick a role (CTO, COO, Manager) or type your own custom role. Everything they do is tagged with their name in the Audit Log. Only you (Owner/Director) can see this page.
      </p>

      {/* Create form */}
      <div style={{ ...S, padding: 16, marginBottom: 18 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 7 }}>
          <MdAdd style={{ color: '#fbbf24' }} /> Add a new admin login
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10 }}>
          <input placeholder="Full name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inp} />
          <input placeholder="Email (login)" type="email" autoComplete="off" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} style={inp} />
          <input list="teamRoleOptions" placeholder="Role — pick or type" value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} style={inp} />
          <datalist id="teamRoleOptions">
            {knownRoles.map(r => <option key={r} value={r} />)}
          </datalist>
          <input placeholder="Temp password (min 8)" type="text" autoComplete="new-password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} style={inp} />
        </div>
        <button onClick={createUser} disabled={busy} className="btn btn-primary" style={{ marginTop: 12, justifyContent: 'center' }}>
          <MdAdd /> {busy ? 'Creating…' : 'Create login'}
        </button>
      </div>

      {/* Users list */}
      {loading ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading team…</div> : (
        <div style={S}>
          {users.map((u) => (
            <div key={u.email} style={{ padding: '14px 16px', borderBottom: '1px solid var(--surface-subtle)', display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', opacity: u.active === false ? 0.55 : 1 }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: `${roleColor(u.role)}22`, color: roleColor(u.role), display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0 }}>
                {(u.name || u.email || '?').trim().charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{u.name || u.email}
                  {u.permanent && <span style={{ marginLeft: 8, fontSize: 10, background: 'rgba(245,158,11,0.15)', color: '#fbbf24', padding: '2px 7px', borderRadius: 20, fontWeight: 700 }}>YOU · permanent</span>}
                  {u.mfa && <span style={{ marginLeft: 8, fontSize: 10, background: 'rgba(52,211,153,0.15)', color: '#34d399', padding: '2px 7px', borderRadius: 20, fontWeight: 700 }}>🔒 MFA on</span>}
                  {u.status === 'FORCE_CHANGE_PASSWORD' && <span style={{ marginLeft: 8, fontSize: 10, background: 'rgba(99,102,241,0.15)', color: '#818cf8', padding: '2px 7px', borderRadius: 20, fontWeight: 700 }}>must set password</span>}
                  {u.active === false && <span style={{ marginLeft: 8, fontSize: 10, background: 'rgba(239,68,68,0.15)', color: '#f87171', padding: '2px 7px', borderRadius: 20, fontWeight: 700 }}>DISABLED</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.email}</div>
                <div style={{ fontSize: 11, color: 'var(--border-color-hover)', marginTop: 2 }}>
                  {u.permanent ? 'Break-glass owner (env)' : `Added: ${fmtDT(u.createdAt)}`}
                </div>
              </div>
              {u.permanent ? (
                <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, background: `${ROLE_COLORS.owner}18`, color: ROLE_COLORS.owner }}>{ROLE_LABELS.owner}</span>
              ) : (
                <>
                  <select
                    value={knownRoles.find(r => r.toLowerCase() === String(u.role || '').toLowerCase()) || '__cur__'}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === '__add__') {
                        const custom = (window.prompt('Enter a custom role name:', '') || '').trim();
                        if (!custom) return;
                        if (custom.toLowerCase() === 'owner') return toast?.('"Owner" is reserved', 'error');
                        changeRole(u, custom);
                      } else if (v !== '__cur__') {
                        changeRole(u, v);
                      }
                    }}
                    style={{ ...inp, padding: '6px 10px' }}>
                    {!knownRoles.some(r => r.toLowerCase() === String(u.role || '').toLowerCase()) &&
                      <option value="__cur__">{roleLabel(u.role)}</option>}
                    {knownRoles.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                    <option value="__add__">＋ Custom role…</option>
                  </select>
                  <button title={u.active === false ? 'Enable' : 'Disable'} onClick={() => toggleActive(u)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: u.active === false ? '#f87171' : '#34d399', fontSize: 26, display: 'flex' }}>
                    {u.active === false ? <MdToggleOff /> : <MdToggleOn />}
                  </button>
                  {resetFor === u.email ? (
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input autoFocus placeholder="New password" type="text" value={resetPwd} onChange={e => setResetPwd(e.target.value)} style={{ ...inp, padding: '6px 10px', width: 140 }} />
                      <button onClick={doReset} className="btn btn-primary btn-sm"><MdSave /></button>
                      <button onClick={() => { setResetFor(null); setResetPwd(''); }} className="btn btn-secondary btn-sm"><MdClose /></button>
                    </span>
                  ) : (
                    <button title="Reset password" onClick={() => { setResetFor(u.email); setResetPwd(''); }} style={{ background: 'none', border: '1px solid var(--surface-strong)', borderRadius: 8, cursor: 'pointer', color: '#818cf8', padding: '7px 9px', display: 'flex' }}>
                      <MdLockReset />
                    </button>
                  )}
                  <button title="Remove login" onClick={() => removeUser(u)} style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, cursor: 'pointer', color: '#f87171', padding: '7px 9px', display: 'flex' }}>
                    <MdDelete />
                  </button>
                </>
              )}
            </div>
          ))}
          <div style={{ padding: '10px 14px', fontSize: 11, color: 'var(--border-color-hover)' }}>{users.length} login{users.length === 1 ? '' : 's'} total</div>
        </div>
      )}
    </div>
  );
};

export default function AdminPanel({ admin, onLogout }) {
  const [tab,    setTab]    = useState('dashboard');
  const [search, setSearch] = useState('');
  const [toast,  setToast]  = useState(null);
  const [loading,setLoading]= useState(false);
  // Owner/Director sees Team Logins + Audit Log; CTO/COO/Manager do not.
  // ('admin' kept for backward-compat with the pre-roles owner token.)
  const isOwner = ['owner', 'admin'].includes(String(admin?.role || '').toLowerCase());
  const [dataErrors, setDataErrors]   = useState([]);
  const [patientApiError, setPatientApiError] = useState(null);
  const [ticketSummaryLoaded, setTicketSummaryLoaded] = useState(false);
  const [referralSummaryLoaded, setReferralSummaryLoaded] = useState(false);

  const [analytics,   setAnalytics]   = useState(null);
  const [hospitals,   setHospitals]   = useState([]);
  const [bookings,    setBookings]    = useState([]);
  const [patients,    setPatients]    = useState([]);
  const [doctors,     setDoctors]     = useState([]);
  const [emergencies, setEmergencies] = useState([]);

  // Hospital detail state
  const [selectedH,  setSelectedH]  = useState(null);
  const [hDetail,    setHDetail]    = useState(null);
  const [hTab,       setHTab]       = useState('overview');
  const [loadingH,   setLoadingH]   = useState(false);
  const [hError,     setHError]     = useState(null);
  const [hActivity,  setHActivity]  = useState(null);
  const [hActLoading,setHActLoading]= useState(false);
  const [showCommissionEdit, setShowCommissionEdit] = useState(false);
  const [ipLinkCode,    setIpLinkCode]    = useState(null);
  const [ipPasteCode,   setIpPasteCode]   = useState('');

  // Support lookup state
  const [supportQ,      setSupportQ]      = useState('');
  const [supportResult, setSupportResult] = useState(null);
  const [supportLoading,setSupportLoading]= useState(false);

  // Activity feed (dashboard live feed)
  const [actFeed,      setActFeed]      = useState(null);
  const [actFeedLoaded,setActFeedLoaded]= useState(false);

  // Support tickets + Refer&Earn dashboard summary
  const [ticketSummary,   setTicketSummary]   = useState(null);
  const [referralSummary, setReferralSummary] = useState(null);
  const [contactLeadSummary, setContactLeadSummary] = useState(null);

  // Intelligence tab state
  const [intelQ,       setIntelQ]       = useState('');
  const [intelResult,  setIntelResult]  = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [intelTab,     setIntelTab]     = useState('timeline');

  // Bookings tab filter (lifted from IIFE to fix Rules of Hooks)
  const [bFilter, setBFilter] = useState('all');

  const [settReqs,     setSettReqs]     = useState([]);
  const [settStats,    setSettStats]    = useState({});
  const [settLoading,  setSettLoading]  = useState(false);
  const [settLoaded,   setSettLoaded]   = useState(false);
  const [settTab,      setSettTab]      = useState('all');
  const [disburseNote, setDisburseNote] = useState({});
  const [txnRef,       setTxnRef]       = useState({});
  const [disbursing,   setDisbursing]   = useState({});

  // Hospital Applications state
  const [applications,    setApplications]    = useState([]);
  const [appLoading,    setAppLoading]    = useState(false);
  const [appError,      setAppError]      = useState(null);
  const [settError,     setSettError]     = useState(null);
  const [selectedApp,   setSelectedApp]   = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [appDetail,     setAppDetail]     = useState(null);

  // Modals
  const [showAdd,    setShowAdd]    = useState(false);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [settlement, setSettlement] = useState({amount:'',note:'',txnRef:''});
  const [addForm,    setAddForm]    = useState({
    name:'', email:'', phone:'', emergencyPhone:'',
    loginPassword:'', type:'Private', rating:'0',
  });
  const [createStep,    setCreateStep]    = useState(1);
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [selectedFacs,  setSelectedFacs]  = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [locQuery,      setLocQuery]      = useState('');
  const [locSelected,   setLocSelected]   = useState(null);
  const [otherDept,     setOtherDept]     = useState('');
  const [createdCreds, setCreatedCreds] = useState(null);
  const [docForm,    setDocForm]    = useState({name:'',specialization:'',phone:'',experience:'',consultationFee:''});

  // Save existing hospital's location (called by LocationMapPicker onChange in overview tab)
  const saveHospitalLocation = async (hId, loc) => {
    const r = await adminAPI.updateHospital(hId, { latitude: loc.lat, longitude: loc.lng, address: loc.display, city: loc.city||'', state: loc.state||'' });
    if (r.error) { setToast({m:'❌ '+r.error,e:true}); setTimeout(()=>setToast(null),3500); return; }
    setHospitals(p => p.map(x => x.hospitalId===hId ? {...x, latitude:loc.lat, longitude:loc.lng, address:loc.display, city:loc.city} : x));
    setHDetail(p => p ? {...p, hospital:{...p.hospital, latitude:loc.lat, longitude:loc.lng, address:loc.display, city:loc.city}} : p);
    setToast({m:'✅ Location saved! Hospital will now appear on patient map.',e:false}); setTimeout(()=>setToast(null),3500);
  };

  const toast_ = useCallback((m,e=false)=>{ setToast({m,e}); setTimeout(()=>setToast(null),3500); },[]);

  const supportLookup = useCallback((q) => {
    if (!q.trim()) { setSupportResult(null); return; }
    setSupportLoading(true);
    const ql = q.toLowerCase().trim();
    const matchP = patients.filter(p =>
      p.phone?.includes(ql) || p.patientName?.toLowerCase().includes(ql) ||
      p.email?.toLowerCase().includes(ql)
    );
    const matchB = bookings.filter(b =>
      b.patientPhone?.includes(ql) || b.patientName?.toLowerCase().includes(ql) ||
      b.bookingId?.toLowerCase().includes(ql)
    );
    const matchE = emergencies.filter(e =>
      e.phone?.includes(ql) || e.patientName?.toLowerCase().includes(ql)
    );
    setSupportResult({ patients: matchP, bookings: matchB, emergencies: matchE, q });
    setSupportLoading(false);
  }, [patients, bookings, emergencies]);

  // `silent`=true skips the loading spinner — used by background polling
  // so refreshes don't flicker the UI.
  const adminWithRetry = async (fn, retries = 2) => {
    let last = { data: null, error: 'Request failed' };
    for (let attempt = 0; attempt <= retries; attempt++) {
      last = await fn();
      if (!last?.error) return last;
      const msg = String(last.error);
      const retryable = /service unavailable|failed to fetch|network error|502|503|504|timeout/i.test(msg);
      if (!retryable || attempt === retries) return last;
      await new Promise(r => setTimeout(r, 900 * (attempt + 1)));
    }
    return last;
  };

  const applyAnalyticsPayload = useCallback((d) => {
    if (!d) return;
    setAnalytics(d);
    if (d.ticketsSummary) {
      setTicketSummary(d.ticketsSummary);
      setTicketSummaryLoaded(true);
    }
    if (d.referralsSummary) {
      setReferralSummary(d.referralsSummary);
      setReferralSummaryLoaded(true);
    }
  }, []);

  const fetchAll = useCallback(async(silent=false)=>{
    if (!silent) setLoading(true);
    const errors = [];
    const ingest = (result, label, apply) => {
      if (result.status === 'rejected') {
        errors.push(`${label}: ${result.reason?.message || 'Network error'}`);
        return;
      }
      const val = result.value;
      if (val?.error) {
        errors.push(`${label}: ${val.error}`);
        return;
      }
      if (val?.data != null) apply(val.data);
    };
    try {
      const [aR,hR,bR,pR,dR,eR] = await Promise.allSettled([
        adminWithRetry(() => adminAPI.getAnalytics()),
        adminWithRetry(() => adminAPI.listHospitals()),
        adminWithRetry(() => adminAPI.getBookings()),
        adminWithRetry(() => adminAPI.getPatients()),
        adminWithRetry(() => adminAPI.getDoctors()),
        adminWithRetry(() => adminAPI.getEmergencies()),
      ]);
      ingest(aR, 'Analytics', applyAnalyticsPayload);
      ingest(hR, 'Hospitals', (d) => setHospitals(d.hospitals || []));
      ingest(bR, 'Bookings', (d) => setBookings(d.bookings || []));
      ingest(pR, 'Patients', (d) => setPatients(d.patients || []));
      ingest(dR, 'Doctors', (d) => setDoctors(d.doctors || []));
      ingest(eR, 'Emergencies', (d) => setEmergencies(d.emergencies || []));
      setDataErrors(errors);
    } catch (e) {
      console.error(e);
      setDataErrors([`Dashboard: ${e.message || 'Unexpected error'}`]);
    }
    if (!silent) setLoading(false);
  },[applyAnalyticsPayload]);

  useEffect(()=>{ fetchAll(); },[fetchAll]);

  const loadApplications = useCallback(async () => {
    setAppLoading(true);
    setAppError(null);
    const r = await adminApplicationsAPI.getAll();
    if (r.error) setAppError(r.error);
    else if (r.data?.applications) setApplications(r.data.applications);
    setAppLoading(false);
  }, []);

  useEffect(() => { loadApplications(); }, [loadApplications]);

  useEffect(() => {
    if (tab === 'applications') loadApplications();
  }, [tab, loadApplications]);

  const loadContactLeadSummary = useCallback(async () => {
    const r = await adminContactInquiriesAPI.list();
    if (!r.error && r.data?.summary) setContactLeadSummary(r.data.summary);
  }, []);

  useEffect(() => { loadContactLeadSummary(); }, [loadContactLeadSummary]);

  useEffect(() => {
    if (tab === 'contact-leads') loadContactLeadSummary();
  }, [tab, loadContactLeadSummary]);

  // ── Live refresh · tiered polling (fast lists + slower overview) ───────
  // Full 6-scan fetchAll every 1.5s was hammering DynamoDB and freezing UI.
  // Fast tick (2.5s): bookings + emergencies — what changes most often.
  // Slow tick (12s): analytics, hospitals, patients, doctors — overview data.
  // Tab-scoped: quiet tabs (settings, settlements, tickets) skip fast poll.
  useEffect(() => {
    let fastInFlight = false;
    let slowInFlight = false;
    let slowTick = 0;
    const tabHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const quietTabs = new Set(['settings', 'settlements', 'tickets', 'referrals', 'audit', 'promos', 'applications', 'contact-leads']);
    const bookingsFpRef = { current: '' };
    const emergenciesFpRef = { current: '' };
    const hospitalsFpRef = { current: '' };
    const patientsFpRef = { current: '' };
    const doctorsFpRef = { current: '' };
    const listFp = (arr, idKey = 'id') => {
      if (!arr?.length) return '0';
      const first = arr[0];
      const last = arr[arr.length - 1];
      return `${arr.length}:${first[idKey] || first.bookingId || first.alertId}:${last[idKey] || last.bookingId || last.alertId}`;
    };

    const pollFast = async () => {
      if (fastInFlight || tabHidden() || quietTabs.has(tab) || (tab === 'hospitals' && selectedH)) return;
      fastInFlight = true;
      try {
        const [bR, eR] = await Promise.allSettled([
          adminAPI.getBookings(),
          adminAPI.getEmergencies(),
        ]);
        if (bR.status === 'fulfilled' && bR.value?.data) {
          const next = bR.value.data.bookings || [];
          const fp = listFp(next, 'bookingId');
          if (fp !== bookingsFpRef.current) {
            bookingsFpRef.current = fp;
            setBookings(next);
          }
        }
        if (eR.status === 'fulfilled' && eR.value?.data) {
          const next = eR.value.data.emergencies || [];
          const fp = listFp(next, 'alertId');
          if (fp !== emergenciesFpRef.current) {
            emergenciesFpRef.current = fp;
            setEmergencies(next);
          }
        }
      } finally { fastInFlight = false; }
    };

    const pollSlow = async () => {
      if (slowInFlight || tabHidden()) return;
      slowInFlight = true;
      try {
        const [aR, hR, pR, dR] = await Promise.allSettled([
          adminAPI.getAnalytics(),
          adminAPI.listHospitals(),
          adminAPI.getPatients(),
          adminAPI.getDoctors(),
        ]);
        if (aR.status === 'fulfilled' && aR.value?.data) applyAnalyticsPayload(aR.value.data);
        if (hR.status === 'fulfilled' && hR.value?.data) {
          const next = hR.value.data.hospitals || [];
          const fp = listFp(next, 'hospitalId');
          if (fp !== hospitalsFpRef.current) {
            hospitalsFpRef.current = fp;
            setHospitals(next);
          }
        }
        if (pR.status === 'fulfilled' && pR.value?.data) {
          const next = pR.value.data.patients || [];
          const fp = listFp(next, 'phone');
          if (fp !== patientsFpRef.current) {
            patientsFpRef.current = fp;
            setPatients(next);
          }
        }
        if (dR.status === 'fulfilled' && dR.value?.data) {
          const next = dR.value.data.doctors || [];
          const fp = listFp(next, 'doctorId');
          if (fp !== doctorsFpRef.current) {
            doctorsFpRef.current = fp;
            setDoctors(next);
          }
        }
      } finally { slowInFlight = false; }
    };

    const onFast = () => { pollFast(); };
    const onSlow = () => { slowTick += 1; pollSlow(); if (slowTick % 2 === 0) pollFast(); };

    onFast();
    onSlow();
    const fastId = setInterval(onFast, 3500);
    const slowId = setInterval(onSlow, 15000);
    return () => { clearInterval(fastId); clearInterval(slowId); };
  }, [tab, selectedH?.hospitalId, applyAnalyticsPayload]);
  useEffect(()=>{ setSearch(''); if (tab !== 'hospitals') { setSelectedH(null); setHDetail(null); setShowCommissionEdit(false); } setHError(null); setHActivity(null); },[tab]);
  useEffect(() => { setShowCommissionEdit(false); }, [selectedH?.hospitalId]);

  // Load activity feed when switching to dashboard
  useEffect(() => {
    if (tab !== 'dashboard' || actFeedLoaded) return;
    adminAPI.getActivityFeed(60).then(r => {
      if (r.data) setActFeed(r.data);
      setActFeedLoaded(true);
    }).catch(() => setActFeedLoaded(true));
  }, [tab, actFeedLoaded]);

  // Enrich ticket/referral cards from dedicated endpoints when available.
  // Dashboard primary source is /admin/analytics (ticketsSummary + referralsSummary).
  useEffect(() => {
    let alive = true;
    const enrichSummaries = async () => {
      try {
        const [tR, rR] = await Promise.all([
          adminWithRetry(() => adminTicketsAPI.list()),
          adminWithRetry(() => adminReferralsAPI.list()),
        ]);
        if (!alive) return;
        if (!tR.error && tR.data?.summary) setTicketSummary(tR.data.summary);
        if (!rR.error) setReferralSummary(rR.data?.summary || rR.data || null);
        setPatientApiError(null);
      } catch (_) {
        /* analytics fallback keeps cards populated */
      } finally {
        if (alive) {
          setTicketSummaryLoaded(true);
          setReferralSummaryLoaded(true);
        }
      }
    };
    enrichSummaries();
    const id = setInterval(enrichSummaries, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Intelligence lookup
  const intelLookup = useCallback(async (q) => {
    if (!q.trim()) { setIntelResult(null); return; }
    setIntelLoading(true);
    const isPhone = /\d{7,}/.test(q.trim());
    const r = await adminAPI.getPatientTimeline(isPhone ? q.trim() : '', isPhone ? '' : q.trim());
    if (r.data) { setIntelResult(r.data); setIntelTab('timeline'); }
    else setIntelResult({ error: r.error || 'Not found' });
    setIntelLoading(false);
  }, []);

  // Load settlement requests when:
  //   • switching to the global Settlements tab, OR
  //   • opening a hospital's Settlement tab on the detail page
  // (Phase A · May 29 2026: the per-hospital history list reuses settReqs
  //  filtered by hospitalId, so we must populate it the moment that tab
  //  becomes visible too.)
  const reloadSettlements = useCallback((silent = false) => {
    if (!silent) setSettLoading(true);
    return adminSettlementAPI.getAll().then(r => {
      if (r.error) {
        setSettError(r.error);
      } else {
        setSettError(null);
        if (r.data) {
          setSettReqs(Array.isArray(r.data) ? r.data : (r.data.requests || []));
          setSettStats(r.data.stats || {});
        }
      }
      setSettLoaded(true);
      if (!silent) setSettLoading(false);
    }).catch((e) => {
      setSettError(e.message || 'Failed to load settlements');
      if (!silent) setSettLoading(false);
    });
  }, []);

  useEffect(() => {
    const needsLoad = (tab === 'settlements' || hTab === 'settlement') && !settLoaded;
    if (!needsLoad) return;
    reloadSettlements();
  }, [tab, hTab, settLoaded, reloadSettlements]);

  // Live settlement ledger refresh (Razorpay-style transparency)
  useEffect(() => {
    if (tab !== 'settlements' && hTab !== 'settlement') return undefined;
    const id = setInterval(() => {
      reloadSettlements(true);
      if (selectedH && hTab === 'settlement') {
        adminAPI.getHospitalDetails(selectedH.hospitalId).then(r => {
          if (r.data) setHDetail(r.data);
        }).catch(() => {});
      }
    }, 5000);
    return () => clearInterval(id);
  }, [tab, hTab, reloadSettlements, selectedH]);

  const loadDetail = useCallback(async(h)=>{
    setHError(null);
    setSelectedH(h);
    setHTab('overview');
    setLoadingH(true);
    setHDetail(null);
    try {
      const r = await adminAPI.getHospitalDetails(h.hospitalId);
      if(r.data) {
        setHDetail({
          ...r.data,
          hospital: r.data.hospital || h,
          doctors: Array.isArray(r.data.doctors) ? r.data.doctors : [],
          bookings: Array.isArray(r.data.bookings) ? r.data.bookings : [],
          emergencies: Array.isArray(r.data.emergencies) ? r.data.emergencies : [],
          stats: r.data.stats || {},
        });
      }
      else setHDetail({ hospital:h, doctors:[], bookings:[], emergencies:[], stats:{} });
    } catch(e){
      setHDetail({ hospital:h, doctors:[], bookings:[], emergencies:[], stats:{} });
    }
    setLoadingH(false);
  },[]);

  const filt = (arr,keys) => arr.filter(x=>!search||keys.some(k=>String(x[k]||'').toLowerCase().includes(search.toLowerCase())));

  // ── Hospital actions ──────────────────────────────────────────
  const saveField = async(hId, field, value) => {
    const r = await adminAPI.updateHospital(hId, {[field]:value});
    if(r.error){ toast_(r.error,true); return; }
    setHospitals(p=>p.map(h=>h.hospitalId===hId?{...h,[field]:value}:h));
    setHDetail(p=>p?{...p,hospital:{...p.hospital,[field]:value}}:p);
    toast_(`${field} saved ✅`);
  };
  const toggleH = async(h) => {
    const ns = h.status==='inactive'?'active':'inactive';
    await adminAPI.updateHospital(h.hospitalId,{status:ns});
    setHospitals(p=>p.map(x=>x.hospitalId===h.hospitalId?{...x,status:ns}:x));
    setHDetail(p=>p?{...p,hospital:{...p.hospital,status:ns}}:p);
    toast_(ns==='active'?'Hospital enabled ✅':'Hospital disabled');
  };
  const verifyBank = async(h) => {
    const r = await adminAPI.verifyBank(h.hospitalId);
    if (r.error) { toast_(r.error, true); return; }
    const verified = {
      bankVerified: true,
      bankVerifiedAt: r.data?.bankVerifiedAt || new Date().toISOString(),
    };
    setHospitals(p => p.map(x => x.hospitalId === h.hospitalId ? { ...x, ...verified } : x));
    setSelectedH(prev => prev?.hospitalId === h.hospitalId ? { ...prev, ...verified } : prev);
    setHDetail(p => p ? { ...p, hospital: { ...p.hospital, ...verified } } : p);
    toast_('✅ Bank account verified!');
  };

  // ── Doctor actions ────────────────────────────────────────────
  const toggleDoctor = async(d) => {
    // Send BOTH flags so the doctor record stays internally consistent —
    // the hospital portal/patient app read `available` while the admin reads
    // `isAvailable`. The admin lambda mirrors them too as a defence in depth.
    const next = !isDoctorAvailable(d);
    await adminAPI.updateDoctor(d.doctorId, { isAvailable: next, available: next });
    setDoctors(p=>p.map(x=>x.doctorId===d.doctorId?{...x,isAvailable:next,available:next}:x));
    setHDetail(p=>p?{...p,doctors:(p.doctors||[]).map(x=>x.doctorId===d.doctorId?{...x,isAvailable:next,available:next}:x)}:p);
    toast_(`Dr. ${d.name} ${next?'available':'unavailable'}`);
  };
  const createDoctor = async(e) => {
    e.preventDefault();
    if(!selectedH) return;
    const r = await adminAPI.createDoctor(selectedH.hospitalId, {
      ...docForm, consultationFee: Number(docForm.consultationFee)||0,
    });
    if(r.error){ toast_(r.error,true); return; }
    toast_('Doctor added ✅');
    setShowAddDoc(false);
    setDocForm({name:'',specialization:'',phone:'',experience:'',consultationFee:''});
    loadDetail(selectedH);
    fetchAll();
  };

  // ── Emergency actions ─────────────────────────────────────────
  const updateEm = async(e,status) => {
    await adminAPI.updateEmergency(e.alertId,status);
    setEmergencies(p=>p.map(x=>x.alertId===e.alertId?{...x,status}:x));
    setHDetail(p=>p?{...p,emergencies:(p.emergencies||[]).map(x=>x.alertId===e.alertId?{...x,status}:x)}:p);
    toast_(`Emergency marked ${status}`);
  };

  // ── Delete ────────────────────────────────────────────────────
  const doDelete = async() => {
    if(!confirmDel) return;
    const {type,item} = confirmDel;
    setConfirmDel(null);
    if(type==='hospital'){
      const r = await adminAPI.deleteHospital(item.hospitalId);
      if(r.error){ toast_(r.error,true); return; }
      setHospitals(p=>p.filter(x=>x.hospitalId!==item.hospitalId));
      setSelectedH(null); setHDetail(null);
      toast_('Hospital deleted');
    }
    if(type==='booking'){
      const bid = item.bookingIds || item.bookingId;
      const r = await adminAPI.deleteBooking(bid);
      if (r.error) { toast_(r.error, true); return; }
      setBookings(p=>p.filter(x=>(x.bookingIds||x.bookingId)!==bid));
      setHDetail(p=>p?{...p,bookings:(p.bookings||[]).filter(x=>(x.bookingIds||x.bookingId)!==bid)}:p);
      toast_('Booking deleted');
    }
    if(type==='doctor'){
      await adminAPI.deleteDoctor(item.doctorId);
      setDoctors(p=>p.filter(x=>x.doctorId!==item.doctorId));
      setHDetail(p=>p?{...p,doctors:(p.doctors||[]).filter(x=>x.doctorId!==item.doctorId)}:p);
      toast_('Doctor removed');
    }
    if(type==='ipunlink'){
      const r = await adminAPI.unlinkIp(item.hospitalId);
      if(r.error){ toast_(r.error,true); return; }
      setHDetail(p=>p?{...p,hospital:{...p.hospital,networksHospitalId:null,networksHospitalName:null,networksLinkedAt:null}}:p);
      setHospitals(p=>p.map(x=>x.hospitalId===item.hospitalId?{...x,networksHospitalId:null,networksHospitalName:null,networksLinkedAt:null}:x));
      toast_('🔓 IP hospital unlinked');
    }
  };

  // ── Create hospital helpers ─────────────────────────────────────
  const resetCreateForm = () => {
    setAddForm({ name:'', email:'', phone:'', emergencyPhone:'', loginPassword:'', type:'Private', rating:'0' });
    setSelectedDepts([]); setSelectedFacs([]);
    setLocQuery(''); setLocSelected(null);
    setOtherDept(''); setCreateStep(1);
  };
  const toggleDept = (d) => setSelectedDepts(p => p.includes(d) ? p.filter(x=>x!==d) : [...p, d]);
  const toggleFacility = (f) => setSelectedFacs(p => p.includes(f) ? p.filter(x=>x!==f) : [...p, f]);

  const createHospital = async() => {
    if (!addForm.name.trim() || !addForm.email.trim()) { toast_('Hospital name and email are required', true); return; }
    if (!locSelected) { toast_('⚠️ Please search and confirm the hospital location — this determines patient routing!', true); return; }
    if (!addForm.emergencyPhone.trim()) { toast_('❗ Emergency phone is required — this is critical for patient safety!', true); return; }
    if (selectedDepts.length === 0) { toast_('Please select at least one department', true); return; }
    setLoading(true);
    const r = await adminAPI.createHospital({
      name: addForm.name, email: addForm.email,
      phone: addForm.phone, emergencyPhone: addForm.emergencyPhone,
      loginPassword: addForm.loginPassword,
      type: addForm.type, rating: parseFloat(addForm.rating)||0,
      city: locSelected.city || locSelected.state || locSelected.country,
      address: locSelected.display,
      latitude: locSelected.lat, longitude: locSelected.lng,
      departments: selectedDepts,
      facilities: selectedFacs,
      equipment: selectedFacs,
      totalBeds: 0,
      icuTotal: 0,
    });
    if (r.error) { toast_(r.error, true); setLoading(false); return; }
    toast_('✅ Hospital created successfully!');
    setCreatedCreds({
      name: addForm.name, email: addForm.email,
      loginPassword: r.data?.loginPassword || addForm.loginPassword || '(Auto-generated — check Lambda logs)',
      hospitalId: r.data?.hospitalId,
    });
    setShowAdd(false);
    resetCreateForm();
    fetchAll();
    setLoading(false);
  };

  // ── Settlement ────────────────────────────────────────────────
  const recordSettlement = async(hospitalId) => {
    if(!settlement.amount){ toast_('Enter settlement amount',true); return; }
    if(!settlement.txnRef?.trim()){ toast_('UTR / transaction reference is required',true); return; }
    const r = await adminAPI.recordSettlement(hospitalId, Number(settlement.amount), settlement.note, settlement.txnRef.trim());
    if(r.data){
      toast_(`${fmtR(settlement.amount)} settled ✅ — ledger row created`);
      setSettlement({amount:'',note:'',txnRef:''});
      // Reload hospital detail (totals) AND the global settlement list so
      // the new ledger row appears in both UIs without a manual refresh.
      loadDetail(selectedH);
      setSettLoaded(false);
    }
    else toast_(r.error||'Failed',true);
  };

  // ── Manual Friday auto-payout trigger (for admin Settlements page) ────
  const runFridayAutoPayout = async({ dryRun = false } = {}) => {
    const verb = dryRun ? 'Preview' : 'Run';
    if (!dryRun && !window.confirm(
      'Trigger the WEEKLY Friday auto-payout NOW?\n\n' +
      'This will create pending settlement requests for every hospital with ' +
      'a verified bank account and an available balance ≥ ₹100. The job is ' +
      'idempotent for the current ISO week — re-running it is safe.'
    )) return;
    const r = await adminAPI.runAutoSettlement({ dryRun });
    if (r.data) {
      const { created = [], skipped = [], weekKey } = r.data;
      const total = created.reduce((s,x)=>s+Number(x.amount||0),0);
      toast_(`${verb} · Week ${weekKey} · ${created.length} created (₹${total.toLocaleString('en-IN')}), ${skipped.length} skipped`);
      setSettLoaded(false); // force reload of the global Settlements list
    } else {
      toast_(r.error || 'Auto-payout failed', true);
    }
  };

  const activeEms = emergencies.filter(e=>e.status==='new').length;

  // ══════════════════════════════════════════════════════════════
  // HOSPITAL DETAIL COMPONENT
  // ══════════════════════════════════════════════════════════════
  const renderHospitalDetail = () => {
    if(loadingH) return (
      <div style={{textAlign:'center',padding:80,color:'var(--text-muted)',fontSize:16}}>
        ⏳ Loading hospital data...
      </div>
    );
    if(hError) return (
      <div style={{textAlign:'center',padding:60,color:'#f87171'}}>
        <div style={{fontSize:32,marginBottom:12}}>⚠️</div>
        <div style={{fontWeight:700,marginBottom:8}}>Error loading hospital</div>
        <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:20}}>{hError}</div>
        <button onClick={()=>{setHError(null);loadDetail(selectedH);}} style={{padding:'8px 20px',background:'rgba(245,158,11,0.2)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:8,color:'#fbbf24',cursor:'pointer',fontFamily:'inherit'}}>Retry</button>
      </div>
    );

    // Safe data access — always use toArr() for any DynamoDB Set fields
    const h = hDetail?.hospital || selectedH || {};
    const myDoc = toArr(hDetail?.doctors).length > 0 ? toArr(hDetail.doctors) : doctors.filter(d=>d.hospitalId===h.hospitalId);
    const myBook = toArr(hDetail?.bookings).length > 0 ? toArr(hDetail.bookings) : bookings.filter(b=>b.hospitalId===h.hospitalId);
    const myEms  = toArr(hDetail?.emergencies).length > 0 ? toArr(hDetail.emergencies) : emergencies.filter(e=>e.hospitalId===h.hospitalId);
    const st = hDetail?.stats || {};
    const myPosts = toArr(h.postImages || hDetail?.hospital?.postImages);
    const HTABS = ['overview','activity','facilities','doctors','bookings','revenue','settlement','emergencies','posts','status','iplink'];

    return (
      <>
      <div>
        {/* Header */}
        <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:18,flexWrap:'wrap'}}>
          <button onClick={()=>{setSelectedH(null);setHDetail(null);setHError(null);}} style={{padding:'8px 14px',background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:9,color:'#94a3b8',cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontSize:13,fontFamily:'inherit'}}>
            <MdArrowBack/> Back
          </button>
          <div style={{flex:1}}>
            <h1 style={{fontSize:21,fontWeight:800,margin:0}}>{h.name||'Hospital'}</h1>
            <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>{h.city} · {h.email} · ID: {h.hospitalId}</div>
          </div>
          <Badge s={h.status||'active'}/>
          <button onClick={()=>toggleH(h)} style={{padding:'7px 11px',background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:8,color:'#818cf8',cursor:'pointer',fontSize:18,display:'flex',alignItems:'center'}}>
            {h.status==='inactive'?<MdToggleOff/>:<MdToggleOn/>}
          </button>
          <button title="Re-invite owner (Cognito will email a fresh temp password)"
            onClick={async()=>{ const r=await adminAPI.reinviteOwner(h.hospitalId); if(r.error){toast_(`❌ ${r.error}`,true);return;} const p=r.data?.tempPassword; toast_(p?`📨 MSG91 credentials sent to ${r.data?.email||h.email} · Backup: ${p}`:`📨 Credentials re-sent to ${r.data?.email||h.email}`); }}
            style={{padding:'7px 11px',background:'rgba(0,212,170,0.1)',border:'1px solid rgba(0,212,170,0.25)',borderRadius:8,color:'#00d4aa',cursor:'pointer',fontSize:18,display:'flex',alignItems:'center'}}>
            <MdMarkEmailRead/>
          </button>
          <button title="Reset password (legacy bcrypt fallback)"
            onClick={async()=>{ const r=await adminAPI.resetPassword(h.hospitalId); if(r.error){toast_(`❌ ${r.error}`,true);return;} const p=r.data?.newPassword; toast_(p?`🔑 Password reset · MSG91 emailed · Backup: ${p}`:'🔑 Password reset & emailed via MSG91'); }} style={{padding:'7px 11px',background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:8,color:'#fbbf24',cursor:'pointer',fontSize:18,display:'flex',alignItems:'center'}}>
            <MdLockReset/>
          </button>
          <button onClick={()=>setConfirmDel({type:'hospital',item:h})} style={{padding:'7px 11px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,color:'#f87171',cursor:'pointer',fontSize:18,display:'flex',alignItems:'center'}}>
            <MdDelete/>
          </button>
        </div>

        {/* Sub-navigation */}
        <div style={{display:'flex',gap:5,marginBottom:18,overflowX:'auto',WebkitOverflowScrolling:'touch',paddingBottom:4}}>
          {HTABS.map(t=>(
            <button key={t} onClick={()=>{
              setHTab(t);
              if(t==='activity'&&!hActivity&&!hActLoading){
                setHActLoading(true);
                adminAPI.getHospitalActivity(h.hospitalId).then(r=>{
                  if(r.data)setHActivity(r.data);
                  setHActLoading(false);
                }).catch(()=>setHActLoading(false));
              }
            }} style={{padding:'7px 14px',borderRadius:8,border:'none',cursor:'pointer',background:hTab===t?t==='activity'?'rgba(99,102,241,0.2)':'rgba(245,158,11,0.2)':'var(--surface-subtle)',color:hTab===t?t==='activity'?'#818cf8':'#fbbf24':'var(--text-muted)',fontWeight:hTab===t?700:400,fontSize:13,fontFamily:'inherit',textTransform:'capitalize',transition:'all 0.15s',whiteSpace:'nowrap',flexShrink:0}}>
              {t==='settlement'&&!isBankVerified(h)&&h.accountNumber?'⚠️ ':''}{t==='activity'?'📊 Activity':t==='iplink'?'🔗 Link with IP':t}
            </button>
          ))}
        </div>

        {/* ACTIVITY DRILLDOWN */}
        {hTab==='activity'&&(
          <div>
            {hActLoading&&<div style={{textAlign:'center',padding:60,color:'var(--text-muted)'}}>⏳ Loading hospital activity…</div>}
            {!hActLoading&&!hActivity&&<div style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}>No activity data. Click the tab to load.</div>}
            {hActivity&&(()=>{
              const a = hActivity;
              return (
                <div>
                  {/* Stats row */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(145px,1fr))',gap:10,marginBottom:14}}>
                    {[
                      {icon:'📋',label:'Total Bookings',val:a.stats.totalBookings,c:'#6366f1'},
                      {icon:'✅',label:'Confirmed',val:a.stats.confirmedBookings,c:'#10b981'},
                      {icon:'❌',label:'Cancelled',val:a.stats.cancelledBookings,c:'#ef4444'},
                      {icon:'💰',label:'Revenue',val:fmtR(a.stats.totalRevenue),c:'#fbbf24'},
                      {icon:'👥',label:'Unique Patients',val:a.stats.uniquePatients,c:'#8b5cf6'},
                      {icon:'🚨',label:'Emergencies',val:a.stats.totalEmergencies,c:'#f87171'},
                    ].map(s=>(
                      <div key={s.label} style={{background:`${s.c}14`,border:`1px solid ${s.c}33`,borderRadius:12,padding:'12px 14px'}}>
                        <div style={{fontSize:16}}>{s.icon}</div>
                        <div style={{fontSize:18,fontWeight:800,color:s.c,marginTop:4}}>{s.val}</div>
                        <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>{s.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* 30-day chart */}
                  <div style={{...S,marginBottom:12}}>
                    <div style={{...H2,marginBottom:10}}>📈 30-Day Booking & Revenue Trend</div>
                    <MiniBar data={a.daily30days.map(d=>d.bookings)} color="#6366f1" height={60} labels={a.daily30days.map(d=>`${d.label}: ${d.bookings} bookings · ${fmtR(d.revenue)}`)} />
                    <div style={{display:'flex',justifyContent:'space-between',marginTop:4}}>
                      {a.daily30days.filter((_,i)=>i%5===0).map((d,i)=>(
                        <div key={i} style={{fontSize:9,color:'var(--border-color-hover)'}}>{d.label}</div>
                      ))}
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginTop:12}}>
                      <div><div style={{fontSize:16,fontWeight:800,color:'#6366f1'}}>{a.daily30days.reduce((s,d)=>s+d.bookings,0)}</div><div style={{fontSize:10,color:'var(--text-muted)'}}>30-Day Total</div></div>
                      <div><div style={{fontSize:16,fontWeight:800,color:'#10b981'}}>{a.daily30days.reduce((s,d)=>s+d.confirmed,0)}</div><div style={{fontSize:10,color:'var(--text-muted)'}}>Confirmed</div></div>
                      <div><div style={{fontSize:16,fontWeight:800,color:'#fbbf24'}}>{fmtR(a.daily30days.reduce((s,d)=>s+d.revenue,0))}</div><div style={{fontSize:10,color:'var(--text-muted)'}}>Revenue</div></div>
                    </div>
                  </div>

                  {/* Top doctors + Top patients */}
                  <div className="resp-grid-2col-equal" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
                    <div style={S}>
                      <div style={H2}>👨‍⚕️ Top Doctors</div>
                      {a.topDoctors.map((d,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:9,padding:'7px 0',borderBottom:'1px solid var(--surface-subtle)'}}>
                          <div style={{width:22,height:22,borderRadius:6,background:'rgba(0,212,170,0.12)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:'#00d4aa'}}>{i+1}</div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:600}}>{d.name}</div>
                            <div style={{fontSize:10,color:'var(--text-muted)'}}>{d.count} bookings</div>
                          </div>
                          <div style={{textAlign:'right'}}>
                            <div style={{fontSize:13,fontWeight:800,color:'#fbbf24'}}>{fmtR(d.revenue)}</div>
                          </div>
                        </div>
                      ))}
                      {!a.topDoctors.length&&<div style={{fontSize:12,color:'var(--border-color-hover)'}}>No doctor bookings yet</div>}
                    </div>
                    <div style={S}>
                      <div style={H2}>👥 Top Patients</div>
                      {a.topPatients.map((p,i)=>(
                        <div key={i} onClick={()=>{setTab('intelligence');setIntelQ(p.phone);setTimeout(()=>intelLookup(p.phone),100);}} style={{display:'flex',alignItems:'center',gap:9,padding:'7px 0',borderBottom:'1px solid var(--surface-subtle)',cursor:'pointer'}}>
                          <div style={{width:22,height:22,borderRadius:6,background:'rgba(99,102,241,0.12)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:'#818cf8'}}>{i+1}</div>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                            <div style={{fontSize:10,color:'var(--text-muted)'}}>{p.phone} · {p.count} visits · {timeAgo(p.lastSeen)}</div>
                          </div>
                          <div style={{textAlign:'right'}}>
                            <div style={{fontSize:13,fontWeight:700,color:'#10b981'}}>{fmtR(p.spent)}</div>
                            <MdInsights style={{fontSize:11,color:'rgba(99,102,241,0.5)',marginTop:2}}/>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recent events timeline */}
                  <div style={S}>
                    <div style={H2}>
                      <span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:'#10b981',boxShadow:'0 0 6px #10b981',marginRight:6}}/>
                      Recent Activity Timeline
                      <span style={{marginLeft:'auto',fontSize:11,fontWeight:400,color:'var(--text-muted)'}}>{a.events.length} events</span>
                    </div>
                    {a.events.slice(0,25).map((ev,i)=>(
                      <div key={i} style={{display:'flex',gap:10,padding:'7px 0',borderBottom:'1px solid var(--surface-subtle)',alignItems:'flex-start'}}>
                        <div style={{width:26,height:26,borderRadius:8,background:`${ev.color}22`,border:`1px solid ${ev.color}44`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,flexShrink:0}}>{ev.icon}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                            <span style={{fontWeight:600,fontSize:13}}>{ev.title}</span>
                            <Badge s={ev.status}/>
                            {ev.fee>0&&<span style={{fontSize:11,color:'#fbbf24',fontWeight:700}}>{fmtR(ev.fee)}</span>}
                          </div>
                          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ev.description}</div>
                          <div style={{fontSize:10,color:'var(--text-muted)',marginTop:1,display:'flex',gap:10}}>
                            <span>{ev.phone}</span>
                            <span><MdAccessTime style={{fontSize:10,verticalAlign:'middle'}}/> {timeAgo(ev.timestamp)}</span>
                          </div>
                        </div>
                        {ev.type==='booking'&&ev.phone&&ev.phone!=='—'&&<button onClick={()=>{setTab('intelligence');setIntelQ(ev.phone);setTimeout(()=>intelLookup(ev.phone),100);}} style={{background:'none',border:'none',color:'rgba(99,102,241,0.5)',cursor:'pointer',fontSize:14,padding:'2px 4px',flexShrink:0}}><MdInsights/></button>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* OVERVIEW */}
        {hTab==='overview'&&(
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:11,marginBottom:14}}>
              <StatCard icon="📋" label="Total Bookings"  value={fmt(st.totalBookings||myBook.length)} color="#6366f1" sub={`Today: ${st.todayBookings||0}`}/>
              <StatCard icon="✅" label="Confirmed"       value={fmt(st.confirmedBookings||0)} color="#10b981"/>
              <StatCard icon="👨‍⚕️" label="Doctors"     value={fmt(st.totalDoctors||myDoc.length)} color="#00d4aa" sub={`${st.availableDoctors||0} available`}/>
              <StatCard icon="💰" label="Revenue"        value={fmtR(st.totalRevenue||0)} color="#fbbf24" sub={`Today: ${fmtR(st.todayRevenue||0)}`}/>
              <StatCard icon="🚨" label="Emergencies"    value={myEms.filter(e=>e.status==='new').length} color="#ef4444"/>
            </div>
            <div className="resp-grid-2col-equal" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div style={S}>
                <div style={H2}>🏥 Hospital Information</div>
                <EditField label="Hospital Name"   value={h.name}          onSave={v=>saveField(h.hospitalId,'name',v)}/>
                <EditField label="Email"           value={h.email}         onSave={v=>saveField(h.hospitalId,'email',v)} type="email"/>
                <EditField label="City"            value={h.city}          onSave={v=>saveField(h.hospitalId,'city',v)}/>
                <EditField label="Main Phone"      value={h.phone}         onSave={v=>saveField(h.hospitalId,'phone',v)} type="tel"/>
                <EditField label="Emergency Phone" value={h.emergencyPhone} onSave={v=>saveField(h.hospitalId,'emergencyPhone',v)} type="tel"/>

                {/* ── Map Location (interactive picker) ── */}
                <div style={{marginTop:14,padding:'12px 14px',background:'rgba(239,68,68,0.05)',border:'1px solid rgba(239,68,68,0.15)',borderRadius:10}}>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
                    <MdLocationOn style={{color:'#ef4444',fontSize:14}}/> Map Location (Patient App)
                  </div>
                  <LocationMapPicker
                    value={(h.latitude && h.longitude && Number(h.latitude)!==0) ? {
                      lat: Number(h.latitude),
                      lng: Number(h.longitude),
                      address: h.address || '',
                      city: h.city || '',
                      district: h.district || '',
                      state: h.state || '',
                    } : null}
                    onChange={(loc) => {
                      if (!loc) return;
                      saveHospitalLocation(h.hospitalId, {
                        lat: loc.lat,
                        lng: loc.lng,
                        display: loc.address || loc.display || '',
                        short:   (loc.address || loc.display || '').split(',').slice(0,3).join(', '),
                        city: loc.city || '',
                        state: loc.state || '',
                      });
                    }}
                    height={280}
                    compact={false}
                  />
                </div>
              </div>
              <div style={S}>
                <div style={H2}>🏷️ Departments & Settings</div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6}}>Departments</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:14}}>
                  {toArr(h.departments).map(d=>(
                    <span key={d} style={{padding:'3px 9px',background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:20,fontSize:12,color:'#818cf8'}}>{d}</span>
                  ))}
                  {!toArr(h.departments).length&&<span style={{fontSize:12,color:'var(--border-color-hover)'}}>None — add via Facilities tab</span>}
                </div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:5}}>Equipment ({toArr(h.equipment).length})</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:12}}>
                  {toArr(h.equipment).map(e=>(<span key={e} style={{padding:'3px 9px',background:'rgba(0,212,170,0.1)',border:'1px solid rgba(0,212,170,0.2)',borderRadius:20,fontSize:12,color:'#34d399'}}>{e}</span>))}
                  {!toArr(h.equipment).length&&<span style={{fontSize:12,color:'var(--border-color-hover)'}}>None — add via Facilities tab</span>}
                </div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:5}}>Facilities ({toArr(h.facilities).length})</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:12}}>
                  {toArr(h.facilities).map(f=>(<span key={f} style={{padding:'3px 9px',background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:20,fontSize:12,color:'#fbbf24'}}>{f}</span>))}
                  {!toArr(h.facilities).length&&<span style={{fontSize:12,color:'var(--border-color-hover)'}}>None — add via Facilities tab</span>}
                </div>
                <div style={{fontSize:11,color:'var(--border-color-hover)',marginBottom:3}}>Cognito: {h.cognitoUsername||'—'}</div>
                <div style={{fontSize:11,color:'var(--border-color-hover)',marginBottom:12}}>Created: {fmtD(h.createdAt)} · 24x7: {h.is24x7?'Yes':'No'}</div>

                {/* Commission Info */}
                {(()=>{
                  const c = hDetail?.commission;
                  if(!c) return null;
                  return (
                    <div style={{padding:'12px 14px',background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.18)',borderRadius:10,marginBottom:12}}>
                      <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:8}}>💰 Commission</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,fontSize:12}}>
                        <span style={{color:'var(--text-muted)'}}>Rate:</span>
                        <span style={{fontWeight:700,color:c.isWaived?'#34d399':'#fbbf24'}}>{c.isWaived?'0% (Waived)':c.commissionPct+'%'}</span>
                        <span style={{color:'var(--text-muted)'}}>GST:</span>
                        <span style={{fontWeight:700,color:c.gstExempt?'#34d399':'#818cf8'}}>{c.gstExempt?'Exempt':c.gstPct+'%'}</span>
                        <span style={{color:'var(--text-muted)'}}>Platform Fee:</span>
                        <span style={{color:'#818cf8'}}>{fmtR(c.platformFee||0)}</span>
                        <span style={{color:'var(--text-muted)'}}>GST on Fee:</span>
                        <span style={{color:'#a78bfa'}}>{fmtR(c.gstOnFee||0)}</span>
                        <span style={{color:'var(--text-muted)'}}>Net Payable:</span>
                        <span style={{fontWeight:700,color:'#10b981'}}>{fmtR(c.netPayable||0)}</span>
                      </div>
                      <button type="button" onClick={() => setShowCommissionEdit(true)} style={{marginTop:8,padding:'4px 10px',background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:6,color:'#fbbf24',cursor:'pointer',fontSize:11,fontWeight:600,fontFamily:'inherit'}}>
                        <MdSettings style={{fontSize:12}}/> Edit Commission
                      </button>
                    </div>
                  );
                })()}
                <div style={{padding:'10px 12px',background:isBankVerified(h)?'rgba(16,185,129,0.08)':'rgba(245,158,11,0.06)',border:`1px solid ${isBankVerified(h)?'rgba(16,185,129,0.2)':'rgba(245,158,11,0.2)'}`,borderRadius:8,fontSize:12}}>
                  {isBankVerified(h)
                    ? <span style={{color:'#34d399',display:'flex',alignItems:'center',gap:6}}><MdVerified/> Bank Account Verified</span>
                    : h.accountNumber
                      ? <span style={{color:'#f59e0b',display:'flex',alignItems:'center',gap:6}}><MdShield/> Bank details submitted — pending verification
                          <button onClick={()=>verifyBank(h)} style={{marginLeft:'auto',padding:'4px 10px',background:'rgba(16,185,129,0.2)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:6,color:'#34d399',cursor:'pointer',fontSize:11,fontWeight:700}}>Verify ✅</button>
                        </span>
                      : <span style={{color:'var(--text-muted)'}}>No bank details added</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FACILITIES & EQUIPMENT */}
        {hTab==='facilities'&&(()=>{
          return <FacilitiesTab h={h} adminAPI={adminAPI} toast_={toast_} setHDetail={setHDetail} setHospitals={setHospitals}/>;
        })()}

        {hTab==='doctors'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <div>
                <div style={{fontSize:20,fontWeight:800}}>Doctors</div>
                <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>{myDoc.length} doctors · {myDoc.filter(isDoctorAvailable).length} available</div>
              </div>
              <button onClick={()=>setShowAddDoc(true)} style={{padding:'8px 16px',background:'linear-gradient(135deg,#00d4aa,#059669)',border:'none',borderRadius:9,color:'#000',fontWeight:700,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontFamily:'inherit'}}>
                <MdAdd/> Add Doctor
              </button>
            </div>
            {!myDoc.length ? (
              <div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)'}}>No doctors yet — click "Add Doctor" to create one</div>
            ) : (
              <div style={{display:'grid',gap:9}}>
                {myDoc.map(d=>(
                  <div key={d.doctorId} style={{background:'var(--surface-subtle)',border:'1px solid var(--surface-muted)',borderRadius:12,padding:'13px 17px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                    <div style={{width:40,height:40,background:'rgba(0,212,170,0.1)',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>👨‍⚕️</div>
                    <div style={{flex:1,minWidth:140}}>
                      <div style={{fontWeight:700,fontSize:14}}>{d.name}</div>
                      <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>
                        {d.specialization}{d.experience&&` · ${d.experience}`}
                        {d.phone&&<> · {d.phone}</>}
                      </div>
                      <div style={{fontSize:12,color:'#fbbf24',fontWeight:600,marginTop:1}}>Consultation: {fmtR(d.consultationFee||d.fee||0)}</div>
                    </div>
                    <Badge s={isDoctorAvailable(d)?'available':'unavailable'}/>
                    <div style={{display:'flex',gap:5}}>
                      <button onClick={()=>toggleDoctor(d)} style={{padding:'6px 9px',background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:7,color:'#818cf8',cursor:'pointer',fontSize:18,display:'flex',alignItems:'center'}}>
                        {isDoctorAvailable(d)?<MdToggleOn/>:<MdToggleOff/>}
                      </button>
                      <button onClick={()=>setConfirmDel({type:'doctor',item:d})} style={{padding:'6px 9px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:7,color:'#f87171',cursor:'pointer',fontSize:18,display:'flex',alignItems:'center'}}>
                        <MdDelete/>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BOOKINGS */}
        {hTab==='bookings'&&(
          <div>
            <div style={{fontSize:20,fontWeight:800,marginBottom:14}}>Bookings ({myBook.length})</div>
            <div style={S}>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead><tr>{['Patient','Phone','Doctor','Date','Time','Fee','Status',''].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                  <tbody>
                    {!myBook.length?<tr><td colSpan={8} style={{...TD,textAlign:'center',color:'var(--text-muted)',padding:'30px 0'}}>No bookings</td></tr>:
                    myBook.map(b=>(
                      <tr key={b.bookingId} style={{transition:'background 0.1s'}}>
                        <td style={TD}>{b.patientName||'—'}</td>
                        <td style={TD}>{b.patientPhone||'—'}</td>
                        <td style={TD}>{b.doctorName||'—'}</td>
                        <td style={TD}>{fmtD(b.date||b.appointmentDate)}</td>
                        <td style={TD}>{b.slot||b.appointmentTime||'—'}</td>
                        <td style={TD}><span style={{color:'#fbbf24',fontWeight:700}}>{fmtR(b.consultationFee||b.fee||0)}</span></td>
                        <td style={TD}><Badge s={b.status}/></td>
                        <td style={TD}><button onClick={()=>setConfirmDel({type:'booking',item:b})} style={{padding:'4px 7px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:6,color:'#f87171',cursor:'pointer',display:'flex',alignItems:'center'}}><MdDelete/></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* REVENUE */}
        {hTab==='revenue'&&(
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:11,marginBottom:14}}>
              <StatCard icon="💰" label="Total Revenue"    value={fmtR(st.totalRevenue||0)} color="#fbbf24"/>
              <StatCard icon="📅" label="Today's Revenue"  value={fmtR(st.todayRevenue||0)} color="#10b981"/>
              <StatCard icon="✅" label="Paid Bookings"    value={fmt(st.confirmedBookings||0)} color="#6366f1"/>
              <StatCard icon="⏳" label="Available to Settle" value={fmtR(hDetail?.settlementLedger?.availableBalance ?? h.pendingSettlement ?? 0)} color="#f59e0b"/>
              <StatCard icon="🏦" label="Total Settled"    value={fmtR(hDetail?.settlementLedger?.totalSettled ?? h.totalSettled ?? 0)} color="#00d4aa"/>
            </div>
            <div style={S}>
              <div style={H2}>💳 Confirmed Payments</div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead><tr>{['Patient','Doctor','Date','Amount'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                  <tbody>
                    {myBook.filter(b=>['confirmed','accepted','checked_in','completed'].includes(b.status)).map(b=>(
                      <tr key={b.bookingId}>
                        <td style={TD}>{b.patientName||'—'}</td>
                        <td style={TD}>{b.doctorName||'—'}</td>
                        <td style={TD}>{fmtD(b.date)}</td>
                        <td style={TD}><span style={{color:'#fbbf24',fontWeight:700}}>{fmtR(b.consultationFee||b.fee||0)}</span></td>
                      </tr>
                    ))}
                    {!myBook.filter(b=>['confirmed','accepted','checked_in','completed'].includes(b.status)).length&&
                      <tr><td colSpan={4} style={{...TD,textAlign:'center',color:'var(--text-muted)',padding:'25px 0'}}>No confirmed payments</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* SETTLEMENT */}
        {hTab==='settlement'&&(() => {
          const ledger = hDetail?.settlementLedger || {};
          const earned = hDetail?.earnedCommission || hDetail?.commission || {};
          const avail = ledger.availableBalance ?? h.pendingSettlement ?? 0;
          const settled = ledger.totalSettled ?? h.totalSettled ?? 0;
          const inProg = ledger.inProgress ?? 0;
          return (
          <div>
            {/* Live payout breakdown — matches hospital Settlements page exactly */}
            <div style={{...S,marginBottom:12}}>
              <div style={H2}>📊 Settlement Ledger · Live</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:10}}>
                <div style={{padding:'10px 12px',background:'rgba(251,191,36,0.06)',border:'1px solid rgba(251,191,36,0.15)',borderRadius:9}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase'}}>Checked-In Revenue</div>
                  <div style={{fontSize:18,fontWeight:800,color:'#fbbf24'}}>{fmtR(ledger.checkedInRevenue ?? earned.revenue ?? 0)}</div>
                </div>
                <div style={{padding:'10px 12px',background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.15)',borderRadius:9}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase'}}>Platform Fee + GST</div>
                  <div style={{fontSize:18,fontWeight:800,color:'#818cf8'}}>{fmtR(ledger.totalDeduction ?? earned.totalDeduction ?? 0)}</div>
                </div>
                <div style={{padding:'10px 12px',background:'rgba(16,185,129,0.06)',border:'1px solid rgba(16,185,129,0.15)',borderRadius:9}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase'}}>Net Payable</div>
                  <div style={{fontSize:18,fontWeight:800,color:'#10b981'}}>{fmtR(ledger.netPayable ?? earned.netPayable ?? 0)}</div>
                </div>
                <div style={{padding:'10px 12px',background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.15)',borderRadius:9}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase'}}>In Progress</div>
                  <div style={{fontSize:18,fontWeight:800,color:'#f59e0b'}}>{fmtR(inProg)}</div>
                </div>
                <div style={{padding:'10px 12px',background:'rgba(0,212,170,0.06)',border:'1px solid rgba(0,212,170,0.15)',borderRadius:9}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase'}}>Available Now</div>
                  <div style={{fontSize:20,fontWeight:800,color:'#00d4aa'}}>{fmtR(avail)}</div>
                </div>
                <div style={{padding:'10px 12px',background:'rgba(16,185,129,0.06)',border:'1px solid rgba(16,185,129,0.15)',borderRadius:9}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase'}}>Total Disbursed</div>
                  <div style={{fontSize:18,fontWeight:800,color:'#34d399'}}>{fmtR(settled)}</div>
                </div>
              </div>
              <div style={{fontSize:12,color:'var(--text-muted)',lineHeight:1.6}}>
                Formula: <strong style={{color:'#fbbf24'}}>{fmtR(ledger.checkedInRevenue ?? 0)}</strong> revenue − <strong style={{color:'#818cf8'}}>{fmtR(ledger.totalDeduction ?? 0)}</strong> fees − <strong style={{color:'#f59e0b'}}>{fmtR(settled)}</strong> paid − <strong style={{color:'#f59e0b'}}>{fmtR(inProg)}</strong> in-flight = <strong style={{color:'#00d4aa'}}>{fmtR(avail)}</strong> available
                {ledger.ledgerReconciled === false && (
                  <span style={{display:'block',marginTop:6,color:'#f87171',fontWeight:600}}>
                    ⚠ Ledger mismatch: stored totalSettled ({fmtR(ledger.storedTotalSettled)}) ≠ ledger sum ({fmtR(settled)}). Ledger is authoritative.
                  </span>
                )}
              </div>
            </div>
          <div className="resp-grid-2col-equal" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            {/* Bank Details */}
            <div style={S}>
              <div style={H2}><MdAccountBalance/> Bank Account Details</div>
              {isBankVerified(h)?
                <div style={{padding:'8px 12px',background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.2)',borderRadius:8,color:'#34d399',fontSize:13,marginBottom:12,display:'flex',alignItems:'center',gap:6}}>
                  <MdVerified/> Bank Account Verified
                </div>
              : h.accountNumber ?
                <div style={{padding:'8px 12px',background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:8,fontSize:13,marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                  <span style={{color:'#f59e0b'}}>⚠️ Pending Verification</span>
                  <button onClick={()=>verifyBank(h)} style={{padding:'5px 12px',background:'rgba(16,185,129,0.2)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:6,color:'#34d399',cursor:'pointer',fontSize:12,fontWeight:700}}>✅ Verify</button>
                </div>
              : <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:12}}>Hospital has not submitted bank details yet</div>}
              <EditField label="Account Holder" value={h.accountHolder} onSave={v=>saveField(h.hospitalId,'accountHolder',v)}/>
              <EditField label="Bank Name"      value={h.bankName}      onSave={v=>saveField(h.hospitalId,'bankName',v)}/>
              <EditField label="Account Number" value={h.accountNumber} onSave={v=>saveField(h.hospitalId,'accountNumber',v)}/>
              <EditField label="IFSC Code"      value={h.ifscCode}      onSave={v=>saveField(h.hospitalId,'ifscCode',v)}/>
              <EditField label="Bank Branch"    value={h.bankBranch}    onSave={v=>saveField(h.hospitalId,'bankBranch',v)}/>
            </div>
            {/* Settlement Record */}
            <div>
              <div style={S}>
                <div style={H2}><MdLocalAtm/> Settlement Summary</div>
                <div style={{display:'grid',gap:12,marginBottom:16}}>
                  <div style={{padding:'12px 14px',background:'rgba(0,212,170,0.06)',border:'1px solid rgba(0,212,170,0.15)',borderRadius:9}}>
                    <div style={{fontSize:11,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:3}}>Available to Settle</div>
                    <div style={{fontSize:24,fontWeight:800,color:'#00d4aa'}}>{fmtR(avail)}</div>
                  </div>
                  <div style={{padding:'12px 14px',background:'rgba(16,185,129,0.06)',border:'1px solid rgba(16,185,129,0.15)',borderRadius:9}}>
                    <div style={{fontSize:11,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:3}}>Total Disbursed</div>
                    <div style={{fontSize:20,fontWeight:700,color:'#10b981'}}>{fmtR(settled)}</div>
                  </div>
                  {h.lastSettlementDate&&<div style={{fontSize:12,color:'var(--text-muted)'}}>Last: {fmtD(h.lastSettlementDate)} — {fmtR(h.lastSettlementAmount||0)}{h.lastSettlementTxn?` · UTR ${h.lastSettlementTxn}`:''}</div>}
                </div>
              </div>
              <div style={S}>
                <div style={H2}>💳 Record Settlement</div>
                {!isBankVerified(h)&&<div style={{padding:'8px 12px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,color:'#f87171',fontSize:12,marginBottom:12}}>⚠️ Verify bank account before settling</div>}
                <Inp label="Amount (₹)" value={settlement.amount} onChange={e=>setSettlement(p=>({...p,amount:e.target.value}))} type="number" placeholder={`Max ${avail}`} required/>
                <Inp label="UTR / Transaction Ref (required)" value={settlement.txnRef} onChange={e=>setSettlement(p=>({...p,txnRef:e.target.value}))} placeholder="Bank UTR / NEFT reference" required/>
                <Inp label="Note (optional)" value={settlement.note} onChange={e=>setSettlement(p=>({...p,note:e.target.value}))} placeholder="Internal note"/>
                <button onClick={()=>recordSettlement(h.hospitalId)} disabled={!isBankVerified(h)||!settlement.amount||!settlement.txnRef?.trim()}
                  style={{width:'100%',padding:10,background:isBankVerified(h)&&settlement.amount&&settlement.txnRef?.trim()?'linear-gradient(135deg,#10b981,#059669)':'var(--surface-muted)',border:'none',borderRadius:9,color:isBankVerified(h)&&settlement.amount&&settlement.txnRef?.trim()?'#fff':'var(--text-muted)',fontWeight:700,cursor:isBankVerified(h)&&settlement.amount&&settlement.txnRef?.trim()?'pointer':'not-allowed',fontFamily:'inherit',fontSize:14}}>
                  💳 Record Settlement Transfer
                </button>
                <div style={{marginTop:8,fontSize:11,color:'var(--text-muted)',lineHeight:1.5}}>
                  Recording here writes an audit row to <code>HCEN_SettlementRequests</code> tagged <code>admin_manual</code>. It will appear in this hospital's Settlements page and in the global Settlements list immediately.
                </div>
              </div>

              {/* ── Per-hospital Settlement History (Phase A fix) ─────
                  Pulls from the unified ledger and shows everything ever
                  paid to this hospital regardless of source: hospital
                  raised, auto-Friday, or admin-recorded. */}
              {(() => {
                const myRows = (settReqs || []).filter(r => r.hospitalId === h.hospitalId);
                const sourceLabel = (s) => s === 'auto_friday' ? '🗓️ Friday Auto' : s === 'admin_manual' ? '🛠️ Admin Manual' : '🙋 Hospital Raised';
                const statusPill = (st) => {
                  const map = {
                    pending:    { bg:'rgba(245,158,11,0.12)', c:'#fbbf24', t:'⏳ Pending' },
                    processing: { bg:'rgba(99,102,241,0.12)', c:'#818cf8', t:'🔄 Processing' },
                    disbursed:  { bg:'rgba(16,185,129,0.12)', c:'#34d399', t:'✅ Disbursed' },
                    rejected:   { bg:'rgba(239,68,68,0.12)',  c:'#f87171', t:'❌ Rejected' },
                  }[st] || { bg:'var(--surface-subtle)', c:'#94a3b8', t: st };
                  return <span style={{padding:'2px 9px',borderRadius:14,fontSize:10,fontWeight:700,background:map.bg,color:map.c,whiteSpace:'nowrap'}}>{map.t}</span>;
                };
                return (
                  <div style={{...S,marginTop:12}}>
                    <div style={{...H2,marginBottom:10}}>📜 Settlement History · {myRows.length}</div>
                    {settLoading && <div style={{fontSize:12,color:'var(--text-muted)'}}>⏳ Loading…</div>}
                    {!settLoading && myRows.length === 0 && (
                      <div style={{padding:'18px 14px',textAlign:'center',background:'var(--surface-subtle)',border:'1px dashed var(--border-color)',borderRadius:8,color:'var(--text-muted)',fontSize:12}}>
                        No settlements recorded yet. Use the form above to record the first transfer.
                      </div>
                    )}
                    {!settLoading && myRows.length > 0 && (
                      <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:340,overflowY:'auto'}}>
                        {myRows.map(r => (
                          <div key={r.requestId} style={{padding:'10px 12px',background:'var(--surface-subtle)',border:'1px solid var(--surface-muted)',borderRadius:9}}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8,flexWrap:'wrap'}}>
                              <div>
                                <div style={{fontWeight:800,fontSize:15,color:'#fbbf24'}}>{fmtR(r.amount)}</div>
                                <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>{sourceLabel(r.source || 'hospital_request')} · {fmtD(r.createdAt)}</div>
                              </div>
                              {statusPill(r.status)}
                            </div>
                            {(r.transactionRef || r.txnRef) && (
                              <div style={{fontSize:11,color:'#818cf8',fontFamily:'monospace',marginTop:5}}>UTR: {r.transactionRef || r.txnRef}</div>
                            )}
                            {(r.adminNote || r.autoNote) && (
                              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3,fontStyle:'italic'}}>“{r.adminNote || r.autoNote}”</div>
                            )}
                            {r.disbursedAt && r.status === 'disbursed' && (
                              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:3}}>Disbursed {fmtD(r.disbursedAt)}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          </div>
        );})()}

        {/* EMERGENCIES */}
        {hTab==='emergencies'&&(
          <div>
            <div style={{fontSize:20,fontWeight:800,marginBottom:14}}>Emergencies ({myEms.length})</div>
            {!myEms.length ? <div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)'}}>No emergency alerts</div> :
              myEms.map(e=>(
                <div key={e.alertId} style={{background:'var(--surface-subtle)',border:`1px solid ${sc(e.status)}33`,borderRadius:12,padding:'13px 17px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',marginBottom:9}}>
                  <div style={{fontSize:22}}>{e.status==='new'?'🚨':e.status==='acknowledged'?'⚠️':'✅'}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:14}}>{e.patientName||'Unknown'}</div>
                    <div style={{fontSize:12,color:'var(--text-muted)'}}>{e.type||'Emergency'} · {fmtD(e.createdAt)}{e.phone&&` · ${e.phone}`}</div>
                  </div>
                  <Badge s={e.status}/>
                  <div style={{display:'flex',gap:5}}>
                    {e.status==='new'&&<button onClick={()=>updateEm(e,'acknowledged')} style={{padding:'5px 10px',background:'rgba(245,158,11,0.15)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:7,color:'#fbbf24',cursor:'pointer',fontSize:12,fontWeight:600}}>Acknowledge</button>}
                    {e.status!=='handled'&&<button onClick={()=>updateEm(e,'handled')} style={{padding:'5px 10px',background:'rgba(16,185,129,0.15)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:7,color:'#34d399',cursor:'pointer',fontSize:12,fontWeight:600}}>Handled</button>}
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* ── POSTS & PROMOTIONS ───────────────────────────────────── */}
        {hTab==='posts'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
              <div>
                <div style={{fontSize:20,fontWeight:800}}>📸 Posts & Promotions</div>
                <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>Images the hospital has uploaded for patients · {myPosts.length}/5 slots used</div>
              </div>
              {myPosts.length>0&&(
                <button
                  onClick={async()=>{
                    if(!window.confirm('Delete ALL posts for this hospital?'))return;
                    const r = await adminAPI.updateHospital(h.hospitalId, { postImages: [] });
                    if (r.error) { toast_(r.error, true); return; }
                    setHDetail(p=>p?{...p,hospital:{...p.hospital,postImages:[]}}:p);
                    setHospitals(p=>p.map(x=>x.hospitalId===h.hospitalId?{...x,postImages:[]}:x));
                    toast_('🗑️ All posts cleared');
                  }}
                  style={{padding:'7px 14px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.25)',borderRadius:8,color:'#f87171',cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>
                  🗑️ Clear All
                </button>
              )}
            </div>
            {myPosts.length===0?(
              <div style={{textAlign:'center',padding:'50px 20px',background:'var(--surface-subtle)',border:'1px dashed var(--border-color-hover)',borderRadius:14}}>
                <div style={{fontSize:40,marginBottom:10}}>📭</div>
                <div style={{fontWeight:700,fontSize:15,marginBottom:5}}>No posts uploaded</div>
                <div style={{fontSize:13,color:'var(--text-muted)'}}>The hospital has not uploaded any promotional images yet.</div>
              </div>
            ):(
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(230px,1fr))',gap:14}}>
                {myPosts.map((url,i)=>(
                  <div key={i} style={{background:'var(--surface-subtle)',border:'1px solid var(--border-color)',borderRadius:13,overflow:'hidden'}}>
                    <div style={{position:'relative',aspectRatio:'16/9',background:'var(--bg-primary)'}}>
                      <img src={url} alt={`Post ${i+1}`} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}
                        onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}/>
                      <div style={{display:'none',position:'absolute',inset:0,alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.5)',flexDirection:'column',gap:6}}>
                        <span style={{fontSize:28}}>🖼️</span>
                        <span style={{fontSize:11,color:'var(--text-muted)'}}>Image unavailable</span>
                      </div>
                      <div style={{position:'absolute',top:7,left:7,background:'rgba(0,0,0,0.65)',color:'#fff',borderRadius:6,padding:'2px 8px',fontSize:11,fontWeight:700}}>{i+1}/{myPosts.length}</div>
                    </div>
                    <div style={{padding:'9px 12px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        style={{fontSize:11,color:'#4f8ef7',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:150,textDecoration:'none'}}>
                        🔗 View
                      </a>
                      <button
                        onClick={async()=>{
                          const updated=myPosts.filter((_,idx)=>idx!==i);
                          const r = await adminAPI.updateHospital(h.hospitalId, { postImages: updated });
                          if (r.error) { toast_(r.error, true); return; }
                          setHDetail(p=>p?{...p,hospital:{...p.hospital,postImages:updated}}:p);
                          setHospitals(p=>p.map(x=>x.hospitalId===h.hospitalId?{...x,postImages:updated}:x));
                          toast_('Image removed');
                        }}
                        style={{padding:'4px 9px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.25)',borderRadius:6,color:'#f87171',cursor:'pointer',fontSize:12,fontFamily:'inherit'}}>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
                {Array.from({length:5-myPosts.length}).map((_,i)=>(
                  <div key={`e${i}`} style={{aspectRatio:'16/9',borderRadius:12,border:'2px dashed var(--surface-muted)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--surface-strong)',fontSize:12,flexDirection:'column',gap:5}}>
                    <span style={{fontSize:22}}>➕</span> Empty slot
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── STATUS & SETTINGS ────────────────────────────────────── */}
        {hTab==='status'&&(
          <div>
            <div style={{fontSize:20,fontWeight:800,marginBottom:14}}>⚙️ Hospital Status & Settings</div>
            <div className="resp-grid-2col-equal" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>

              {/* Left: Toggles */}
              <div>
                <div style={{...S,padding:'16px 18px'}}>
                  <div style={{...H2,marginBottom:14}}>🔘 Status Toggles</div>
                  {[
                    {key:'isOnline',    label:'Hospital Online',   sub:'Visible to patients in app',               color:'#00d4aa', icon:'🟢'},
                    {key:'is24x7',     label:'24/7 Open',         sub:'Open all hours, no OP restrictions',       color:'#4f8ef7', icon:'⏰'},
                    {key:'hasEmergency',label:'Emergency Care',    sub:'Appears in Emergency tab for patients',    color:'#ef4444', icon:'🚨'},
                    {key:'isTrusted',  label:'Verified Hospital',  sub:'Shows trust/verified badge to patients',   color:'#f59e0b', icon:'✅'},
                  ].map(({key,label,sub,color,icon})=>(
                    <div key={key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 0',borderBottom:'1px solid var(--surface-subtle)'}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <span style={{fontSize:18}}>{icon}</span>
                        <div>
                          <div style={{fontWeight:600,fontSize:13,color:h[key]?color:'#94a3b8'}}>{label}</div>
                          <div style={{fontSize:11,color:'var(--text-muted)'}}>{sub}</div>
                        </div>
                      </div>
                      <label style={{position:'relative',display:'inline-block',width:42,height:24,cursor:'pointer'}}>
                        <input type="checkbox" style={{opacity:0,width:0,height:0}} checked={!!h[key]}
                          onChange={async e=>{
                            const newVal=e.target.checked;
                            const r = await adminAPI.updateHospital(h.hospitalId, { [key]: newVal });
                            if (r.error) { toast_(r.error, true); return; }
                            setHDetail(p=>p?{...p,hospital:{...p.hospital,[key]:newVal}}:p);
                            setHospitals(p=>p.map(x=>x.hospitalId===h.hospitalId?{...x,[key]:newVal}:x));
                            toast_(`✅ ${label} → ${newVal?'ON':'OFF'}`);
                          }}/>
                        <span style={{position:'absolute',cursor:'pointer',inset:0,background:h[key]?color:'var(--border-color-hover)',borderRadius:24,transition:'0.2s'}}>
                          <span style={{position:'absolute',left:h[key]?20:3,top:3,width:18,height:18,background:'#fff',borderRadius:'50%',transition:'0.2s',boxShadow:'0 1px 4px rgba(0,0,0,0.4)'}}/>
                        </span>
                      </label>
                    </div>
                  ))}
                </div>

                {/* Shutdown */}
                <div style={{...S,padding:'16px 18px',marginTop:12,borderLeft:`3px solid ${h.isShutdown?'#ef4444':'var(--border-color-hover)'}`}}>
                  <div style={{...H2,color:h.isShutdown?'#f87171':'#f1f5f9',marginBottom:10}}>🚫 Shutdown Status</div>
                  {h.isShutdown&&(
                    <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#fca5a5',marginBottom:10}}>
                      Hospital is currently shut down · Reason: {h.shutdownReason||'—'} · Reopens: {h.reopenDate||'TBD'}
                    </div>
                  )}
                  <div style={{display:'flex',gap:8}}>
                    <button onClick={async()=>{
                      const r = await adminAPI.updateHospital(h.hospitalId, { isShutdown: !h.isShutdown });
                      if (r.error) { toast_(r.error, true); return; }
                      setHDetail(p=>p?{...p,hospital:{...p.hospital,isShutdown:!h.isShutdown}}:p);
                      setHospitals(p=>p.map(x=>x.hospitalId===h.hospitalId?{...x,isShutdown:!h.isShutdown}:x));
                      toast_(h.isShutdown?'✅ Shutdown lifted':'⚠️ Shutdown activated');
                    }}
                      style={{flex:1,padding:'9px',background:h.isShutdown?'rgba(16,185,129,0.15)':'rgba(239,68,68,0.12)',border:`1px solid ${h.isShutdown?'rgba(16,185,129,0.3)':'rgba(239,68,68,0.3)'}`,borderRadius:8,color:h.isShutdown?'#34d399':'#f87171',cursor:'pointer',fontWeight:700,fontSize:12,fontFamily:'inherit'}}>
                      {h.isShutdown?'✅ Lift Shutdown':'🚫 Activate Shutdown'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Right: Images + Info */}
              <div>
                <div style={{...S,padding:'16px 18px'}}>
                  <div style={{...H2,marginBottom:14}}>🖼️ Hospital Images</div>
                  <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6}}>Logo (shown in hospital cards)</div>
                  {h.logoUrl?(
                    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
                      <img src={h.logoUrl} alt="Logo" style={{width:56,height:56,borderRadius:10,objectFit:'cover',border:'1px solid var(--border-color-hover)'}}
                        onError={e=>{e.target.style.display='none';}}/>
                      <div>
                        <a href={h.logoUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:'#4f8ef7',display:'block',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>🔗 View Logo</a>
                        <button onClick={async()=>{
                          const r = await adminAPI.updateHospital(h.hospitalId, { logoUrl: '' });
                          if (r.error) { toast_(r.error, true); return; }
                          setHDetail(p=>p?{...p,hospital:{...p.hospital,logoUrl:''}}:p);
                          setHospitals(p=>p.map(x=>x.hospitalId===h.hospitalId?{...x,logoUrl:''}:x));
                          toast_('Logo removed');
                        }} style={{fontSize:10,color:'#f87171',background:'none',border:'none',cursor:'pointer',padding:0,marginTop:3,fontFamily:'inherit'}}>✕ Remove</button>
                      </div>
                    </div>
                  ):(
                    <div style={{fontSize:12,color:'var(--border-color-hover)',marginBottom:14}}>No logo uploaded</div>
                  )}
                  <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6}}>Hospital Photo</div>
                  {h.photoUrl?(
                    <div style={{marginBottom:10}}>
                      <img src={h.photoUrl} alt="Hospital exterior" style={{width:'100%',height:90,objectFit:'cover',borderRadius:9,border:'1px solid var(--border-color)'}}
                        onError={e=>{e.target.style.display='none';}}/>
                      <div style={{display:'flex',justifyContent:'space-between',marginTop:5}}>
                        <a href={h.photoUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:'#4f8ef7',textDecoration:'none'}}>🔗 View Photo</a>
                        <button onClick={async()=>{
                          const r = await adminAPI.updateHospital(h.hospitalId, { photoUrl: '' });
                          if (r.error) { toast_(r.error, true); return; }
                          setHDetail(p=>p?{...p,hospital:{...p.hospital,photoUrl:''}}:p);
                          setHospitals(p=>p.map(x=>x.hospitalId===h.hospitalId?{...x,photoUrl:''}:x));
                          toast_('Photo removed');
                        }} style={{fontSize:10,color:'#f87171',background:'none',border:'none',cursor:'pointer',fontFamily:'inherit'}}>✕ Remove</button>
                      </div>
                    </div>
                  ):(
                    <div style={{fontSize:12,color:'var(--border-color-hover)',marginBottom:10}}>No photo uploaded</div>
                  )}
                </div>

                <div style={{...S,padding:'16px 18px',marginTop:12}}>
                  <div style={{...H2,marginBottom:12}}>🕐 OP Schedule</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
                    <div style={{background:'var(--surface-subtle)',borderRadius:8,padding:'8px 12px'}}>
                      <div style={{fontSize:10,color:'var(--text-muted)',marginBottom:2}}>START</div>
                      <div style={{fontWeight:700,color:'#00d4aa'}}>{h.opStart||'08:00'}</div>
                    </div>
                    <div style={{background:'var(--surface-subtle)',borderRadius:8,padding:'8px 12px'}}>
                      <div style={{fontSize:10,color:'var(--text-muted)',marginBottom:2}}>END</div>
                      <div style={{fontWeight:700,color:'#f59e0b'}}>{h.opEnd||'18:00'}</div>
                    </div>
                  </div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                    {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d=>{
                      const active=toArr(h.opDays).includes(d)||(!toArr(h.opDays).length&&d!=='Sun');
                      return <span key={d} style={{padding:'3px 9px',borderRadius:6,fontSize:12,fontWeight:600,background:active?'rgba(0,212,170,0.15)':'var(--surface-subtle)',color:active?'#00d4aa':'var(--border-color-hover)',border:`1px solid ${active?'rgba(0,212,170,0.3)':'var(--border-color)'}`}}>{d}</span>;
                    })}
                  </div>
                  <div style={{fontSize:11,color:'var(--border-color-hover)',marginTop:8}}>Edited by hospital via Settings · Syncs to patient app in real-time</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* IP LINK */}
        {hTab==='iplink'&&(
          <div>
            <div style={{fontSize:20,fontWeight:800,marginBottom:14}}>🔗 OP ↔ IP Hospital Link</div>
            <div style={S}>
              <div style={H2}>Link Status</div>
              {h.networksHospitalId?(
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
                  <span style={{padding:'4px 12px',background:'rgba(16,185,129,0.15)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:20,color:'#34d399',fontWeight:700,fontSize:12}}>✅ Linked</span>
                  <div style={{fontSize:12,color:'var(--text-muted)'}}>
                    IP Hospital: <strong style={{color:'#f1f5f9'}}>{h.networksHospitalName||h.networksHospitalId}</strong>
                    {h.networksLinkedAt&&<span style={{marginLeft:8}}>· since {fmtD(h.networksLinkedAt)}</span>}
                  </div>
                </div>
              ):(
                <div style={{marginBottom:14}}>
                  <span style={{padding:'4px 12px',background:'rgba(239,68,68,0.12)',border:'1px solid rgba(239,68,68,0.25)',borderRadius:20,color:'#f87171',fontWeight:700,fontSize:12}}>⚪ Not Linked</span>
                </div>
              )}

              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:5}}>This OP Hospital ID</div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <code style={{flex:1,padding:'7px 11px',background:'rgba(0,0,0,0.3)',border:'1px solid var(--border-color-hover)',borderRadius:8,fontSize:12,color:'#e2e8f0',fontFamily:'monospace'}}>{h.hospitalId}</code>
                  <button onClick={()=>{navigator.clipboard.writeText(h.hospitalId||'');toast_('Copied hospital ID');}} style={{padding:'7px 11px',background:'rgba(99,102,241,0.12)',border:'1px solid rgba(99,102,241,0.25)',borderRadius:8,color:'#818cf8',cursor:'pointer',fontSize:12,fontFamily:'inherit',flexShrink:0}}>📋 Copy</button>
                </div>
              </div>

              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:5}}>Step 1 — Generate OP Link Code</div>
                <button onClick={async()=>{
                  const r = await adminAPI.generateIpLink(h.hospitalId);
                  if(r.error){toast_(`❌ ${r.error}`,true);return;}
                  const code = r.data?.code||r.data;
                  setIpLinkCode(typeof code==='string'?code:JSON.stringify(code));
                  toast_('✅ Code generated — share with IP admin');
                }} style={{padding:'8px 16px',background:'rgba(245,158,11,0.15)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:8,color:'#fbbf24',cursor:'pointer',fontWeight:700,fontSize:12,fontFamily:'inherit'}}>
                  🔑 Generate OP Link Code
                </button>
                {ipLinkCode&&(
                  <div style={{marginTop:10}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <code style={{flex:1,padding:'9px 12px',background:'rgba(0,0,0,0.4)',border:'1px solid rgba(245,158,11,0.35)',borderRadius:8,fontSize:11,color:'#fbbf24',fontFamily:'monospace',wordBreak:'break-all'}}>{ipLinkCode}</code>
                      <button onClick={()=>{navigator.clipboard.writeText(ipLinkCode);toast_('Code copied');}} style={{padding:'7px 11px',background:'rgba(245,158,11,0.12)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:8,color:'#fbbf24',cursor:'pointer',fontSize:12,fontFamily:'inherit',flexShrink:0}}>📋 Copy</button>
                    </div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:5}}>Share this code with the IP-side admin. They paste it in Networks HMS → Admin → OP Link.</div>
                  </div>
                )}
              </div>

              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:5}}>Step 2 — Accept IP Link Code</div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6}}>Paste the code provided by the IP admin (Networks HMS) below:</div>
                <textarea value={ipPasteCode} onChange={e=>setIpPasteCode(e.target.value)} rows={3} placeholder="Paste IP→OP code here…" style={{width:'100%',boxSizing:'border-box',padding:'9px 12px',background:'rgba(0,0,0,0.3)',border:'1px solid var(--border-color-hover)',borderRadius:8,color:'#e2e8f0',fontSize:12,fontFamily:'monospace',resize:'vertical',marginBottom:8}}/>
                <button onClick={async()=>{
                  if(!ipPasteCode.trim()){toast_('Paste the IP code first',true);return;}
                  const r = await adminAPI.acceptIpLink(h.hospitalId,{code:ipPasteCode.trim()});
                  if(r.error){toast_(`❌ ${r.error}`,true);return;}
                  toast_('✅ Hospitals linked successfully');
                  setIpPasteCode('');
                  setIpLinkCode(null);
                  loadDetail(selectedH);
                }} style={{padding:'8px 16px',background:'rgba(16,185,129,0.15)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:8,color:'#34d399',cursor:'pointer',fontWeight:700,fontSize:12,fontFamily:'inherit'}}>
                  🔗 Link with IP
                </button>
              </div>

              {h.networksHospitalId&&(
                <div style={{borderTop:'1px solid var(--surface-muted)',paddingTop:14}}>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:5}}>Danger Zone</div>
                  <button onClick={()=>setConfirmDel({type:'ipunlink',item:h})} style={{padding:'8px 16px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.25)',borderRadius:8,color:'#f87171',cursor:'pointer',fontWeight:700,fontSize:12,fontFamily:'inherit'}}>
                    🔓 Unlink from IP Hospital
                  </button>
                </div>
              )}

              <div style={{marginTop:16,padding:'10px 14px',background:'rgba(99,102,241,0.07)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:8,fontSize:11,color:'var(--text-muted)',lineHeight:1.6}}>
                <strong style={{color:'#818cf8'}}>Cross-account setup note:</strong> OP (HCEN) and IP (Networks HMS) run in separate AWS accounts.
                Each side generates and accepts a code. Both deployments must share the same <code style={{color:'#c4b5fd',fontFamily:'monospace'}}>HCEN_CROSS_LINK_SECRET</code> env var.
              </div>
            </div>
          </div>
        )}

      </div>
      <HospitalCommissionModal
        open={showCommissionEdit}
        hospital={h}
        commission={hDetail?.commission}
        toast_={toast_}
        onClose={() => setShowCommissionEdit(false)}
        onSaved={() => selectedH && loadDetail(selectedH)}
      />
      </>
    );
  };

  // ══════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <div style={{minHeight:'100vh',background:'#070d1a',display:'flex',fontFamily:'Outfit,Inter,sans-serif',color:'var(--text-primary)'}}>
      <Toast t={toast}/>

      {dataErrors.length > 0 && (
        <div style={{
          position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9998, maxWidth: 560, width: 'calc(100% - 48px)',
          background: 'rgba(127,29,29,0.95)', border: '1px solid rgba(239,68,68,0.5)',
          borderRadius: 12, padding: '12px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
        }}>
          <div style={{ fontWeight: 800, fontSize: 13, color: '#fecaca', marginBottom: 6 }}>
            ⚠ Some admin data failed to load — numbers below may be wrong
          </div>
          <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 12, color: '#fca5a5', lineHeight: 1.5 }}>
            {dataErrors.map((e) => <li key={e}>{e}</li>)}
          </ul>
          <button
            onClick={() => fetchAll()}
            style={{
              padding: '6px 12px', background: 'rgba(239,68,68,0.2)',
              border: '1px solid rgba(239,68,68,0.4)', borderRadius: 8,
              color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Retry all
          </button>
        </div>
      )}

      {/* SIDEBAR */}
      <aside style={{width:220,background:'var(--surface-subtle)',borderRight:'1px solid rgba(245,158,11,0.1)',display:'flex',flexDirection:'column',padding:'16px 0',flexShrink:0}}>
        <div style={{padding:'0 14px 16px',borderBottom:'1px solid var(--surface-muted)'}}>
          <div style={{display:'flex',alignItems:'center',gap:9}}>
            <div style={{width:33,height:33,background:'linear-gradient(135deg,#f59e0b,#d97706)',borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center'}}>
              <MdAdminPanelSettings style={{color:'#000',fontSize:18}}/>
            </div>
            <div>
              <div style={{fontWeight:800,fontSize:14,color:'#fbbf24'}}>HCEN Admin</div>
              <div style={{fontSize:10,color:'var(--text-muted)'}}>Super Control Panel</div>
            </div>
          </div>
        </div>
        <nav style={{flex:1,padding:'10px 7px'}}>
          {NAVS.filter(item=>!item.ownerOnly||isOwner).map(item=>{
            const cnt = item.id==='hospitals'?hospitals.length:item.id==='bookings'?bookings.length:item.id==='patients'?patients.length:item.id==='doctors'?doctors.length:item.id==='emergencies'?activeEms:item.id==='applications'?applications.filter(a=>a.status==='pending').length:item.id==='contact-leads'?(contactLeadSummary?contactLeadSummary.new:null):item.id==='tickets'?(ticketSummary?(ticketSummary.open+(ticketSummary.in_progress||0)):null):item.id==='referrals'?(referralSummary?referralSummary.credited:null):null;
            return (
              <button key={item.id} onClick={()=>setTab(item.id)}
                style={{width:'100%',display:'flex',alignItems:'center',gap:9,padding:'8px 9px',borderRadius:9,border:'none',cursor:'pointer',marginBottom:2,background:tab===item.id?'rgba(245,158,11,0.15)':'transparent',color:tab===item.id?'#fbbf24':'var(--text-muted)',fontWeight:tab===item.id?700:400,fontSize:13,textAlign:'left',transition:'all 0.15s',fontFamily:'inherit'}}>
                <span style={{fontSize:17}}>{item.icon}</span>
                <span style={{flex:1}}>{item.label}</span>
                {cnt!=null&&cnt>0&&<span style={{background:item.id==='emergencies'?'rgba(239,68,68,0.3)':item.id==='applications'?'rgba(0,212,170,0.3)':item.id==='contact-leads'?'rgba(99,102,241,0.25)':'rgba(245,158,11,0.2)',color:item.id==='emergencies'?'#f87171':item.id==='applications'?'#00d4aa':item.id==='contact-leads'?'#818cf8':'#fbbf24',borderRadius:20,padding:'1px 6px',fontSize:11,fontWeight:700}}>{cnt}</span>}
              </button>
            );
          })}
        </nav>
        <div style={{padding:'10px 14px',borderTop:'1px solid var(--surface-muted)'}}>
          <div style={{fontSize:10,color:'var(--text-muted)',marginBottom:1}}>Signed in as {roleLabel(admin?.role)}</div>
          <div style={{fontSize:11,color:'#fbbf24',fontWeight:600,marginBottom:8,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{admin?.email}</div>
          <button onClick={onLogout} style={{width:'100%',padding:'7px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,color:'#f87171',fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:5,fontWeight:600,fontFamily:'inherit'}}>
            <MdLogout/> Sign Out
          </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main style={{flex:1,overflowY:'auto',padding:22}}>

        {/* Search/toolbar — hidden inside hospital detail */}
        {!(tab==='hospitals'&&selectedH)&&(
          <div style={{display:'flex',gap:10,marginBottom:18,alignItems:'center'}}>
            <div style={{position:'relative',flex:1}}>
              <MdSearch style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:'var(--text-muted)',fontSize:17}}/>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={`Search ${tab}...`}
                style={{width:'100%',padding:'9px 12px 9px 37px',background:'var(--surface-subtle)',border:'1px solid var(--border-color)',borderRadius:9,color:'#f1f5f9',fontSize:13,outline:'none',boxSizing:'border-box',fontFamily:'inherit'}}/>
            </div>
            <button onClick={() => fetchAll(true)} style={{padding:'9px 14px',background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:9,color:'#94a3b8',cursor:'pointer',display:'flex',alignItems:'center',gap:5,fontSize:13,fontFamily:'inherit'}}>
              <MdRefresh/> Refresh
            </button>
            {tab==='hospitals'&&<button onClick={()=>setShowAdd(true)} style={{padding:'9px 16px',background:'linear-gradient(135deg,#f59e0b,#d97706)',border:'none',borderRadius:9,color:'#000',fontWeight:700,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:5,fontFamily:'inherit'}}><MdAdd/> Add Hospital</button>}
          </div>
        )}

        {/* ── HOSPITAL APPLICATIONS ─────────────────────────────── */}
        {tab==='applications'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
              <div>
                <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>🏥 Hospital Applications</h1>
                <p style={{color:'var(--text-muted)',fontSize:13}}>Review and approve new hospital registrations</p>
              </div>
              <div style={{display:'flex',gap:10}}>
                <button onClick={loadApplications} style={{padding:'9px 14px',background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:9,color:'#94a3b8',cursor:'pointer',display:'flex',alignItems:'center',gap:5,fontSize:13}}>
                  <MdRefresh/>{appLoading?'…':'Refresh'}
                </button>
              </div>
            </div>

            {appError && (
              <div style={{ padding: '12px 16px', marginBottom: 14, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#fca5a5', fontSize: 13 }}>
                Could not load applications: {appError}
              </div>
            )}

            {appLoading ? (
              <div style={{textAlign:'center',padding:60,color:'var(--text-muted)'}}>⏳ Loading applications...</div>
            ) : applications.length === 0 ? (
              <div style={{textAlign:'center',padding:60,color:'var(--text-muted)'}}>
                <div style={{fontSize:48,marginBottom:16}}>📋</div>
                <div style={{fontSize:16,fontWeight:600}}>No pending applications</div>
                <div style={{fontSize:13,marginTop:8}}>Hospitals can apply at /apply on the login page</div>
              </div>
            ) : (
              <div style={{display:'grid',gap:12}}>
                {applications.map(app => (
                  <div key={app.applicationId} style={{padding:20,background:'var(--surface-subtle)',border:'1px solid var(--border-color)',borderRadius:12,display:'grid',gridTemplateColumns:'1fr auto',gap:16,alignItems:'center'}}>
                    <div>
                      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:8}}>
                        <span style={{fontSize:24}}>🏥</span>
                        <span style={{fontSize:17,fontWeight:700,color:'var(--text-primary)'}}>{app.hospitalName}</span>
                        <span style={{padding:'3px 10px',background:app.status==='pending'?'rgba(245,158,11,0.2)':app.status==='approved'?'rgba(0,212,170,0.2)':'rgba(239,68,68,0.2)',borderRadius:20,fontSize:11,color:app.status==='pending'?'#fbbf24':app.status==='approved'?'#00d4aa':'#f87171',fontWeight:700,textTransform:'uppercase'}}>
                          {app.status}
                        </span>
                        {(() => {
                          const es = app.kyc?.esign || {};
                          const hasPdf = !!(es.signedDocumentUrl || app.documentUrls?.signedAgreement);
                          const agrColor = hasPdf ? '#00d4aa' : (es.status === 'pending' || es.transactionId || es.docketId ? '#fbbf24' : '#f87171');
                          const agrBg = hasPdf ? 'rgba(0,212,170,0.2)' : (es.status === 'pending' || es.transactionId || es.docketId ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.15)');
                          const agrLabel = hasPdf ? 'Agreement PDF ✓' : (es.status === 'pending' || es.transactionId || es.docketId ? 'eSign started' : 'No agreement PDF');
                          return (
                            <span style={{padding:'3px 10px',background:agrBg,borderRadius:20,fontSize:11,color:agrColor,fontWeight:700}}>
                              {agrLabel}
                            </span>
                          );
                        })()}
                      </div>
                      <div style={{display:'flex',gap:20,flexWrap:'wrap',fontSize:12,color:'var(--text-muted)'}}>
                        <span><strong>Contact:</strong> {app.contactName}</span>
                        <span><strong>Email:</strong> {app.email}</span>
                        <span><strong>Phone:</strong> {app.phone}</span>
                        <span><strong>City:</strong> {app.city}, {app.state}</span>
                        <span><strong>Type:</strong> {app.hospitalType}</span>
                        <span><strong>Submitted:</strong> {fmtD(app.submittedAt)}</span>
                        {app.reviewedByName && <span><strong>Reviewed by:</strong> {app.reviewedByName}</span>}
                      </div>
                    </div>
                    <div style={{display:'flex',gap:8}}>
                      <button 
                        onClick={async () => {
                          // PRODUCTION FIX (May 25, 2026): the list endpoint
                          // returns RAW S3 URLs (which 403 because the bucket
                          // is private) and STALE kyc (which shows "NOT RUN"
                          // even after the applicant verified). The single-
                          // app endpoint presigns every URL for 15 min and
                          // lazy-populates missing kyc.* fields. We must
                          // hydrate from there before opening the wizard.
                          const r = await adminApplicationsAPI.getById(app.applicationId);
                          if (r.error || !r.data) {
                            toast_('Could not load application details: ' + (r.error || 'unknown'), true);
                            // Fall back to the list-cached row so the admin
                            // can at least see meta-info even if S3 is down.
                            setSelectedApp(app);
                            return;
                          }
                          setSelectedApp(r.data);
                        }}
                        style={{padding:'10px 18px',background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.3)',borderRadius:8,color:'#818cf8',cursor:'pointer',fontSize:13,fontWeight:600,whiteSpace:'nowrap'}}
                      >
                        View Details
                      </button>
                      {app.status==='pending'&&(
                        <>
                          <button 
                            onClick={async()=>{
                              const defaultName = getAdminReviewerName();
                              const name = window.prompt('Enter your name (recorded permanently on approval):', defaultName);
                              if (name === null) return;
                              if (!String(name).trim()) { toast_('Reviewer name is required', true); return; }
                              setAdminReviewerName(name.trim());
                              // eslint-disable-next-line no-restricted-globals
                              const confirmed = confirm('Approve this hospital? They will receive login credentials via email.');
                              if(!confirmed) return;
                              const r = await adminApplicationsAPI.update(app.applicationId, { status:'approved', reviewerName: name.trim() });
                              if(r.error){ toast_(r.error,true); return; }
                              toast_('Hospital approved! Credentials sent via email.');
                              setApplications(p=>p.map(a=>a.applicationId===app.applicationId?{...a,status:'approved', reviewedByName: name.trim()}:a));
                            }}
                            style={{padding:'10px 18px',background:'rgba(0,212,170,0.2)',border:'1px solid rgba(0,212,170,0.3)',borderRadius:8,color:'#00d4aa',cursor:'pointer',fontSize:13,fontWeight:700,whiteSpace:'nowrap'}}
                          >
                            <MdCheckCircle/> Approve
                          </button>
                          <button 
                            onClick={async()=>{
                              const defaultName = getAdminReviewerName();
                              const name = window.prompt('Enter your name (recorded permanently on rejection):', defaultName);
                              if (name === null) return;
                              if (!String(name).trim()) { toast_('Reviewer name is required', true); return; }
                              setAdminReviewerName(name.trim());
                              const reason = prompt('Rejection reason (optional):');
                              if(reason===null) return;
                              const r = await adminApplicationsAPI.update(app.applicationId, {
                                status:'rejected',
                                rejectionReason: reason||'',
                                reviewerName: name.trim(),
                              });
                              if(r.error){ toast_(r.error,true); return; }
                              toast_('Application rejected.');
                              setApplications(p=>p.map(a=>a.applicationId===app.applicationId?{...a,status:'rejected',rejectionReason:reason, reviewedByName: name.trim()}:a));
                            }}
                            style={{padding:'10px 18px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,color:'#f87171',cursor:'pointer',fontSize:13,whiteSpace:'nowrap'}}
                          >
                            <MdClose/> Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Application Review Wizard (multi-step KYC review) */}
            {selectedApp && (
              <AdminApplicationReview
                app={selectedApp}
                toast={toast_}
                onClose={() => setSelectedApp(null)}
                onUpdated={(updated) => {
                  setApplications(prev => prev.map(a =>
                    a.applicationId === updated.applicationId ? { ...a, ...updated } : a
                  ));
                }}
              />
            )}

          </div>
        )}

        {tab==='contact-leads'&&(
          <AdminContactInquiries embedded onBack={()=>setTab('dashboard')} />
        )}

        {/* ── DASHBOARD ─────────────────────────────────────────── */}
        {tab==='dashboard'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16,flexWrap:'wrap',gap:10}}>
              <div>
                <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>Platform Overview 📊</h1>
                <p style={{color:'var(--text-muted)',fontSize:13}}>HCEN Network — Live Intelligence Dashboard</p>
              </div>
              <button onClick={()=>{setActFeedLoaded(false);}} style={{padding:'8px 14px',background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:9,color:'#94a3b8',cursor:'pointer',display:'flex',alignItems:'center',gap:5,fontSize:13,fontFamily:'inherit'}}>
                <MdRefresh/> Refresh Feed
              </button>
            </div>

            {patientApiError && (
              <div style={{ padding: '12px 16px', marginBottom: 14, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 10, color: '#fcd34d', fontSize: 13 }}>
                Tickets / Referrals summary unavailable ({patientApiError}).
              </div>
            )}

            {/* KPI Cards */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:10,marginBottom:18}}>
              <StatCard icon="🏥" label="Hospitals"   value={analytics?.totalHospitals||0}     color="#f59e0b" sub={`${analytics?.activeHospitals||0} active`}/>
              <StatCard icon="📋" label="Bookings"    value={fmt(analytics?.totalBookings||0)}  color="#6366f1" sub={`Today: ${analytics?.todayBookings||0}`}/>
              <StatCard icon="👥" label="Patients"    value={fmt(analytics?.uniquePatients||0)} color="#8b5cf6"/>
              <StatCard icon="👨‍⚕️" label="Doctors" value={fmt(analytics?.totalDoctors||0)}   color="#00d4aa"/>
              <StatCard icon="💰" label="Revenue"     value={fmtR(analytics?.totalRevenue||0)}  color="#fbbf24" sub={`Today: ${fmtR(analytics?.todayRevenue||0)}`}/>
              <StatCard icon="✅" label="Confirmed"   value={fmt(analytics?.confirmedBookings||0)} color="#10b981"/>
              <StatCard icon="🚨" label="Emergencies" value={analytics?.activeEmergencies||0}   color="#ef4444"/>

              {/* Support Tickets — click to open dashboard */}
              <div onClick={()=>setTab('tickets')} style={{cursor:'pointer'}}>
                <StatCard icon="🎫" label="Open Tickets"
                  value={(ticketSummary || analytics?.ticketsSummary) ? fmt((ticketSummary || analytics?.ticketsSummary).open + ((ticketSummary || analytics?.ticketsSummary).in_progress||0)) : '—'}
                  color="#fbbf24"
                  sub={(ticketSummary || analytics?.ticketsSummary) ? `Resolved today: ${(ticketSummary || analytics?.ticketsSummary).resolvedToday||0} · ★${(ticketSummary || analytics?.ticketsSummary).avgRating ?? '—'}` : (ticketSummaryLoaded ? 'No data' : 'Loading…')}/>
              </div>
              <div onClick={()=>setTab('tickets')} style={{cursor:'pointer'}}>
                <StatCard icon="🚨" label="Overdue Tickets"
                  value={(ticketSummary || analytics?.ticketsSummary) ? fmt((ticketSummary || analytics?.ticketsSummary).overdue||0) : '—'}
                  color="#ef4444"
                  sub={(ticketSummary || analytics?.ticketsSummary) ? `${(ticketSummary || analytics?.ticketsSummary).total||0} total · ${(ticketSummary || analytics?.ticketsSummary).resolved||0} done` : (ticketSummaryLoaded ? 'No data' : 'Loading…')}/>
              </div>

              {/* Refer & Earn — click to open dashboard */}
              <div onClick={()=>setTab('referrals')} style={{cursor:'pointer'}}>
                <StatCard icon="🎁" label="Referrals Credited"
                  value={(referralSummary || analytics?.referralsSummary) ? fmt((referralSummary || analytics?.referralsSummary).credited||0) : '—'}
                  color="#a78bfa"
                  sub={(referralSummary || analytics?.referralsSummary) ? `${fmt((referralSummary || analytics?.referralsSummary).total||0)} invited · ${fmt((referralSummary || analytics?.referralsSummary).totalCouponsIssued||0)} coupons` : (referralSummaryLoaded ? 'No data' : 'Loading…')}/>
              </div>
              <div onClick={()=>setTab('referrals')} style={{cursor:'pointer'}}>
                <StatCard icon="💸" label="Rewards Paid"
                  value={(referralSummary || analytics?.referralsSummary) ? fmtR((referralSummary || analytics?.referralsSummary).totalEarnedINR||0) : '—'}
                  color="#10b981"
                  sub={(referralSummary || analytics?.referralsSummary) ? `₹${(referralSummary || analytics?.referralsSummary).rewardAmount||50} per referral` : (referralSummaryLoaded ? 'No data' : 'Loading…')}/>
              </div>
            </div>

            {/* ── NEEDS ATTENTION ──────────────────────────────────── */}
            {(()=>{
              const pendingBank  = hospitals.filter(h=>h.accountNumber&&!isBankVerified(h));
              const activeEmsNow = emergencies.filter(e=>e.status==='new');
              const inactiveH    = hospitals.filter(h=>h.status==='inactive');
              if(!pendingBank.length&&!activeEmsNow.length&&!inactiveH.length) return null;
              return (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#ef4444',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.08em'}}>⚡ Needs Attention</div>
                  <div style={{display:'grid',gap:7}}>
                    {activeEmsNow.map(e=>(
                      <div key={e.alertId} onClick={()=>setTab('emergencies')} style={{display:'flex',alignItems:'center',gap:12,padding:'11px 15px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:11,cursor:'pointer'}}>
                        <span style={{fontSize:18}}>🚨</span>
                        <div style={{flex:1}}><div style={{fontWeight:700,color:'#f87171',fontSize:13}}>Active Emergency — {e.patientName||'Unknown'}</div><div style={{fontSize:11,color:'var(--text-muted)'}}>{e.emergencyType||'Emergency'} · {hospitals.find(h=>h.hospitalId===e.hospitalId)?.name||e.hospitalId} · {e.phone||'—'}</div></div>
                        <span style={{fontSize:11,color:'#f87171',fontWeight:700}}>VIEW →</span>
                      </div>
                    ))}
                    {pendingBank.map(h=>(
                      <div key={h.hospitalId} onClick={()=>{setTab('hospitals');loadDetail(h);}} style={{display:'flex',alignItems:'center',gap:12,padding:'11px 15px',background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:11,cursor:'pointer'}}>
                        <span style={{fontSize:18}}>🏦</span>
                        <div style={{flex:1}}><div style={{fontWeight:700,color:'#fbbf24',fontSize:13}}>Bank Verification Pending — {h.name}</div><div style={{fontSize:11,color:'var(--text-muted)'}}>Account: ••••{(h.accountNumber||'').slice(-4)} · {h.bankName||'—'}</div></div>
                        <span style={{fontSize:11,color:'#fbbf24',fontWeight:700}}>VERIFY →</span>
                      </div>
                    ))}
                    {inactiveH.map(h=>(
                      <div key={h.hospitalId} style={{display:'flex',alignItems:'center',gap:12,padding:'11px 15px',background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:11}}>
                        <span style={{fontSize:18}}>🔴</span>
                        <div style={{flex:1}}><div style={{fontWeight:700,color:'#818cf8',fontSize:13}}>Inactive — {h.name}</div><div style={{fontSize:11,color:'var(--text-muted)'}}>Patients cannot see this hospital</div></div>
                        <button onClick={()=>toggleH(h)} style={{padding:'4px 11px',background:'rgba(99,102,241,0.2)',border:'1px solid rgba(99,102,241,0.3)',borderRadius:7,color:'#818cf8',cursor:'pointer',fontSize:12,fontWeight:700}}>Enable</button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── CHARTS ROW ────────────────────────────────────────── */}
            <div className="resp-grid-2col-equal" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
              {/* 7-day booking trend */}
              <div style={S}>
                <div style={{...H2,marginBottom:10}}>📈 7-Day Booking Trend</div>
                {actFeed?.daily7days ? (
                  <div>
                    <MiniBar data={actFeed.daily7days.map(d=>d.bookings)} color="#6366f1" height={56} labels={actFeed.daily7days.map(d=>`${d.label}: ${d.bookings} bookings`)}/>
                    <div style={{display:'flex',justifyContent:'space-between',marginTop:5}}>
                      {actFeed.daily7days.map((d,i)=>(
                        <div key={i} style={{fontSize:9,color:'var(--text-muted)',textAlign:'center',flex:1}}>{d.label.split(' ')[0]}</div>
                      ))}
                    </div>
                    <div style={{display:'flex',gap:14,marginTop:8}}>
                      <div><div style={{fontSize:18,fontWeight:800,color:'#6366f1'}}>{actFeed.daily7days.reduce((s,d)=>s+d.bookings,0)}</div><div style={{fontSize:10,color:'var(--text-muted)'}}>Total Bookings</div></div>
                      <div><div style={{fontSize:18,fontWeight:800,color:'#fbbf24'}}>{fmtR(actFeed.daily7days.reduce((s,d)=>s+d.revenue,0))}</div><div style={{fontSize:10,color:'var(--text-muted)'}}>7-Day Revenue</div></div>
                    </div>
                  </div>
                ) : <div style={{color:'var(--border-color-hover)',fontSize:12,padding:'16px 0'}}>Loading chart data…</div>}
              </div>

              {/* Today's hourly bookings */}
              <div style={S}>
                <div style={{...H2,marginBottom:10}}>⏱️ Today — Hourly Bookings</div>
                {actFeed?.hourlyToday ? (
                  <div>
                    <MiniBar data={actFeed.hourlyToday} color="#00d4aa" height={56} labels={actFeed.hourlyToday.map((v,i)=>`${i}:00 — ${v} bookings`)}/>
                    <div style={{display:'flex',justifyContent:'space-between',marginTop:5}}>
                      {['12A','3A','6A','9A','12P','3P','6P','9P'].map((l,i)=>(
                        <div key={i} style={{fontSize:9,color:'var(--text-muted)'}}>{l}</div>
                      ))}
                    </div>
                    <div style={{display:'flex',gap:14,marginTop:8}}>
                      <div><div style={{fontSize:18,fontWeight:800,color:'#00d4aa'}}>{actFeed.hourlyToday.reduce((s,v)=>s+v,0)}</div><div style={{fontSize:10,color:'var(--text-muted)'}}>Today's Bookings</div></div>
                      <div><div style={{fontSize:18,fontWeight:800,color:'#fbbf24'}}>{fmtR(analytics?.todayRevenue||0)}</div><div style={{fontSize:10,color:'var(--text-muted)'}}>Today's Revenue</div></div>
                    </div>
                  </div>
                ) : <div style={{color:'var(--border-color-hover)',fontSize:12,padding:'16px 0'}}>Loading…</div>}
              </div>
            </div>

            {/* ── LIVE ACTIVITY FEED + TOP PATIENTS ─────────────────── */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 340px',gap:12,marginBottom:14}}>

              {/* Live Activity Feed */}
              <div style={S}>
                <div style={{...H2,marginBottom:12}}>
                  <span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:'#10b981',boxShadow:'0 0 6px #10b981',marginRight:4}}/>
                  Live Platform Activity
                  <span style={{marginLeft:'auto',fontSize:11,color:'var(--border-color-hover)',fontWeight:400}}>{actFeed?.total||0} total events</span>
                </div>
                {!actFeed && <div style={{textAlign:'center',padding:30,color:'var(--text-muted)'}}>Loading feed…</div>}
                {actFeed?.feed?.slice(0,20).map((ev,i)=>(
                  <div key={i} style={{display:'flex',gap:10,padding:'8px 0',borderBottom:'1px solid var(--surface-subtle)',alignItems:'flex-start'}}>
                    <div style={{width:28,height:28,borderRadius:8,background:`${ev.color}22`,border:`1px solid ${ev.color}44`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,flexShrink:0}}>{ev.icon}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                        <span style={{fontWeight:600,fontSize:13,color:'var(--text-primary)'}}>{ev.title}</span>
                        <Badge s={ev.status} text={ev.status}/>
                        {ev.fee>0&&<span style={{fontSize:11,color:'#fbbf24',fontWeight:700}}>{fmtR(ev.fee)}</span>}
                      </div>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ev.description}</div>
                      <div style={{fontSize:10,color:'var(--text-muted)',marginTop:1,display:'flex',gap:8}}>
                        {ev.phone&&ev.phone!=='—'&&<span><MdPhone style={{fontSize:10}}/> {ev.phone}</span>}
                        <span><MdAccessTime style={{fontSize:10}}/> {timeAgo(ev.timestamp)}</span>
                      </div>
                    </div>
                    {ev.type==='booking'&&<button onClick={()=>{setTab('intelligence');setIntelQ(ev.phone||'');if(ev.phone&&ev.phone!=='—')setTimeout(()=>intelLookup(ev.phone),100);}} title="View patient intelligence" style={{background:'none',border:'none',color:'rgba(99,102,241,0.6)',cursor:'pointer',fontSize:14,padding:'2px 4px',flexShrink:0}}><MdInsights/></button>}
                  </div>
                ))}
              </div>

              {/* Top Patients */}
              <div style={S}>
                <div style={{...H2,marginBottom:12}}>🏆 Top Patients (All Time)</div>
                {actFeed?.topPatients?.map((p,i)=>(
                  <div key={i} onClick={()=>{setTab('intelligence');setIntelQ(p.phone);setTimeout(()=>intelLookup(p.phone),100);}} style={{display:'flex',alignItems:'center',gap:9,padding:'7px 0',borderBottom:'1px solid var(--surface-subtle)',cursor:'pointer'}}>
                    <div style={{width:22,height:22,borderRadius:6,background:'rgba(245,158,11,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:'#fbbf24',flexShrink:0}}>{i+1}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                      <div style={{fontSize:10,color:'var(--text-muted)'}}>{p.phone} · {timeAgo(p.lastSeen)}</div>
                    </div>
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <div style={{fontSize:13,fontWeight:800,color:'#6366f1'}}>{p.count}</div>
                      <div style={{fontSize:10,color:'var(--text-muted)'}}>bookings</div>
                    </div>
                  </div>
                ))}
                {!actFeed?.topPatients&&<div style={{color:'var(--border-color-hover)',fontSize:12}}>Loading…</div>}
              </div>
            </div>

            {/* ── Hospital quick list ───────────────────────────────── */}
            <div style={S}>
              <div style={H2}>🏥 Hospitals — click to manage</div>
              {hospitals.slice(0,8).map(h=>(
                <div key={h.hospitalId} onClick={()=>{setTab('hospitals');loadDetail(h);}}
                  style={{display:'flex',alignItems:'center',gap:12,padding:'9px 0',borderBottom:'1px solid var(--surface-subtle)',cursor:'pointer'}}>
                  <div style={{fontSize:16}}>🏥</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:13}}>{h.name}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)'}}>{h.city} · {h.email}</div>
                  </div>
                  {h.accountNumber&&!isBankVerified(h)&&<span style={{fontSize:10,color:'#f59e0b',fontWeight:700}}>⚠️ Bank Unverified</span>}
                  <Badge s={h.status||'active'}/>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── HOSPITALS ─────────────────────────────────────────── */}
        {tab==='hospitals'&&(
          selectedH ? renderHospitalDetail() : (
            <div>
              <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>Hospital Management 🏥</h1>
              <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16}}>{hospitals.length} hospitals · Click any to manage</p>
              <div style={{display:'grid',gap:9}}>
                {filt(hospitals,['name','city','email']).map(h=>(
                  <div key={h.hospitalId} onClick={()=>loadDetail(h)}
                    style={{background:'var(--surface-subtle)',border:'1px solid var(--surface-muted)',borderRadius:13,padding:'15px 18px',display:'flex',alignItems:'center',gap:13,flexWrap:'wrap',cursor:'pointer',transition:'border-color 0.15s'}}>
                    <div style={{width:42,height:42,background:'rgba(245,158,11,0.1)',borderRadius:11,display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0}}>🏥</div>
                    <div style={{flex:1,minWidth:180}}>
                      <div style={{fontWeight:700,fontSize:15}}>{h.name}</div>
                      <div style={{display:'flex',gap:10,flexWrap:'wrap',marginTop:3}}>
                        {h.city&&<span style={{fontSize:12,color:'var(--text-muted)',display:'flex',alignItems:'center',gap:3}}><MdLocationCity style={{fontSize:11}}/>{h.city}</span>}
                        {h.email&&<span style={{fontSize:12,color:'var(--text-muted)',display:'flex',alignItems:'center',gap:3}}><MdEmail style={{fontSize:11}}/>{h.email}</span>}
                        {h.phone&&<span style={{fontSize:12,color:'var(--text-muted)',display:'flex',alignItems:'center',gap:3}}><MdPhone style={{fontSize:11}}/>{h.phone}</span>}
                      </div>
                      <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>Click to view full details →</div>
                    </div>
                    {h.accountNumber&&!isBankVerified(h)&&<span style={{fontSize:11,color:'#f59e0b',fontWeight:700,flexShrink:0}}>⚠️ Bank pending</span>}
                    <Badge s={h.status||'active'}/>
                    <div style={{display:'flex',gap:5}} onClick={e=>e.stopPropagation()}>
                      <button onClick={()=>toggleH(h)} style={{padding:'6px 9px',background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:7,color:'#818cf8',cursor:'pointer',fontSize:17,display:'flex',alignItems:'center'}}>{h.status==='inactive'?<MdToggleOff/>:<MdToggleOn/>}</button>
                      <button title="Re-invite owner (Cognito email)"
                        onClick={async()=>{ const r=await adminAPI.reinviteOwner(h.hospitalId); if(r.error){toast_(`❌ ${r.error}`,true);return;} const p=r.data?.tempPassword; toast_(p?`📨 MSG91 credentials sent to ${r.data?.email||h.email} · Backup: ${p}`:`📨 Credentials re-sent to ${r.data?.email||h.email}`); }}
                        style={{padding:'6px 9px',background:'rgba(0,212,170,0.1)',border:'1px solid rgba(0,212,170,0.25)',borderRadius:7,color:'#00d4aa',cursor:'pointer',fontSize:17,display:'flex',alignItems:'center'}}><MdMarkEmailRead/></button>
                      <button title="Reset password" onClick={async()=>{ const r=await adminAPI.resetPassword(h.hospitalId); if(r.error){toast_(`❌ ${r.error}`,true);return;} const p=r.data?.newPassword; toast_(p?`🔑 Password reset · MSG91 emailed · Backup: ${p}`:'🔑 Password reset & emailed via MSG91'); }} style={{padding:'6px 9px',background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:7,color:'#fbbf24',cursor:'pointer',fontSize:17,display:'flex',alignItems:'center'}}><MdLockReset/></button>
                      <button onClick={()=>setConfirmDel({type:'hospital',item:h})} style={{padding:'6px 9px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:7,color:'#f87171',cursor:'pointer',fontSize:17,display:'flex',alignItems:'center'}}><MdDelete/></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        )}

        {/* ── BOOKINGS ──────────────────────────────────────────── */}
        {tab==='bookings'&&(()=>{
          const FILTERS = ['all','confirmed','pending','cancelled','walkin'];
          const confirmed  = bookings.filter(b=>['confirmed','completed','accepted','checked_in'].includes(b.status));
          const cancelled  = bookings.filter(b=>b.status==='cancelled');
          const pendingB   = bookings.filter(b=>b.status==='pending');
          const walkins    = bookings.filter(b=>b.bookingType==='walkin'||b.doctorName==='Walk-in');
          const totalRev   = confirmed.reduce((s,b)=>s+bookingFee(b,doctors),0);
          const lostRev    = cancelled.reduce((s,b)=>s+bookingFee(b,doctors),0);
          const filtered = bookings.filter(b=>{
            const s = search.toLowerCase();
            const matchSearch = !s || (b.patientName||'').toLowerCase().includes(s) ||
              (b.doctorName||'').toLowerCase().includes(s) || (b.status||'').includes(s) ||
              (b.patientPhone||b.phone||'').includes(s);
            const matchFilter = bFilter==='all' ? true
              : bFilter==='walkin' ? (b.bookingType==='walkin'||b.doctorName==='Walk-in')
              : (b.status||'')===bFilter;
            return matchSearch && matchFilter;
          }).sort((a,b)=>(b.createdAt||'')>(a.createdAt||'')?1:-1);
          return (
            <div>
              <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>All Bookings & OPs 📋</h1>
              <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:14}}>{bookings.length} total across all hospitals</p>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:10,marginBottom:16}}>
                {[
                  {icon:'📋',label:'Total',val:bookings.length,c:'#6366f1'},
                  {icon:'✅',label:'Confirmed',val:confirmed.length,c:'#10b981',sub:fmtR(totalRev)},
                  {icon:'⏳',label:'Pending',val:pendingB.length,c:'#f59e0b'},
                  {icon:'❌',label:'Cancelled',val:cancelled.length,c:'#ef4444',sub:`Lost: ${fmtR(lostRev)}`},
                  {icon:'🚶',label:'Walk-in OPs',val:walkins.length,c:'#00d4aa'},
                ].map(x=>(
                  <div key={x.label} style={{background:'var(--surface-subtle)',border:`1px solid ${x.c}22`,borderRadius:12,padding:'12px 15px'}}>
                    <div style={{fontSize:18}}>{x.icon}</div>
                    <div style={{fontSize:22,fontWeight:800,color:x.c,marginTop:4}}>{x.val}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)'}}>{x.label}</div>
                    {x.sub&&<div style={{fontSize:11,color:x.c,fontWeight:600,marginTop:2}}>{x.sub}</div>}
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:7,marginBottom:14,flexWrap:'wrap'}}>
                {FILTERS.map(f=>(
                  <button key={f} onClick={()=>setBFilter(f)}
                    style={{padding:'6px 14px',borderRadius:20,border:'1px solid',cursor:'pointer',fontSize:12,fontWeight:600,fontFamily:'inherit',
                      background: bFilter===f ? 'rgba(99,102,241,0.2)' : 'var(--surface-subtle)',
                      borderColor: bFilter===f ? '#6366f1' : 'var(--border-color-hover)',
                      color: bFilter===f ? '#818cf8' : 'var(--text-muted)'}}>
                    {f==='all'?`All (${bookings.length})`:f==='confirmed'?`✅ Confirmed (${confirmed.length})`:f==='pending'?`⏳ Pending (${pendingB.length})`:f==='cancelled'?`❌ Cancelled (${cancelled.length})`:`🚶 Walk-in (${walkins.length})`}
                  </button>
                ))}
              </div>
              <div style={S}>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr>{['Patient','Phone','Doctor','Hospital','Date & Slot','Type','Fee','Status',''].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                    <tbody>
                      {filtered.map(b=>(
                        <tr key={b.bookingId} style={{opacity:b.status==='cancelled'?0.65:1}}>
                          <td style={TD}>
                            <div style={{fontWeight:600}}>{b.patientName||'—'}</div>
                            <div style={{fontSize:11,color:'var(--text-secondary)'}}>
                              {b.patientAge ? `${b.patientAge}y` : '—'}
                              {b.patientGender ? ` · ${b.patientGender}` : ''}
                            </div>
                            {b.patientAddress && (
                              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2,maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={b.patientAddress}>
                                🏠 {b.patientAddress}
                              </div>
                            )}
                            <div style={{fontSize:10,color:'var(--text-muted)',fontFamily:'monospace',marginTop:2}}>{b.bookingId||b.bookingIds||'—'}</div>
                          </td>
                          <td style={TD}><span style={{fontSize:12}}>{b.patientPhone||b.phone||'—'}</span></td>
                          <td style={TD}>{b.doctorName||'Walk-in'}</td>
                          <td style={TD}>{hospitals.find(h=>h.hospitalId===b.hospitalId)?.name||'—'}</td>
                          <td style={TD}><div>{fmtD(b.date||b.appointmentDate)}</div>{b.slot&&<div style={{fontSize:11,color:'var(--text-muted)'}}>@ {b.slot}</div>}{b.cancelledAt&&<div style={{fontSize:10,color:'#f87171'}}>Cancelled: {fmtD(b.cancelledAt)}</div>}</td>
                          <td style={TD}><span style={{fontSize:11,padding:'2px 8px',borderRadius:10,background:b.bookingType==='walkin'?'rgba(0,212,170,0.1)':'rgba(99,102,241,0.1)',color:b.bookingType==='walkin'?'#00d4aa':'#818cf8'}}>{b.bookingType==='walkin'?'Walk-in':'Doctor'}</span></td>
                          <td style={TD}><span style={{color:b.status==='cancelled'?'#f87171':'#fbbf24',fontWeight:700,textDecoration:b.status==='cancelled'?'line-through':'none'}}>{fmtR(b.consultationFee||b.fee||0)}</span></td>
                          <td style={TD}><Badge s={b.status}/></td>
                          <td style={TD}><button onClick={()=>setConfirmDel({type:'booking',item:b})} style={{padding:'4px 7px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:6,color:'#f87171',cursor:'pointer',display:'flex',alignItems:'center'}}><MdDelete/></button></td>
                        </tr>
                      ))}
                      {!filtered.length&&<tr><td colSpan={9} style={{...TD,textAlign:'center',color:'var(--text-muted)',padding:'30px 0'}}>No bookings for this filter</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}


        {/* ── PATIENTS ──────────────────────────────────────────── */}
        {tab==='patients'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:14,flexWrap:'wrap',gap:10}}>
              <div>
                <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>HCEN App Patients 👥</h1>
                <p style={{color:'var(--text-muted)',fontSize:13}}>{patients.length} unique patients — click any row to see full intelligence</p>
              </div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {[
                  {l:'All',v:patients.length,c:'#6366f1'},
                  {l:'Active',v:patients.filter(p=>p.cognitoStatus==='CONFIRMED').length,c:'#10b981'},
                  {l:'Has Bookings',v:patients.filter(p=>p.bookingCount>0).length,c:'#fbbf24'},
                  {l:'App Only',v:patients.filter(p=>p.source==='cognito'&&!p.bookingCount).length,c:'#8b5cf6'},
                ].map(s=>(
                  <div key={s.l} style={{padding:'6px 12px',background:`${s.c}18`,border:`1px solid ${s.c}44`,borderRadius:20,fontSize:12,fontWeight:700,color:s.c}}>{s.l}: {s.v}</div>
                ))}
              </div>
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr>{['Patient','Phone / Email','Account','Bookings','Spent','Hospitals','Last Active',''].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                <tbody>
                  {filt(patients,['patientName','phone','email']).map((p,i)=>(
                    <tr key={i} style={{cursor:'pointer',transition:'background 0.1s'}}
                      onMouseOver={e=>e.currentTarget.style.background='rgba(99,102,241,0.06)'}
                      onMouseOut={e=>e.currentTarget.style.background='transparent'}
                      onClick={()=>{setTab('intelligence');setIntelQ(p.phone||p.patientName);setTimeout(()=>intelLookup(p.phone||p.patientName),100);}}>
                      <td style={TD}>
                        <div style={{display:'flex',alignItems:'center',gap:9}}>
                          <div style={{width:32,height:32,borderRadius:10,background:'rgba(99,102,241,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:800,color:'#818cf8',flexShrink:0}}>{(p.patientName||'?').charAt(0).toUpperCase()}</div>
                          <div>
                            <div style={{fontWeight:700,fontSize:13}}>{p.patientName||'Unknown'}</div>
                            {p.source==='cognito'&&!p.bookingCount&&<div style={{fontSize:10,color:'#6366f1'}}>📱 App only</div>}
                            {p.bookingCount>5&&<div style={{fontSize:10,color:'#fbbf24'}}>⭐ Frequent user</div>}
                          </div>
                        </div>
                      </td>
                      <td style={TD}>
                        <div style={{fontSize:13}}>{p.phone||'—'}</div>
                        <div style={{fontSize:11,color:'var(--text-muted)'}}>{p.email||'—'}</div>
                      </td>
                      <td style={TD}>
                        {p.cognitoStatus&&p.cognitoStatus!=='—'
                          ? <Badge s={p.cognitoStatus==='CONFIRMED'?'confirmed':p.cognitoStatus==='FORCE_CHANGE_PASSWORD'?'pending':'active'} text={p.cognitoStatus==='CONFIRMED'?'✓ Active':p.cognitoStatus==='FORCE_CHANGE_PASSWORD'?'Setup':'Inactive'}/>
                          : <span style={{fontSize:11,color:'var(--border-color-hover)'}}>—</span>}
                      </td>
                      <td style={{...TD,textAlign:'center'}}>
                        <span style={{fontSize:18,fontWeight:800,color:p.bookingCount>5?'#fbbf24':p.bookingCount>0?'#6366f1':'var(--text-muted)'}}>{p.bookingCount||0}</span>
                      </td>
                      <td style={TD}><span style={{fontWeight:700,color:p.totalSpent>0?'#10b981':'var(--text-muted)'}}>{fmtR(p.totalSpent||0)}</span></td>
                      <td style={{...TD,textAlign:'center'}}>
                        <span style={{fontSize:16,fontWeight:800,color:'#00d4aa'}}>{(p.hospitals||[]).length}</span>
                      </td>
                      <td style={TD}>
                        <div style={{fontSize:12}}>{fmtD(p.lastBooking)}</div>
                        <div style={{fontSize:10,color:'var(--text-muted)'}}>{timeAgo(p.lastBooking)}</div>
                      </td>
                      <td style={TD}>
                        <button onClick={e=>{e.stopPropagation();setTab('intelligence');setIntelQ(p.phone||p.patientName);setTimeout(()=>intelLookup(p.phone||p.patientName),100);}}
                          style={{padding:'5px 10px',background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.3)',borderRadius:7,color:'#818cf8',cursor:'pointer',fontSize:12,fontWeight:700,display:'flex',alignItems:'center',gap:4,whiteSpace:'nowrap'}}>
                          <MdInsights style={{fontSize:13}}/> Investigate
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!filt(patients,['patientName','phone','email']).length&&<tr><td colSpan={8} style={{...TD,textAlign:'center',color:'var(--text-muted)',padding:'30px 0'}}>No patients found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── ANALYTICS ────────────────────────────────────────── */}
        {tab==='analytics'&&(()=>{
          const PLAT_FEE = 10;
          const confirmed   = bookings.filter(b=>['confirmed','completed','accepted','checked_in'].includes(b.status));
          const cancelled   = bookings.filter(b=>['cancelled','canceled','rejected'].includes(b.status));
          const pending     = bookings.filter(b=>b.status==='pending');
          const totalRev    = confirmed.reduce((s,b)=>s+bookingFee(b,doctors),0);
          const totalLost   = cancelled.reduce((s,b)=>s+bookingFee(b,doctors),0);
          const platFee     = Math.round(totalRev*PLAT_FEE/100);
          const cancelRate  = bookings.length>0?Math.round(cancelled.length/bookings.length*100):0;
          // PRODUCTION FIX: local date, not UTC (was returning yesterday in IST until 5:30 AM)
          const today       = (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
          const todayBk     = bookings.filter(b=>(b.createdAt||b.date||'').startsWith(today));

          // Bookings per day for last 30 days
          const day30 = Array.from({length:30},(_,i)=>{
            const d=new Date(); d.setDate(d.getDate()-29+i);
            const key=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; // PRODUCTION FIX: local date, not UTC
            const lbl=d.toLocaleDateString('en-IN',{day:'2-digit',month:'short'});
            const bks=bookings.filter(b=>(b.createdAt||b.date||'').startsWith(key));
            const rev=bks.filter(b=>['confirmed','completed','accepted','checked_in'].includes(b.status)).reduce((s,b)=>s+bookingFee(b,doctors),0);
            return {key,lbl,count:bks.length,confirmed:bks.filter(b=>['confirmed','completed','accepted','checked_in'].includes(b.status)).length,cancelled:bks.filter(b=>['cancelled','canceled','rejected'].includes(b.status)).length,rev};
          });

          // Hourly distribution across all bookings
          const hourDist = Array(24).fill(0);
          bookings.forEach(b=>{
            const t=b.createdAt||b.date||'';
            if(t){ const h=new Date(t).getHours(); if(!isNaN(h)) hourDist[h]++; }
          });
          const peakHour = hourDist.indexOf(Math.max(...hourDist));

          // Hospital performance
          const hPerf = hospitals.map(h=>{
            const hBk=bookings.filter(b=>b.hospitalId===h.hospitalId);
            const hConf=hBk.filter(b=>['confirmed','completed','accepted','checked_in'].includes(b.status));
            const rev=hConf.reduce((s,b)=>s+bookingFee(b,doctors),0);
            const cRate=hBk.length>0?Math.round(hBk.filter(b=>['cancelled','canceled','rejected'].includes(b.status)).length/hBk.length*100):0;
            const myDocs=doctors.filter(d=>d.hospitalId===h.hospitalId);
            return {...h,bkCount:hBk.length,confCount:hConf.length,rev,cRate,docCount:myDocs.length};
          }).sort((a,b)=>b.rev-a.rev);

          // Doctor leaderboard
          const dPerf = doctors.map(d=>{
            const dBk=confirmed.filter(b=>b.doctorId===d.doctorId);
            const rev=dBk.reduce((s,b)=>s+bookingFee(b,doctors),0);
            return {...d,count:dBk.length,rev,hName:hospitals.find(h=>h.hospitalId===d.hospitalId)?.name||'—'};
          }).filter(d=>d.count>0).sort((a,b)=>b.rev-a.rev).slice(0,10);

          // Specialization breakdown
          const specMap={};
          confirmed.forEach(b=>{
            const sp=b.doctorSpecialization||b.specialization||'General';
            specMap[sp]=(specMap[sp]||0)+1;
          });
          const specArr=Object.entries(specMap).sort((a,b)=>b[1]-a[1]).slice(0,8);
          const maxSpec=specArr[0]?.[1]||1;

          // Patient growth: unique patients per week last 8 weeks
          const weekGrowth=Array.from({length:8},(_,i)=>{
            const end=new Date(); end.setDate(end.getDate()-i*7);
            const start=new Date(end); start.setDate(start.getDate()-7);
            const wBk=bookings.filter(b=>{
              const t=new Date(b.createdAt||b.date||0);
              return t>=start&&t<end;
            });
            return {label:`W-${i===0?'This':i}`,count:new Set(wBk.map(b=>b.userId||b.patientPhone)).size,bk:wBk.length};
          }).reverse();

          const maxRev30=Math.max(...day30.map(d=>d.rev),1);

          return (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:10}}>
                <div>
                  <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>📊 Platform Analytics</h1>
                  <p style={{color:'var(--text-muted)',fontSize:13}}>Deep insights across all hospitals, bookings & patients</p>
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {[
                    {l:'Total Bookings',v:bookings.length,c:'#6366f1'},
                    {l:'Confirmed',v:confirmed.length,c:'#10b981'},
                    {l:'Cancel Rate',v:`${cancelRate}%`,c:cancelRate>30?'#ef4444':'#fbbf24'},
                    {l:'Today',v:todayBk.length,c:'#00d4aa'},
                  ].map(s=>(
                    <div key={s.l} style={{padding:'7px 13px',background:`${s.c}14`,border:`1px solid ${s.c}33`,borderRadius:20,fontSize:12,fontWeight:700,color:s.c,textAlign:'center'}}>
                      <div style={{fontSize:16,fontWeight:800}}>{s.v}</div>
                      <div style={{fontSize:10,opacity:.7}}>{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── ROW 1: Revenue KPIs ─────────────────────────────── */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:10,marginBottom:14}}>
                {[
                  {icon:'💰',label:'Gross Revenue',val:fmtR(totalRev),c:'#fbbf24',big:true},
                  {icon:'🏦',label:'Platform Earnings',val:fmtR(platFee),c:'#6366f1'},
                  {icon:'🏥',label:'Hospital Payout',val:fmtR(totalRev-platFee),c:'#10b981'},
                  {icon:'❌',label:'Lost Revenue',val:fmtR(totalLost),c:'#ef4444'},
                  {icon:'📅',label:"Today's Revenue",val:fmtR(analytics?.todayRevenue||0),c:'#00d4aa'},
                  {icon:'⏳',label:'Pending Value',val:fmtR(pending.reduce((s,b)=>s+bookingFee(b,doctors),0)),c:'#f59e0b'},
                ].map(k=>(
                  <div key={k.label} style={{background:`${k.c}0e`,border:`1px solid ${k.c}2a`,borderRadius:13,padding:'14px 15px'}}>
                    <div style={{fontSize:20,marginBottom:4}}>{k.icon}</div>
                    <div style={{fontSize:k.big?22:18,fontWeight:800,color:k.c,lineHeight:1.1}}>{k.val}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3}}>{k.label}</div>
                  </div>
                ))}
              </div>

              {/* ── ROW 2: Booking Funnel + Hourly Heat ─────────────── */}
              <div className="resp-grid-2col-equal" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>

                {/* Booking Funnel */}
                <div style={S}>
                  <div style={{...H2,marginBottom:14}}>🎯 Booking Funnel</div>
                  {[
                    {l:'Total Bookings',v:bookings.length,c:'#6366f1',pct:100},
                    {l:'Confirmed / Completed',v:confirmed.length,c:'#10b981',pct:bookings.length?Math.round(confirmed.length/bookings.length*100):0},
                    {l:'Pending',v:pending.length,c:'#f59e0b',pct:bookings.length?Math.round(pending.length/bookings.length*100):0},
                    {l:'Cancelled / Rejected',v:cancelled.length,c:'#ef4444',pct:bookings.length?Math.round(cancelled.length/bookings.length*100):0},
                  ].map(f=>(
                    <div key={f.l} style={{marginBottom:12}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:5,fontSize:12}}>
                        <span style={{color:'var(--text-secondary)',fontWeight:500}}>{f.l}</span>
                        <span style={{fontWeight:800,color:f.c}}>{f.v} <span style={{fontSize:10,fontWeight:400,color:'var(--text-muted)'}}>{f.pct}%</span></span>
                      </div>
                      <div style={{height:8,background:'var(--surface-muted)',borderRadius:6,overflow:'hidden'}}>
                        <div style={{height:'100%',background:f.c,borderRadius:6,width:`${f.pct}%`,transition:'width 0.5s'}}></div>
                      </div>
                    </div>
                  ))}
                  <div style={{marginTop:14,padding:'10px 14px',background:'var(--surface-subtle)',borderRadius:10,display:'flex',gap:20,flexWrap:'wrap'}}>
                    <div><div style={{fontSize:20,fontWeight:800,color:'#10b981'}}>{bookings.length?Math.round(confirmed.length/bookings.length*100):0}%</div><div style={{fontSize:10,color:'var(--text-muted)'}}>Conversion Rate</div></div>
                    <div><div style={{fontSize:20,fontWeight:800,color:'#ef4444'}}>{cancelRate}%</div><div style={{fontSize:10,color:'var(--text-muted)'}}>Cancellation Rate</div></div>
                    <div><div style={{fontSize:20,fontWeight:800,color:'#fbbf24'}}>{confirmed.length>0?fmtR(Math.round(totalRev/confirmed.length)):'₹0'}</div><div style={{fontSize:10,color:'var(--text-muted)'}}>Avg. Booking Value</div></div>
                  </div>
                </div>

                {/* 24h Peak Hour Heatmap */}
                <div style={S}>
                  <div style={{...H2,marginBottom:4}}>⏰ Peak Booking Hours (All Time)</div>
                  <p style={{fontSize:11,color:'var(--text-muted)',marginBottom:12}}>Peak: <strong style={{color:'#fbbf24'}}>{peakHour}:00 – {peakHour+1}:00</strong></p>
                  <MiniBar data={hourDist} color="#6366f1" height={70} labels={hourDist.map((v,i)=>`${i}:00 — ${v} bookings`)}/>
                  <div style={{display:'flex',justifyContent:'space-between',marginTop:5}}>
                    {['12AM','3AM','6AM','9AM','12PM','3PM','6PM','9PM'].map((l,i)=>(
                      <div key={i} style={{fontSize:9,color:'var(--text-muted)'}}>{l}</div>
                    ))}
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginTop:12}}>
                    {[
                      {l:'Morning 6–12',v:hourDist.slice(6,12).reduce((s,v)=>s+v,0),c:'#f59e0b'},
                      {l:'Afternoon 12–17',v:hourDist.slice(12,17).reduce((s,v)=>s+v,0),c:'#00d4aa'},
                      {l:'Evening 17–22',v:hourDist.slice(17,22).reduce((s,v)=>s+v,0),c:'#8b5cf6'},
                    ].map(s=>(
                      <div key={s.l} style={{background:`${s.c}14`,border:`1px solid ${s.c}33`,borderRadius:9,padding:'8px 10px',textAlign:'center'}}>
                        <div style={{fontSize:16,fontWeight:800,color:s.c}}>{s.v}</div>
                        <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>{s.l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── ROW 3: 30-Day Dual Chart ────────────────────────── */}
              <div style={{...S,marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
                  <div style={H2}>📈 30-Day Trend — Bookings & Revenue</div>
                  <div style={{display:'flex',gap:12,fontSize:11}}>
                    <span style={{color:'#6366f1'}}>■ Bookings</span>
                    <span style={{color:'#fbbf24'}}>■ Revenue</span>
                    <span style={{color:'#ef4444'}}>■ Cancelled</span>
                  </div>
                </div>

                {/* Booking bars */}
                <div style={{marginBottom:4}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4}}>BOOKINGS</div>
                  <MiniBar data={day30.map(d=>d.count)} color="#6366f1" height={50} labels={day30.map(d=>`${d.lbl}: ${d.count} bookings, ${d.confirmed} confirmed`)}/>
                </div>
                {/* Cancelled overlay */}
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4}}>CANCELLED</div>
                  <MiniBar data={day30.map(d=>d.cancelled)} color="#ef4444" height={30} labels={day30.map(d=>`${d.lbl}: ${d.cancelled} cancelled`)}/>
                </div>
                {/* Revenue bars */}
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4}}>REVENUE (₹)</div>
                  <MiniBar data={day30.map(d=>d.rev)} color="#fbbf24" height={40} labels={day30.map(d=>`${d.lbl}: ${fmtR(d.rev)}`)}/>
                </div>

                <div style={{display:'flex',justifyContent:'space-between',marginTop:4}}>
                  {day30.filter((_,i)=>i%5===0).map((d,i)=>(
                    <div key={i} style={{fontSize:9,color:'var(--border-color-hover)'}}>{d.lbl}</div>
                  ))}
                </div>

                <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8,marginTop:12}}>
                  {[
                    {l:'30d Bookings',v:day30.reduce((s,d)=>s+d.count,0),c:'#6366f1'},
                    {l:'30d Confirmed',v:day30.reduce((s,d)=>s+d.confirmed,0),c:'#10b981'},
                    {l:'30d Cancelled',v:day30.reduce((s,d)=>s+d.cancelled,0),c:'#ef4444'},
                    {l:'30d Revenue',v:fmtR(day30.reduce((s,d)=>s+d.rev,0)),c:'#fbbf24'},
                    {l:'Best Day',v:`${fmtR(maxRev30)} (${day30.find(d=>d.rev===maxRev30)?.lbl||'—'})`,c:'#00d4aa'},
                  ].map(s=>(
                    <div key={s.l} style={{background:`${s.c}10`,border:`1px solid ${s.c}25`,borderRadius:9,padding:'8px 10px'}}>
                      <div style={{fontSize:14,fontWeight:800,color:s.c}}>{s.v}</div>
                      <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── ROW 4: Patient Weekly Growth + Specialization ────── */}
              <div className="resp-grid-2col-equal" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>

                {/* Weekly patient growth */}
                <div style={S}>
                  <div style={{...H2,marginBottom:10}}>👥 Weekly Patient Activity</div>
                  <MiniBar data={weekGrowth.map(w=>w.bk)} color="#8b5cf6" height={55} labels={weekGrowth.map(w=>`${w.label}: ${w.bk} bookings, ${w.count} unique patients`)}/>
                  <div style={{display:'flex',justifyContent:'space-between',marginTop:5}}>
                    {weekGrowth.map((w,i)=>(
                      <div key={i} style={{fontSize:9,color:'var(--border-color-hover)',textAlign:'center',flex:1}}>{w.label}</div>
                    ))}
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginTop:12}}>
                    <div style={{background:'rgba(139,92,246,0.12)',border:'1px solid rgba(139,92,246,0.25)',borderRadius:9,padding:'9px 12px'}}>
                      <div style={{fontSize:18,fontWeight:800,color:'#8b5cf6'}}>{new Set(bookings.map(b=>b.userId||b.patientPhone)).size}</div>
                      <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>Total Unique Patients</div>
                    </div>
                    <div style={{background:'rgba(0,212,170,0.08)',border:'1px solid rgba(0,212,170,0.2)',borderRadius:9,padding:'9px 12px'}}>
                      <div style={{fontSize:18,fontWeight:800,color:'#00d4aa'}}>{weekGrowth[weekGrowth.length-1]?.count||0}</div>
                      <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>This Week Active</div>
                    </div>
                  </div>
                </div>

                {/* Top Specializations */}
                <div style={S}>
                  <div style={{...H2,marginBottom:12}}>🩺 Top Specializations (by Confirmed Bookings)</div>
                  {specArr.map(([sp,cnt],i)=>(
                    <div key={sp} style={{marginBottom:9}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:4,fontSize:12}}>
                        <span style={{color:'var(--text-secondary)',fontWeight:500,display:'flex',gap:7,alignItems:'center'}}>
                          <span style={{width:16,height:16,borderRadius:5,background:`hsl(${i*42},70%,55%)`,display:'inline-block',flexShrink:0}}></span>
                          {sp}
                        </span>
                        <span style={{fontWeight:700,color:'var(--text-primary)'}}>{cnt}</span>
                      </div>
                      <div style={{height:6,background:'var(--surface-muted)',borderRadius:4,overflow:'hidden'}}>
                        <div style={{height:'100%',background:`hsl(${i*42},70%,55%)`,borderRadius:4,width:`${Math.round(cnt/maxSpec*100)}%`,transition:'width 0.4s'}}></div>
                      </div>
                    </div>
                  ))}
                  {!specArr.length&&<div style={{fontSize:12,color:'var(--border-color-hover)'}}>No confirmed bookings yet</div>}
                </div>
              </div>

              {/* ── ROW 5: Hospital Performance Table ───────────────── */}
              <div style={{...S,marginBottom:12}}>
                <div style={{...H2,marginBottom:12}}>🏥 Hospital Performance Rankings</div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead>
                      <tr>{['Rank','Hospital','City','Doctors','Bookings','Confirmed','Cancel Rate','Revenue','Action'].map(c=>(
                        <th key={c} style={TH}>{c}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {hPerf.map((h,i)=>(
                        <tr key={h.hospitalId}
                          style={{cursor:'pointer',transition:'background 0.1s'}}
                          onMouseOver={e=>e.currentTarget.style.background='var(--surface-subtle)'}
                          onMouseOut={e=>e.currentTarget.style.background='transparent'}>
                          <td style={{...TD,textAlign:'center'}}>
                            <span style={{width:24,height:24,borderRadius:7,background:i<3?`rgba(245,158,11,${0.3-i*0.08})`:'var(--surface-muted)',display:'inline-flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:12,color:i<3?'#fbbf24':'var(--text-muted)'}}>{i+1}</span>
                          </td>
                          <td style={TD}>
                            <div style={{fontWeight:700,fontSize:13}}>{h.name}</div>
                            <Badge s={h.status||'active'}/>
                          </td>
                          <td style={TD}><span style={{fontSize:12,color:'var(--text-muted)'}}>{h.city}</span></td>
                          <td style={{...TD,textAlign:'center',color:'#00d4aa',fontWeight:700}}>{h.docCount}</td>
                          <td style={{...TD,textAlign:'center',fontWeight:700,color:'#6366f1'}}>{h.bkCount}</td>
                          <td style={{...TD,textAlign:'center',color:'#10b981',fontWeight:700}}>{h.confCount}</td>
                          <td style={{...TD,textAlign:'center'}}>
                            <span style={{fontWeight:700,color:h.cRate>30?'#ef4444':h.cRate>15?'#f59e0b':'#10b981'}}>{h.cRate}%</span>
                          </td>
                          <td style={{...TD,color:'#fbbf24',fontWeight:700}}>{fmtR(h.rev)}</td>
                          <td style={TD}>
                            <button onClick={()=>{setTab('hospitals');loadDetail(h);}} style={{padding:'4px 10px',background:'rgba(99,102,241,0.12)',border:'1px solid rgba(99,102,241,0.25)',borderRadius:7,color:'#818cf8',cursor:'pointer',fontSize:11,fontWeight:700}}>View →</button>
                          </td>
                        </tr>
                      ))}
                      {!hPerf.length&&<tr><td colSpan={9} style={{...TD,textAlign:'center',color:'var(--text-muted)',padding:24}}>No data yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── ROW 6: Doctor Leaderboard ────────────────────────── */}
              {dPerf.length>0&&(
                <div style={S}>
                  <div style={{...H2,marginBottom:12}}>👨‍⚕️ Doctor Revenue Leaderboard (Top 10)</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:9}}>
                    {dPerf.map((d,i)=>(
                      <div key={d.doctorId} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'var(--surface-subtle)',border:'1px solid var(--surface-muted)',borderRadius:11}}>
                        <div style={{width:28,height:28,borderRadius:8,background:i<3?'rgba(245,158,11,0.18)':'rgba(0,212,170,0.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800,color:i<3?'#fbbf24':'#00d4aa',flexShrink:0}}>{i+1}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:13,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.name}</div>
                          <div style={{fontSize:10,color:'var(--text-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.specialization} · {d.hName}</div>
                        </div>
                        <div style={{textAlign:'right',flexShrink:0}}>
                          <div style={{fontSize:13,fontWeight:800,color:'#fbbf24'}}>{fmtR(d.rev)}</div>
                          <div style={{fontSize:10,color:'var(--text-muted)'}}>{d.count} bookings</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── INTELLIGENCE ──────────────────────────────────────── */}
        {tab==='intelligence'&&(
          <div>
            <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>🔍 Patient Intelligence</h1>
            <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16}}>Complete lifetime view of any patient — every login, booking, hospital visit, and location</p>

            {/* Search bar */}
            <div style={{position:'relative',marginBottom:20}}>
              <MdSearch style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'#6366f1',fontSize:20}}/>
              <input
                value={intelQ}
                onChange={e=>setIntelQ(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&intelLookup(intelQ)}
                placeholder="Enter phone number (e.g. 9876543210) or patient name…"
                style={{width:'100%',padding:'14px 130px 14px 46px',background:'rgba(99,102,241,0.07)',border:'1px solid rgba(99,102,241,0.4)',borderRadius:12,color:'#fff',fontSize:15,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}
              />
              <button onClick={()=>intelLookup(intelQ)} disabled={intelLoading}
                style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',padding:'9px 18px',background:'linear-gradient(135deg,#6366f1,#4f46e5)',border:'none',borderRadius:9,color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:6}}>
                {intelLoading?'⏳ Loading…':<><MdInsights/> Investigate</>}
              </button>
            </div>

            {/* Quick suggestions from patients list */}
            {!intelResult&&!intelLoading&&(
              <div style={S}>
                <div style={{...H2,marginBottom:12}}>👥 Quick Access — All Patients</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:8}}>
                  {patients.filter(p=>p.bookingCount>0).sort((a,b)=>b.bookingCount-a.bookingCount).slice(0,12).map((p,i)=>(
                    <div key={i} onClick={()=>{setIntelQ(p.phone||p.patientName);intelLookup(p.phone||p.patientName);}}
                      style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'var(--surface-subtle)',border:'1px solid var(--surface-muted)',borderRadius:10,cursor:'pointer',transition:'border-color 0.15s'}}
                      onMouseOver={e=>e.currentTarget.style.borderColor='rgba(99,102,241,0.4)'}
                      onMouseOut={e=>e.currentTarget.style.borderColor='var(--surface-muted)'}>
                      <div style={{width:34,height:34,borderRadius:10,background:'rgba(99,102,241,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,fontWeight:800,color:'#818cf8',flexShrink:0}}>{(p.patientName||'?').charAt(0).toUpperCase()}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:13,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.patientName||'Unknown'}</div>
                        <div style={{fontSize:11,color:'var(--text-muted)'}}>{p.phone} · {p.bookingCount} bookings</div>
                      </div>
                      <div style={{textAlign:'right',flexShrink:0}}>
                        <div style={{fontSize:13,fontWeight:800,color:'#10b981'}}>{fmtR(p.totalSpent||0)}</div>
                        <div style={{fontSize:10,color:'var(--border-color-hover)'}}>{timeAgo(p.lastBooking)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {intelLoading&&<div style={{textAlign:'center',padding:60,color:'var(--text-muted)'}}>⏳ Loading intelligence data…</div>}

            {intelResult?.error&&(
              <div style={{...S,textAlign:'center',padding:40,border:'1px solid rgba(239,68,68,0.2)'}}>
                <div style={{fontSize:40,marginBottom:10}}>🔍</div>
                <div style={{fontWeight:700,marginBottom:6,color:'#f87171'}}>Not Found</div>
                <div style={{fontSize:13,color:'var(--text-muted)'}}>{intelResult.error}</div>
              </div>
            )}

            {intelResult&&!intelResult.error&&(()=>{
              const { profile, events, hospitalsVisited } = intelResult;
              const ITABS = ['timeline','hospitals','spending','pattern'];
              return (
                <div>
                  {/* Patient Identity Card */}
                  <div style={{background:'linear-gradient(135deg,rgba(99,102,241,0.12),rgba(139,92,246,0.08))',border:'1px solid rgba(99,102,241,0.3)',borderRadius:16,padding:'20px 24px',marginBottom:14}}>
                    <div style={{display:'flex',gap:16,alignItems:'flex-start',flexWrap:'wrap'}}>
                      <div style={{width:56,height:56,borderRadius:16,background:'rgba(99,102,241,0.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,fontWeight:800,color:'#818cf8',flexShrink:0}}>{(profile.name||'?').charAt(0).toUpperCase()}</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:22,fontWeight:800,marginBottom:4}}>{profile.name||'Unknown'}</div>
                        <div style={{display:'flex',gap:16,flexWrap:'wrap',fontSize:13,color:'var(--text-muted)'}}>
                          <span><MdPhone style={{fontSize:13,verticalAlign:'middle'}}/> {profile.phone}</span>
                          {profile.email&&profile.email!=='—'&&<span><MdEmail style={{fontSize:13,verticalAlign:'middle'}}/> {profile.email}</span>}
                          {profile.age&&profile.age!=='—'&&<span>Age: {profile.age}</span>}
                        </div>
                        <div style={{display:'flex',gap:10,flexWrap:'wrap',marginTop:8}}>
                          {profile.cognitoStatus&&profile.cognitoStatus!=='—'&&<Badge s={profile.cognitoStatus==='CONFIRMED'?'confirmed':'pending'} text={profile.cognitoStatus==='CONFIRMED'?'✓ App Active':'Inactive'}/>}
                          {profile.registeredAt&&<span style={{fontSize:11,color:'var(--text-muted)'}}>📱 Registered: {fmtD(profile.registeredAt)}</span>}
                          {profile.lastActivity&&<span style={{fontSize:11,color:'var(--text-muted)'}}>🕐 Last seen: {timeAgo(profile.lastActivity)}</span>}
                        </div>
                      </div>
                      {/* KPI pills */}
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {[
                          {label:'Total OPs',val:profile.totalBookings,c:'#6366f1'},
                          {label:'Confirmed',val:profile.confirmedBookings,c:'#10b981'},
                          {label:'Cancelled',val:profile.cancelledBookings,c:'#ef4444'},
                          {label:'Lifetime Spend',val:fmtR(profile.totalSpent),c:'#fbbf24'},
                          {label:'Hospitals',val:profile.uniqueHospitals,c:'#00d4aa'},
                          {label:'Emergencies',val:profile.totalEmergencies,c:'#f87171'},
                        ].map(k=>(
                          <div key={k.label} style={{textAlign:'center',padding:'9px 14px',background:`${k.c}14`,border:`1px solid ${k.c}33`,borderRadius:12,minWidth:72}}>
                            <div style={{fontSize:18,fontWeight:800,color:k.c,lineHeight:1}}>{k.val}</div>
                            <div style={{fontSize:10,color:'var(--text-muted)',marginTop:3}}>{k.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Preferences */}
                    <div style={{display:'flex',gap:16,marginTop:14,flexWrap:'wrap',fontSize:12,borderTop:'1px solid var(--surface-muted)',paddingTop:12}}>
                      <span style={{color:'var(--text-muted)'}}>⭐ Preferred Hospital: <strong style={{color:'#f1f5f9'}}>{profile.preferredHospital}</strong></span>
                      {profile.preferredDoctor&&profile.preferredDoctor!=='—'&&<span style={{color:'var(--text-muted)'}}>👨‍⚕️ Preferred Doctor: <strong style={{color:'#f1f5f9'}}>{profile.preferredDoctor}</strong></span>}
                      {profile.cancelRate>0&&<span style={{color:profile.cancelRate>40?'#f87171':'var(--text-muted)'}}>❌ Cancel Rate: <strong style={{color:profile.cancelRate>40?'#f87171':'#f1f5f9'}}>{profile.cancelRate}%</strong></span>}
                    </div>
                  </div>

                  {/* Intelligence sub-tabs */}
                  <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
                    {ITABS.map(t=>(
                      <button key={t} onClick={()=>setIntelTab(t)} style={{padding:'7px 16px',borderRadius:20,border:`1px solid ${intelTab===t?'rgba(99,102,241,0.6)':'var(--border-color-hover)'}`,cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:intelTab===t?700:400,background:intelTab===t?'rgba(99,102,241,0.18)':'var(--surface-subtle)',color:intelTab===t?'#818cf8':'var(--text-muted)',textTransform:'capitalize'}}>
                        {t==='timeline'?'📋 Full Timeline':t==='hospitals'?'🏥 Hospitals Trail':t==='spending'?'💰 Spend Profile':'📊 Booking Pattern'}
                      </button>
                    ))}
                  </div>

                  {/* TIMELINE */}
                  {intelTab==='timeline'&&(
                    <div>
                      {events.length===0&&<div style={{...S,textAlign:'center',padding:30,color:'var(--text-muted)'}}>No events found</div>}
                      {events.map((ev,i)=>(
                        <div key={i} style={{display:'flex',gap:12,marginBottom:10}}>
                          {/* Timeline spine */}
                          <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:0,width:36,flexShrink:0}}>
                            <div style={{width:36,height:36,borderRadius:12,background:`${ev.color}22`,border:`2px solid ${ev.color}66`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>{ev.icon}</div>
                            {i<events.length-1&&<div style={{width:2,flex:1,background:'var(--surface-muted)',minHeight:12,marginTop:4}}/>}
                          </div>
                          <div style={{flex:1,background:'var(--surface-subtle)',border:`1px solid ${ev.color}22`,borderRadius:12,padding:'12px 15px',marginBottom:4}}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8,marginBottom:6}}>
                              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                <span style={{fontWeight:700,fontSize:14,color:ev.color}}>{ev.title}</span>
                                {ev.status&&<Badge s={ev.status}/>}
                                {ev.fee>0&&<span style={{fontSize:13,fontWeight:800,color:'#fbbf24'}}>{fmtR(ev.fee)}</span>}
                              </div>
                              <div style={{fontSize:11,color:'var(--text-muted)',flexShrink:0}}>
                                <MdAccessTime style={{fontSize:11,verticalAlign:'middle'}}/> {ev.timestamp ? new Date(ev.timestamp).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}
                              </div>
                            </div>
                            <div style={{fontSize:12,color:'var(--text-muted)',lineHeight:1.5}}>{ev.description}</div>
                            {ev.type==='booking'&&(
                              <div style={{display:'flex',gap:14,marginTop:8,flexWrap:'wrap',fontSize:11,color:'var(--text-muted)'}}>
                                {ev.slot&&ev.slot!=='—'&&<span>🕐 Slot: {ev.slot}</span>}
                                {ev.doctorName&&<span>👨‍⚕️ {ev.doctorName}</span>}
                                {ev.otp&&<span style={{color:'#fbbf24',fontWeight:700}}>🔐 Check-in OTP: {ev.otp}</span>}
                                {ev.hospitalCity&&<span><MdLocationOn style={{fontSize:11,verticalAlign:'middle'}}/> {ev.hospitalCity}</span>}
                                {ev.lat&&ev.lng&&<a href={`https://maps.google.com/?q=${ev.lat},${ev.lng}`} target="_blank" rel="noreferrer" style={{color:'#60a5fa',textDecoration:'none',display:'flex',alignItems:'center',gap:3}}>🗺️ View on Map</a>}
                              </div>
                            )}
                            {ev.type==='emergency'&&ev.address&&<div style={{marginTop:6,fontSize:11,color:'#f87171'}}>📍 Location: {ev.address}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* HOSPITALS TRAIL */}
                  {intelTab==='hospitals'&&(
                    <div>
                      {hospitalsVisited.length===0&&<div style={{...S,textAlign:'center',padding:30,color:'var(--text-muted)'}}>No hospital visits found</div>}
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:10}}>
                        {hospitalsVisited.map((h,i)=>(
                          <div key={i} style={{background:'var(--surface-subtle)',border:'1px solid rgba(0,212,170,0.2)',borderRadius:14,padding:'16px 18px'}}>
                            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                              <div style={{width:36,height:36,borderRadius:10,background:'rgba(0,212,170,0.12)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>🏥</div>
                              <div style={{flex:1}}>
                                <div style={{fontWeight:700,fontSize:14}}>{h.name}</div>
                                <div style={{fontSize:11,color:'var(--text-muted)'}}><MdLocationOn style={{fontSize:10,verticalAlign:'middle'}}/> {h.city}</div>
                              </div>
                              {i===0&&<span style={{fontSize:10,color:'#fbbf24',fontWeight:700,background:'rgba(245,158,11,0.1)',padding:'2px 8px',borderRadius:20}}>⭐ Favourite</span>}
                            </div>
                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6}}>
                              {[
                                {l:'Visits',v:h.visits,c:'#6366f1'},
                                {l:'Spent',v:fmtR(h.totalSpent),c:'#10b981'},
                                {l:'Last Visit',v:timeAgo(h.lastVisit),c:'#00d4aa'},
                              ].map(s=>(
                                <div key={s.l} style={{background:`${s.c}14`,borderRadius:8,padding:'7px 8px',textAlign:'center'}}>
                                  <div style={{fontSize:13,fontWeight:800,color:s.c}}>{s.v}</div>
                                  <div style={{fontSize:9,color:'var(--text-muted)',marginTop:1}}>{s.l}</div>
                                </div>
                              ))}
                            </div>
                            {h.lat&&h.lng&&(
                              <a href={`https://maps.google.com/?q=${h.lat},${h.lng}`} target="_blank" rel="noreferrer"
                                style={{display:'flex',alignItems:'center',gap:5,marginTop:10,fontSize:11,color:'#60a5fa',textDecoration:'none'}}>
                                🗺️ View on Google Maps →
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* SPENDING PROFILE */}
                  {intelTab==='spending'&&(
                    <div className="resp-grid-2col-equal" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                      <div style={S}>
                        <div style={H2}>💰 Spend Breakdown</div>
                        {[
                          {l:'Lifetime Total',v:fmtR(profile.totalSpent),c:'#fbbf24',big:true},
                          {l:'Per Booking (avg)',v:profile.confirmedBookings>0?fmtR(Math.round(profile.totalSpent/profile.confirmedBookings)):'₹0',c:'#10b981'},
                          {l:'Cancel Rate',v:`${profile.cancelRate}%`,c:profile.cancelRate>40?'#ef4444':'#94a3b8'},
                          {l:'Confirmed OPs',v:profile.confirmedBookings,c:'#10b981'},
                          {l:'Cancelled OPs',v:profile.cancelledBookings,c:'#ef4444'},
                          {l:'Emergencies',v:profile.totalEmergencies,c:'#f87171'},
                        ].map(r=>(
                          <div key={r.l} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 0',borderBottom:'1px solid var(--surface-subtle)'}}>
                            <span style={{fontSize:13,color:'var(--text-muted)'}}>{r.l}</span>
                            <span style={{fontSize:r.big?20:15,fontWeight:800,color:r.c}}>{r.v}</span>
                          </div>
                        ))}
                      </div>
                      <div style={S}>
                        <div style={H2}>🏥 Spend Per Hospital</div>
                        {hospitalsVisited.map((h,i)=>(
                          <div key={i} style={{marginBottom:10}}>
                            <div style={{display:'flex',justifyContent:'space-between',marginBottom:3,fontSize:12}}>
                              <span style={{color:'var(--text-primary)',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,marginRight:8}}>{h.name}</span>
                              <span style={{color:'#fbbf24',fontWeight:700,flexShrink:0}}>{fmtR(h.totalSpent)}</span>
                            </div>
                            <div style={{height:6,background:'var(--surface-muted)',borderRadius:3,overflow:'hidden'}}>
                              <div style={{height:'100%',background:'#fbbf24',borderRadius:3,width:`${Math.round(h.totalSpent/(profile.totalSpent||1)*100)}%`,transition:'width 0.4s'}}/>
                            </div>
                            <div style={{fontSize:10,color:'var(--border-color-hover)',marginTop:2}}>{h.visits} visits · {h.city}</div>
                          </div>
                        ))}
                        {hospitalsVisited.length===0&&<div style={{fontSize:12,color:'var(--border-color-hover)'}}>No paid bookings yet</div>}
                      </div>
                    </div>
                  )}

                  {/* BOOKING PATTERN */}
                  {intelTab==='pattern'&&(
                    <div style={S}>
                      <div style={H2}>📊 Booking Time Pattern (24h)</div>
                      <p style={{fontSize:12,color:'var(--text-muted)',marginBottom:14}}>Which hours of the day this patient typically books — hover bars for detail</p>
                      <MiniBar data={profile.hourPattern||Array(24).fill(0)} color="#6366f1" height={80} labels={(profile.hourPattern||[]).map((v,i)=>`${i}:00 — ${v} bookings`)}/>
                      <div style={{display:'flex',justifyContent:'space-between',marginTop:6}}>
                        {['12A','2A','4A','6A','8A','10A','12P','2P','4P','6P','8P','10P'].map((l,i)=>(
                          <div key={i} style={{fontSize:9,color:'var(--text-muted)'}}>{l}</div>
                        ))}
                      </div>
                      <div style={{marginTop:16,display:'flex',gap:12,flexWrap:'wrap'}}>
                        {(()=>{
                          const hp = profile.hourPattern||[];
                          const peak = hp.indexOf(Math.max(...hp));
                          const morning = hp.slice(6,12).reduce((s,v)=>s+v,0);
                          const afternoon = hp.slice(12,17).reduce((s,v)=>s+v,0);
                          const evening = hp.slice(17,22).reduce((s,v)=>s+v,0);
                          return [
                            {l:'Peak Hour',v:`${peak}:00`,c:'#fbbf24'},
                            {l:'Morning (6-12)',v:morning,c:'#f59e0b'},
                            {l:'Afternoon (12-17)',v:afternoon,c:'#00d4aa'},
                            {l:'Evening (17-22)',v:evening,c:'#8b5cf6'},
                          ].map(s=>(
                            <div key={s.l} style={{padding:'8px 14px',background:`${s.c}14`,border:`1px solid ${s.c}33`,borderRadius:10,textAlign:'center'}}>
                              <div style={{fontSize:16,fontWeight:800,color:s.c}}>{s.v}</div>
                              <div style={{fontSize:10,color:'var(--text-muted)',marginTop:2}}>{s.l}</div>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ── DOCTORS (all) with revenue ──────────────────────── */}
        {tab==='doctors'&&(
          <div>
            <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>All Doctors 👨‍⚕️</h1>
            <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16}}>{doctors.length} across all hospitals</p>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr>{['Doctor','Specialization','Hospital','Fee/Visit','Bookings','Revenue Earned','Cancelled','Availability',''].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                <tbody>
                  {filt(doctors,['name','specialization']).map(d=>{
                    const dBookings  = bookings.filter(b=>b.doctorId===d.doctorId);
                    const dConfirmed = dBookings.filter(b=>b.status==='confirmed'||b.status==='completed'||b.status==='accepted'||b.status==='checked_in');
                    const dCancelled = dBookings.filter(b=>b.status==='cancelled');
                    const dRevenue   = dConfirmed.reduce((s,b)=>s+bookingFee(b,doctors),0);
                    // Hospital column resolution order:
                    //   1. Match by current hospitalId in the loaded Hospitals list.
                    //   2. Fall back to the doctor record's own snapshot
                    //      (`hospitalName`) — newly written by the lambdas.
                    //   3. If hospitalId exists but no match anywhere, label
                    //      the row "(deleted hospital)" instead of "—" so
                    //      admins know it's an orphan, not a missing field.
                    const hospital = hospitals.find(h=>h.hospitalId===d.hospitalId);
                    const hospitalLabel = hospital?.name
                      || d.hospitalName
                      || (d.hospitalId ? '(deleted hospital)' : '—');
                    const isOrphan = !hospital && !!d.hospitalId && !d.hospitalName;
                    const fee = Number(d.consultationFee || d.fee || 0);
                    return (
                      <tr key={d.doctorId}>
                        <td style={TD}>
                          <div style={{fontWeight:700}}>{d.name}</div>
                          {d.phone&&<div style={{fontSize:11,color:'var(--text-muted)'}}>{d.phone}</div>}
                        </td>
                        <td style={TD}>{d.specialization||'—'}{d.experience&&<span style={{fontSize:11,color:'var(--text-muted)'}}> · {d.experience}</span>}</td>
                        <td style={TD}>
                          <span style={{fontSize:12,color:isOrphan?'#f87171':undefined}}>{hospitalLabel}</span>
                        </td>
                        <td style={{...TD,color:'#fbbf24',fontWeight:700}}>{fee>0?fmtR(fee):'—'}</td>
                        <td style={{...TD,color:'#818cf8',fontWeight:700,textAlign:'center'}}>{dConfirmed.length}</td>
                        <td style={{...TD,color:'#10b981',fontWeight:700}}>{fmtR(dRevenue)}</td>
                        <td style={{...TD,color:'#f87171',fontWeight:700,textAlign:'center'}}>{dCancelled.length}</td>
                        <td style={TD}><Badge s={isDoctorAvailable(d)?'available':'unavailable'}/></td>
                        <td style={TD}>
                          <div style={{display:'flex',gap:5}}>
                            <button onClick={()=>toggleDoctor(d)} style={{padding:'6px 9px',background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:7,color:'#818cf8',cursor:'pointer',fontSize:17,display:'flex',alignItems:'center'}}>{isDoctorAvailable(d)?<MdToggleOn/>:<MdToggleOff/>}</button>
                            <button onClick={()=>setConfirmDel({type:'doctor',item:d})} style={{padding:'6px 9px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:7,color:'#f87171',cursor:'pointer',fontSize:17,display:'flex',alignItems:'center'}}><MdDelete/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── REVENUE ─────────────────────────────────────────────── */}
        {tab==='revenue'&&<RevenueTab bookings={bookings} doctors={doctors} hospitals={hospitals} analytics={analytics} bookingFee={bookingFee} setTab={setTab} loadDetail={loadDetail} S={S} H2={H2} TH={TH} TD={TD}/>}

        {/* OLD REVENUE IIFE — being removed, replaced by RevenueTab above */}
        {false/*DEAD_START_REVENUE*/&&(()=>{
          // Dynamic commission from platform settings (fetched once per render)
          const [revCfg, setRevCfg] = React.useState({ defaultCommissionPct: 2, defaultGstPct: 18 });
          const [revHospComm, setRevHospComm] = React.useState([]);
          React.useEffect(()=>{
            (async()=>{
              const [cfgR, hcR] = await Promise.all([
                adminAPI.getPlatformSettings().catch(()=>({})),
                adminAPI.getHospitalCommissions().catch(()=>({})),
              ]);
              if(cfgR.data) setRevCfg(cfgR.data);
              if(hcR.data?.hospitals) setRevHospComm(hcR.data.hospitals);
            })();
          },[]);
          const PLATFORM_FEE_PCT = Number(revCfg.defaultCommissionPct)||2;
          const GST_PCT          = Number(revCfg.defaultGstPct)||18;
          const round2 = (n) => Math.round((Number(n)||0) * 100) / 100;
          const isOpI = b => { const t = String(b?.type || '').toLowerCase(); return t !== 'sos' && t !== 'emergency'; };
          const isEarnedI = b => isOpI(b) && ['checked_in','completed'].includes(String(b?.status || '').toLowerCase());
          const earnedI  = bookings.filter(isEarnedI);
          const cancelled  = bookings.filter(b=>b.status==='cancelled');
          const noShows    = bookings.filter(b=>b.status==='no_show');
          const totalRev   = earnedI.reduce((s,b)=>s+bookingFee(b,doctors),0);
          const totalLost  = cancelled.reduce((s,b)=>s+bookingFee(b,doctors),0);
          const platformFee= round2(totalRev * PLATFORM_FEE_PCT / 100);
          const gstOnFee   = round2(platformFee * GST_PCT / 100);
          const totalDed   = round2(platformFee + gstOnFee);
          const payoutTotal= round2(totalRev - totalDed);

          // Helper: get effective commission for a hospital (per-hospital override > platform default)
          const getHospRate = (hospitalId) => {
            const hc = revHospComm.find(x=>x.hospitalId===hospitalId);
            if(!hc) return { cPct: PLATFORM_FEE_PCT, gPct: GST_PCT };
            return { cPct: hc.isWaived ? 0 : (hc.commissionPct ?? PLATFORM_FEE_PCT), gPct: hc.gstExempt ? 0 : (hc.gstPct ?? GST_PCT) };
          };

          // Per hospital breakdown (using per-hospital commission rates)
          const hStats = hospitals.map(h=>{
            const hB  = earnedI.filter(b=>b.hospitalId===h.hospitalId);
            const hC  = cancelled.filter(b=>b.hospitalId===h.hospitalId);
            const hNS = noShows.filter(b=>b.hospitalId===h.hospitalId);
            const rev = hB.reduce((s,b)=>s+bookingFee(b,doctors),0);
            const lost= hC.reduce((s,b)=>s+bookingFee(b,doctors),0);
            const { cPct, gPct } = getHospRate(h.hospitalId);
            const fee = round2(rev * cPct / 100);
            const gst = round2(fee * gPct / 100);
            const ded = round2(fee + gst);
            const settled = Number(h.totalSettled||0);
            const due = Math.max(0, round2((rev - ded) - settled));
            return {...h, confirmedCount:hB.length, cancelledCount:hC.length, noShowCount:hNS.length, revenue:rev, lostRevenue:lost, platformFee:fee, gstOnFee:gst, commPct:cPct, gstPct:gPct, payout:round2(rev-ded), settled, due};
          }).sort((a,b)=>b.revenue-a.revenue);

          // Per doctor breakdown
          const dStats = doctors.map(d=>{
            const dB  = earnedI.filter(b=>b.doctorId===d.doctorId);
            const dC  = cancelled.filter(b=>b.doctorId===d.doctorId);
            const rev = dB.reduce((s,b)=>s+bookingFee(b,doctors),0);
            return {...d, confirmedCount:dB.length, cancelledCount:dC.length, revenue:rev,
              hospitalName: (hospitals.find(h=>h.hospitalId===d.hospitalId)?.name)
                || d.hospitalName
                || (d.hospitalId ? '(deleted hospital)' : '—')};
          }).filter(d=>d.confirmedCount>0).sort((a,b)=>b.revenue-a.revenue);

          const maxRev = Math.max(...hStats.map(h=>h.revenue),1);

          return (
            <div>
              <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>Revenue & Settlement 💰</h1>
              <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16}}>Platform financial overview · {PLATFORM_FEE_PCT}% commission + {GST_PCT}% GST on commission only</p>

              {/* Top stats */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:11,marginBottom:18}}>
                {[
                  {icon:'💰',label:'Gross Revenue',val:fmtR(totalRev),c:'#fbbf24'},
                  {icon:'🏦',label:`Platform Fee (${PLATFORM_FEE_PCT}%)`,val:fmtR(platformFee),c:'#6366f1'},
                  {icon:'🧾',label:`GST (${GST_PCT}% on fee)`,val:fmtR(gstOnFee),c:'#a78bfa'},
                  {icon:'🏥',label:'Hospital Payout',val:fmtR(payoutTotal),c:'#10b981'},
                  {icon:'❌',label:'Lost (Cancelled)',val:fmtR(totalLost),c:'#ef4444'},
                  {icon:'⚠️',label:'No-Shows',val:String(noShows.length),c:'#f59e0b'},
                  {icon:'📅',label:'Today',val:fmtR(analytics?.todayRevenue||0),c:'#00d4aa'},
                  {icon:'💳',label:'Outstanding Dues',val:fmtR(hStats.reduce((s,h)=>s+h.due,0)),c:'#f59e0b'},
                ].map(x=>(
                  <div key={x.label} style={{background:'var(--surface-subtle)',border:`1px solid ${x.c}22`,borderRadius:12,padding:'12px 15px'}}>
                    <div style={{fontSize:18}}>{x.icon}</div>
                    <div style={{fontSize:20,fontWeight:800,color:x.c,marginTop:4}}>{x.val}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>{x.label}</div>
                  </div>
                ))}
              </div>

              {/* Hospital settlement table */}
              <div style={{...S,marginBottom:16}}>
                <div style={{...H2,marginBottom:12}}>🏥 Hospital Settlement — click hospital name to manage</div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr>{['Hospital','Confirmed','Cancelled','No-Shows','Gross Revenue','Comm %','Platform Fee','GST','Payout','Settled','Due',''].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                    <tbody>
                      {hStats.map(h=>(
                        <tr key={h.hospitalId}>
                          <td style={TD}>
                            <span style={{fontWeight:700,cursor:'pointer',color:'#60a5fa'}} onClick={()=>{setTab('hospitals');loadDetail(h);}}>{h.name}</span>
                            <div style={{fontSize:11,color:'var(--text-muted)'}}>{h.city}</div>
                          </td>
                          <td style={{...TD,color:'#10b981',fontWeight:700,textAlign:'center'}}>{h.confirmedCount}</td>
                          <td style={{...TD,color:'#f87171',fontWeight:700,textAlign:'center'}}>{h.cancelledCount}</td>
                          <td style={{...TD,color:'#f59e0b',textAlign:'center'}}>{h.noShowCount||0}</td>
                          <td style={{...TD,color:'#fbbf24',fontWeight:700}}>{fmtR(h.revenue)}</td>
                          <td style={TD}><span style={{fontSize:11,color:h.commPct===0?'#34d399':'#818cf8',fontWeight:h.commPct!==PLATFORM_FEE_PCT?700:400}}>{h.commPct===0?'Waived':h.commPct+'%'}{h.gstPct===0?' (GST-free)':''}</span></td>
                          <td style={{...TD,color:'#818cf8'}}>{fmtR(h.platformFee)}</td>
                          <td style={{...TD,color:'#c4b5fd'}}>{fmtR(h.gstOnFee)}</td>
                          <td style={{...TD,color:'#10b981',fontWeight:700}}>{fmtR(h.payout)}</td>
                          <td style={{...TD,color:'#34d399'}}>{fmtR(h.settled)}</td>
                          <td style={TD}>
                            <span style={{fontWeight:700,color:h.due>0?'#f59e0b':'#10b981'}}>
                              {h.due>0?`⚠️ ${fmtR(h.due)}`:'✅ Settled'}
                            </span>
                          </td>
                          <td style={TD}>
                            <button onClick={()=>{setTab('hospitals');loadDetail(h);}} style={{padding:'5px 10px',background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:7,color:'#818cf8',cursor:'pointer',fontSize:11,fontWeight:700}}>Settle →</button>
                          </td>
                        </tr>
                      ))}
                      {!hStats.length&&<tr><td colSpan={10} style={{...TD,textAlign:'center',color:'var(--text-muted)',padding:24}}>No data</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Per-doctor revenue table */}
              {dStats.length>0&&(
                <div style={S}>
                  <div style={{...H2,marginBottom:12}}>👨‍⚕️ Revenue by Doctor</div>
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Doctor','Specialization','Hospital','Fee/Visit','Bookings','Cancelled','Revenue Earned'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                      <tbody>
                        {dStats.map(d=>(
                          <tr key={d.doctorId}>
                            <td style={{...TD,fontWeight:700}}>{d.name}</td>
                            <td style={TD}>{d.specialization}</td>
                            <td style={TD}>{d.hospitalName}</td>
                            <td style={{...TD,color:'#fbbf24'}}>{fmtR(d.consultationFee||d.fee||0)}</td>
                            <td style={{...TD,color:'#818cf8',fontWeight:700,textAlign:'center'}}>{d.confirmedCount}</td>
                            <td style={{...TD,color:'#f87171',textAlign:'center'}}>{d.cancelledCount}</td>
                            <td style={{...TD,color:'#10b981',fontWeight:700}}>{fmtR(d.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Revenue bar chart by hospital */}
              <div style={S}>
                <div style={H2}>Revenue Distribution</div>
                {hStats.map(h=>(
                  <div key={h.hospitalId} style={{marginBottom:14}}>
                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                      <span style={{fontSize:13,fontWeight:600,cursor:'pointer'}} onClick={()=>{setTab('hospitals');loadDetail(h);}}>{h.name}</span>
                      <div style={{display:'flex',gap:14,fontSize:12}}>
                        <span style={{color:'#10b981'}}>{fmtR(h.revenue)} earned</span>
                        {h.lostRevenue>0&&<span style={{color:'#f87171'}}>-{fmtR(h.lostRevenue)} lost</span>}
                      </div>
                    </div>
                    <div style={{height:7,background:'var(--surface-muted)',borderRadius:10,overflow:'hidden',position:'relative'}}>
                      <div style={{position:'absolute',height:'100%',width:`${Math.round(h.revenue/maxRev*100)}%`,background:'linear-gradient(90deg,#f59e0b,#d97706)',borderRadius:10,transition:'width 0.4s'}}/>
                    </div>
                    <div style={{display:'flex',gap:14,marginTop:4,fontSize:11,color:'var(--text-muted)'}}>
                      <span>✅ {h.confirmedCount} confirmed</span>
                      <span>❌ {h.cancelledCount} cancelled</span>
                      <span>Payout: {fmtR(h.payout)}</span>
                      {h.due>0&&<span style={{color:'#f59e0b'}}>Due: {fmtR(h.due)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── HEALTH OFFERS ──────────────────────────────────────── */}
        {tab==='offers'&&<AdminOffersTab />}

        {/* ── EMERGENCIES ───────────────────────────────────────── */}
        {tab==='emergencies'&&(
          <div>
            <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>Emergency Alerts 🚨</h1>
            <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16}}>{activeEms} active · {emergencies.length} total</p>
            <div style={{display:'grid',gap:9}}>
              {filt(emergencies,['patientName','type']).map(e=>(
                <div key={e.alertId} style={{background:'var(--surface-subtle)',border:`1px solid ${sc(e.status)}33`,borderRadius:12,padding:'13px 17px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                  <div style={{fontSize:22}}>{e.status==='new'?'🚨':e.status==='acknowledged'?'⚠️':'✅'}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:14}}>{e.patientName||'Unknown'}</div>
                    <div style={{fontSize:12,color:'var(--text-muted)'}}>{e.type||'Emergency'} · {hospitals.find(h=>h.hospitalId===e.hospitalId)?.name||'—'} · {fmtD(e.createdAt)}</div>
                  </div>
                  <Badge s={e.status}/>
                  <div style={{display:'flex',gap:5}}>
                    {e.status==='new'&&<button onClick={()=>updateEm(e,'acknowledged')} style={{padding:'5px 10px',background:'rgba(245,158,11,0.15)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:7,color:'#fbbf24',cursor:'pointer',fontSize:12,fontWeight:600}}>Acknowledge</button>}
                    {e.status!=='handled'&&<button onClick={()=>updateEm(e,'handled')} style={{padding:'5px 10px',background:'rgba(16,185,129,0.15)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:7,color:'#34d399',cursor:'pointer',fontSize:12,fontWeight:600}}>Mark Handled</button>}
                  </div>
                </div>
              ))}
              {!filt(emergencies,['patientName','type']).length&&<div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)'}}>No emergencies</div>}
            </div>
          </div>
        )}

        {/* ── TRANSACTIONS ────────────────────────────────────────── */}
        {tab==='transactions' && (
          <AdminTransactions adminEmail={admin?.email || 'admin@hcen.in'} toast={toast_} />
        )}

        {/* ── REFUNDS (top-level since 28-May-2026) ───────────────── */}
        {tab==='refunds' && (
          <AdminRefunds adminEmail={admin?.email || 'admin@hcen.in'} toast={toast_} />
        )}

        {/* ── SUPPORT TICKETS ──────────────────────────────────────── */}
        {tab==='tickets' && (
          <AdminTickets embedded onBack={()=>setTab('dashboard')} />
        )}

        {/* ── REFER & EARN ─────────────────────────────────────────── */}
        {tab==='referrals' && (
          <AdminReferrals embedded onBack={()=>setTab('dashboard')} />
        )}

        {/* ── SETTLEMENTS ─────────────────────────────────────────── */}
        {tab==='settlements'&&(()=>{
          const fmtRS=(n)=>`₹${(Number(n)||0).toLocaleString('en-IN')}`;
          const fmtDT=(s)=>s?new Date(s).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',hour12:true}):'—';

          const doAction = async(req, status)=>{
            if(status==='disbursed'&&!txnRef[req.requestId]?.trim()){
              toast_('UTR / transaction reference is required to mark disbursed',true);
              return;
            }
            setDisbursing(p=>({...p,[req.requestId]:true}));
            const updates={status,adminNote:disburseNote[req.requestId]||''};
            if(status==='disbursed'){
              updates.disbursedAt=new Date().toISOString();
              updates.transactionRef=txnRef[req.requestId].trim();
            }
            const r=await adminSettlementAPI.update(req.requestId, updates);
            if(r.error){toast_(r.error,true);}else{
              setSettReqs(p=>p.map(x=>x.requestId===req.requestId?{...x,...updates}:x));
              toast_(status==='disbursed'?'✅ Marked Disbursed!':status==='processing'?'🔄 Marked Processing':'❌ Rejected');
            }
            setDisbursing(p=>({...p,[req.requestId]:false}));
          };

          const STABS=[
            {k:'all',label:'All',c:'#94a3b8'},
            {k:'pending',label:'⏳ Pending',c:'#f59e0b'},
            {k:'processing',label:'🔄 Processing',c:'#818cf8'},
            {k:'disbursed',label:'✅ Disbursed',c:'#10b981'},
            {k:'rejected',label:'❌ Rejected',c:'#f87171'},
          ];
          const today=(() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(); // PRODUCTION FIX: local date, not UTC
          const todayDisbAmt=settStats.todayDisbursed||settReqs.filter(r=>r.status==='disbursed'&&(r.disbursedAt||'').startsWith(today)).reduce((s,r)=>s+Number(r.amount||0),0);
          const totalDisbAmt=settStats.totalDisbursed||settReqs.filter(r=>r.status==='disbursed').reduce((s,r)=>s+Number(r.amount||0),0);
          const totalPendAmt=settStats.totalPending||settReqs.filter(r=>r.status==='pending').reduce((s,r)=>s+Number(r.amount||0),0);
          const displayed=settTab==='all'?settReqs:settReqs.filter(r=>r.status===settTab);

          return (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10,marginBottom:18}}>
                <div>
                  <h1 style={{fontSize:22,fontWeight:800,margin:0}}>Settlement Requests 🏦</h1>
                  <p style={{color:'var(--text-muted)',fontSize:13,marginTop:4,marginBottom:0}}>Hospital payout requests — review, disburse, track all payments</p>
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <button onClick={()=>runFridayAutoPayout({ dryRun:true })}
                    style={{padding:'8px 14px',background:'rgba(99,102,241,0.10)',border:'1px solid rgba(99,102,241,0.3)',borderRadius:9,color:'#a5b4fc',cursor:'pointer',fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:5,fontFamily:'inherit'}}
                    title="Preview which hospitals would get paid this Friday — no rows are written."
                  >
                    🔍 Preview Friday Auto-Payout
                  </button>
                  <button onClick={()=>runFridayAutoPayout({ dryRun:false })}
                    style={{padding:'8px 14px',background:'linear-gradient(135deg,#10b981,#059669)',border:'none',borderRadius:9,color:'#fff',cursor:'pointer',fontSize:13,fontWeight:700,display:'flex',alignItems:'center',gap:5,fontFamily:'inherit'}}
                    title="Run the weekly auto-payout job now. Idempotent per ISO week — safe to re-click."
                  >
                    🗓️ Run Friday Auto-Payout
                  </button>
                  <button onClick={()=>reloadSettlements()} style={{padding:'8px 14px',background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:9,color:'#94a3b8',cursor:'pointer',fontSize:13,display:'flex',alignItems:'center',gap:5,fontFamily:'inherit'}}>
                    <MdRefresh/> Refresh
                  </button>
                </div>
              </div>

              {/* Stat cards */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10,marginBottom:18}}>
                {[
                  {icon:'⏳',label:'Pending',val:settReqs.filter(r=>r.status==='pending').length,sub:fmtRS(totalPendAmt),c:'#f59e0b'},
                  {icon:'🔄',label:'Processing',val:settReqs.filter(r=>r.status==='processing').length,c:'#818cf8'},
                  {icon:'✅',label:'Disbursed',val:settReqs.filter(r=>r.status==='disbursed').length,sub:fmtRS(totalDisbAmt),c:'#10b981'},
                  {icon:'📅',label:"Today's Payout",val:fmtRS(todayDisbAmt),c:'#00d4aa'},
                  {icon:'❌',label:'Rejected',val:settReqs.filter(r=>r.status==='rejected').length,c:'#f87171'},
                ].map(x=>(
                  <div key={x.label} style={{background:'var(--surface-subtle)',border:`1px solid ${x.c}22`,borderRadius:12,padding:'13px 15px'}}>
                    <div style={{fontSize:18}}>{x.icon}</div>
                    <div style={{fontSize:20,fontWeight:800,color:x.c,marginTop:4}}>{x.val}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>{x.label}</div>
                    {x.sub&&<div style={{fontSize:11,color:x.c,fontWeight:600,marginTop:1}}>{x.sub}</div>}
                  </div>
                ))}
              </div>

              {/* Status tabs */}
              <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
                {STABS.map(t=>{
                  const cnt=t.k==='all'?settReqs.length:settReqs.filter(r=>r.status===t.k).length;
                  const active=settTab===t.k;
                  return(
                    <button key={t.k} onClick={()=>setSettTab(t.k)} style={{padding:'6px 14px',borderRadius:20,border:`1px solid ${active?t.c+'66':'var(--border-color-hover)'}`,cursor:'pointer',fontSize:12,fontWeight:active?700:400,background:active?`${t.c}18`:'var(--surface-subtle)',color:active?t.c:'var(--text-muted)',fontFamily:'inherit'}}>
                      {t.label} ({cnt})
                    </button>
                  );
                })}
              </div>

              {settError && (
                <div style={{ padding: '12px 16px', marginBottom: 14, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#fca5a5', fontSize: 13 }}>
                  Could not load settlement requests: {settError}
                  <button onClick={() => reloadSettlements()} style={{ marginLeft: 12, padding: '4px 10px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 6, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 }}>Retry</button>
                </div>
              )}

              {settLoading&&<div style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}>⏳ Loading…</div>}

              {!settLoading&&displayed.length===0&&(
                <div style={{...S,textAlign:'center',padding:40,color:'var(--text-muted)'}}>
                  <div style={{fontSize:40,marginBottom:12}}>✅</div>
                  <div style={{fontWeight:600}}>No {settTab!=='all'?settTab:''} requests</div>
                </div>
              )}

              {/* Pending/Processing action cards */}
              {!settLoading&&displayed.filter(r=>r.status==='pending'||r.status==='processing').map(req=>{
                const hosp=hospitals.find(h=>h.hospitalId===req.hospitalId);
                const accNum=req.accountNumber||hosp?.accountNumber||'';
                const masked=accNum?'••••••'+accNum.slice(-4):hosp?.accountNumber?'••••••'+hosp.accountNumber.slice(-4):'—';
                const verified=isBankVerified(req)||isBankVerified(hosp);
                return(
                  <div key={req.requestId} style={{background:req.status==='processing'?'rgba(99,102,241,0.05)':'rgba(245,158,11,0.05)',border:`1px solid ${req.status==='processing'?'rgba(99,102,241,0.25)':'rgba(245,158,11,0.25)'}`,borderRadius:14,padding:'18px 20px',marginBottom:12}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12,marginBottom:14}}>
                      <div>
                        <div style={{fontWeight:800,fontSize:22,color:req.status==='processing'?'#818cf8':'#fbbf24'}}>
                          {fmtRS(req.amount)}
                        </div>
                        <div style={{fontWeight:700,fontSize:15,marginTop:3}}>{req.hospitalName||hosp?.name||req.hospitalId}</div>
                        <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2}}>{req.hospitalCity||hosp?.city||''}</div>
                        <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>Raised: {fmtDT(req.createdAt)}</div>
                        {req.status==='processing'&&<span style={{display:'inline-block',marginTop:6,padding:'2px 10px',background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.3)',borderRadius:20,fontSize:11,fontWeight:700,color:'#818cf8'}}>🔄 PROCESSING</span>}
                      </div>
                      <div style={{background:'var(--surface-subtle)',border:'1px solid var(--border-color)',borderRadius:10,padding:'12px 16px',minWidth:200}}>
                        <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6}}>🏦 Bank Account</div>
                        {verified&&<div style={{fontSize:11,color:'#34d399',marginBottom:6,display:'flex',alignItems:'center',gap:4}}><MdVerified style={{fontSize:13}}/> Verified</div>}
                        {!verified&&accNum&&<div style={{fontSize:11,color:'#f59e0b',marginBottom:6}}>⚠️ Not yet verified</div>}
                        <div style={{fontWeight:700,fontSize:13}}>{req.accountHolder||hosp?.accountHolder||'—'}</div>
                        <div style={{fontSize:13,fontFamily:'monospace',color:'var(--text-secondary)',marginTop:3}}>{masked}</div>
                        <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>{req.ifscCode||hosp?.ifscCode||'—'}</div>
                        <div style={{fontSize:12,color:'var(--text-muted)'}}>{req.bankName||hosp?.bankName||'—'}{(req.bankBranch||hosp?.bankBranch)?' · '+(req.bankBranch||hosp?.bankBranch):''}</div>
                        {!verified&&(req.accountNumber||hosp?.accountNumber)&&(
                          <button onClick={()=>verifyBank(hosp||{hospitalId:req.hospitalId})} style={{marginTop:8,padding:'4px 10px',background:'rgba(16,185,129,0.2)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:6,color:'#34d399',cursor:'pointer',fontSize:11,fontWeight:700}}>✅ Verify Bank</button>
                        )}
                      </div>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
                      <input value={disburseNote[req.requestId]||''} onChange={e=>setDisburseNote(p=>({...p,[req.requestId]:e.target.value}))}
                        placeholder="Admin note (e.g. Ref: NEFT2026042300123)"
                        style={{padding:'9px 12px',background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:9,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                      <input value={txnRef[req.requestId]||''} onChange={e=>setTxnRef(p=>({...p,[req.requestId]:e.target.value}))}
                        placeholder="Transaction Ref / UTR Number"
                        style={{padding:'9px 12px',background:'var(--surface-muted)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:9,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                    </div>
                    <div style={{display:'flex',gap:8}}>
                      <button onClick={()=>doAction(req,'disbursed')} disabled={disbursing[req.requestId]}
                        style={{flex:2,padding:'11px',background:'linear-gradient(135deg,#10b981,#059669)',border:'none',borderRadius:10,color:'#fff',fontWeight:800,cursor:'pointer',fontSize:13,fontFamily:'inherit',opacity:disbursing[req.requestId]?0.6:1}}>
                        {disbursing[req.requestId]?'⏳ Saving…':'✅ Mark Disbursed — Transfer Done'}
                      </button>
                      {req.status==='pending'&&(
                        <button onClick={()=>doAction(req,'processing')} disabled={disbursing[req.requestId]}
                          style={{flex:1,padding:'11px',background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.3)',borderRadius:10,color:'#818cf8',fontWeight:700,cursor:'pointer',fontSize:13,fontFamily:'inherit'}}>
                          🔄 Mark Processing
                        </button>
                      )}
                      <button onClick={()=>doAction(req,'rejected')} disabled={disbursing[req.requestId]}
                        style={{padding:'11px 16px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:10,color:'#f87171',fontWeight:700,cursor:'pointer',fontSize:13,fontFamily:'inherit'}}>
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* History table for disbursed/rejected */}
              {!settLoading&&displayed.filter(r=>r.status==='disbursed'||r.status==='rejected').length>0&&(
                <div style={S}>
                  <div style={{...H2,marginBottom:12}}>📜 Settlement History</div>
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Hospital','Amount','Source','Raised','Status','Disbursed','Txn Ref','Note'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                      <tbody>
                        {displayed.filter(r=>r.status==='disbursed'||r.status==='rejected').map(req=>{
                          const hosp=hospitals.find(h=>h.hospitalId===req.hospitalId);
                          const ok=req.status==='disbursed';
                          const srcLabel=req.source==='auto_friday'?'🗓️ Auto':req.source==='admin_manual'?'🛠️ Finance':'🙋 Raised';
                          return(
                            <tr key={req.requestId}>
                              <td style={{...TD,fontWeight:600}}><div>{req.hospitalName||hosp?.name||req.hospitalId}</div><div style={{fontSize:10,color:'var(--border-color-hover)'}}>{req.hospitalCity||hosp?.city}</div></td>
                              <td style={{...TD,color:'#fbbf24',fontWeight:700}}>₹{(Number(req.amount)||0).toLocaleString('en-IN')}</td>
                              <td style={{...TD,fontSize:11,color:'var(--text-secondary)'}}>{srcLabel}</td>
                              <td style={TD}>{fmtDT(req.createdAt)}</td>
                              <td style={TD}><span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:700,background:ok?'rgba(16,185,129,0.12)':'rgba(239,68,68,0.12)',color:ok?'#34d399':'#f87171'}}>{ok?'✅ Disbursed':'❌ Rejected'}</span></td>
                              <td style={{...TD,fontSize:12}}>{ok?fmtDT(req.disbursedAt):'—'}</td>
                              <td style={{...TD,fontSize:12,color:'#818cf8',fontFamily:'monospace'}}>{req.transactionRef||req.txnRef||'—'}</td>
                              <td style={{...TD,color:'var(--text-muted)',fontSize:12}}>{req.adminNote||req.autoNote||'—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })()}


        {/* ── SUPPORT LOOKUP ────────────────────────────────────── */}
        {tab==='support'&&(
          <div>
            <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>🎧 Support Lookup</h1>
            <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:18}}>Search by patient phone, name, email, or booking ID to instantly see all their data</p>

            {/* Search bar */}
            <div style={{position:'relative',marginBottom:20}}>
              <MdSearch style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'#f59e0b',fontSize:20}}/>
              <input
                value={supportQ}
                onChange={e=>{setSupportQ(e.target.value);supportLookup(e.target.value);}}
                placeholder="Type phone number, patient name, email or booking ID…"
                style={{width:'100%',padding:'14px 14px 14px 46px',background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.4)',borderRadius:12,color:'#fff',fontSize:15,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}
              />
              {supportQ&&<button onClick={()=>{setSupportQ('');setSupportResult(null);}} style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:20}}><MdClose/></button>}
            </div>

            {!supportResult&&(
              <div style={{...S,textAlign:'center',padding:40}}>
                <div style={{fontSize:48,marginBottom:12}}>🎧</div>
                <div style={{fontWeight:700,fontSize:16,marginBottom:6}}>Call Center Support Tool</div>
                <div style={{color:'var(--text-muted)',fontSize:13,lineHeight:1.7}}>
                  When a patient calls:<br/>
                  1. Type their phone number or name above<br/>
                  2. See all their bookings, emergencies &amp; account status instantly<br/>
                  3. Identify and resolve issues without asking them to repeat information
                </div>
              </div>
            )}

            {supportLoading&&<div style={{textAlign:'center',padding:30,color:'var(--text-muted)'}}>🔍 Searching…</div>}

            {supportResult&&!supportLoading&&(
              <div>
                <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:14}}>
                  Results for "<strong style={{color:'#fbbf24'}}>{supportResult.q}</strong>" —
                  {supportResult.patients.length} patients · {supportResult.bookings.length} bookings · {supportResult.emergencies.length} emergencies
                </div>

                {/* Patient profiles */}
                {supportResult.patients.map((p,i)=>(
                  <div key={i} style={{...S,border:'1px solid rgba(245,158,11,0.25)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
                      <div style={{width:48,height:48,background:'rgba(245,158,11,0.12)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,fontWeight:800,color:'#fbbf24',flexShrink:0}}>
                        {p.patientName?.charAt(0)||'?'}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:800,fontSize:16}}>{p.patientName||'Unknown'}</div>
                        <div style={{display:'flex',gap:14,flexWrap:'wrap',marginTop:4,fontSize:12,color:'var(--text-muted)'}}>
                          <span><MdPhone style={{fontSize:13}}/> {p.phone||'—'}</span>
                          <span><MdEmail style={{fontSize:13}}/> {p.email||'—'}</span>
                        </div>
                      </div>
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        <div style={{textAlign:'center',padding:'8px 14px',background:'rgba(99,102,241,0.1)',borderRadius:10,border:'1px solid rgba(99,102,241,0.2)'}}>
                          <div style={{fontSize:20,fontWeight:800,color:'#818cf8'}}>{p.bookingCount||0}</div>
                          <div style={{fontSize:10,color:'var(--text-muted)'}}>Bookings</div>
                        </div>
                        <div style={{textAlign:'center',padding:'8px 14px',background:'rgba(16,185,129,0.1)',borderRadius:10,border:'1px solid rgba(16,185,129,0.2)'}}>
                          <div style={{fontSize:20,fontWeight:800,color:'#34d399'}}>{fmtR(p.totalSpent||0)}</div>
                          <div style={{fontSize:10,color:'var(--text-muted)'}}>Total Spent</div>
                        </div>
                        {p.cognitoStatus&&<Badge s={p.cognitoStatus==='CONFIRMED'?'confirmed':'pending'} text={p.cognitoStatus==='CONFIRMED'?'✓ Active':'Inactive'}/>}
                      </div>
                    </div>
                    {/* All bookings for this patient */}
                    {supportResult.bookings.filter(b=>b.patientPhone===p.phone||b.patientName===p.patientName).length>0&&(
                      <div style={{marginTop:14,overflowX:'auto'}}>
                        <div style={{fontSize:11,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:8}}>Booking History</div>
                        <table style={{width:'100%',borderCollapse:'collapse'}}>
                          <thead><tr>{['Booking ID','Hospital','Doctor','Date','Fee','Status'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                          <tbody>
                            {supportResult.bookings.filter(b=>b.patientPhone===p.phone||b.patientName===p.patientName).map(b=>(
                              <tr key={b.bookingId}>
                                <td style={{...TD,fontFamily:'monospace',fontSize:11}}>{b.bookingId}</td>
                                <td style={TD}>{hospitals.find(h=>h.hospitalId===b.hospitalId)?.name||b.hospitalId||'—'}</td>
                                <td style={TD}>{b.doctorName||'—'}</td>
                                <td style={TD}>{fmtD(b.date||b.appointmentDate)} {b.slot?`@ ${b.slot}`:''}</td>
                                <td style={{...TD,color:'#fbbf24',fontWeight:700}}>{fmtR(b.consultationFee||b.fee||0)}</td>
                                <td style={TD}><Badge s={b.status}/></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {/* Emergencies */}
                    {supportResult.emergencies.filter(e=>e.phone===p.phone||e.patientName===p.patientName).map(e=>(
                      <div key={e.alertId} style={{marginTop:10,padding:'10px 14px',background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:10,display:'flex',alignItems:'center',gap:12}}>
                        <span style={{fontSize:20}}>🚨</span>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:700,fontSize:13}}>{e.emergencyType||e.type||'Emergency'}</div>
                          <div style={{fontSize:12,color:'var(--text-muted)'}}>{e.description||'—'} · {fmtD(e.createdAt)} · {hospitals.find(h=>h.hospitalId===e.hospitalId)?.name||'—'}</div>
                        </div>
                        <Badge s={e.status}/>
                      </div>
                    ))}
                  </div>
                ))}

                {/* Bookings without patient match */}
                {supportResult.bookings.filter(b=>!supportResult.patients.some(p=>p.phone===b.patientPhone||p.patientName===b.patientName)).length>0&&(
                  <div style={S}>
                    <div style={{fontWeight:700,marginBottom:10}}>📋 Matching Bookings</div>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Patient','Phone','Hospital','Doctor','Date','Fee','Status'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                      <tbody>
                        {supportResult.bookings.filter(b=>!supportResult.patients.some(p=>p.phone===b.patientPhone||p.patientName===b.patientName)).map(b=>(
                          <tr key={b.bookingId}>
                            <td style={TD}>{b.patientName||'—'}</td>
                            <td style={TD}>{b.patientPhone||'—'}</td>
                            <td style={TD}>{hospitals.find(h=>h.hospitalId===b.hospitalId)?.name||'—'}</td>
                            <td style={TD}>{b.doctorName||'—'}</td>
                            <td style={TD}>{fmtD(b.date)} {b.slot?`@ ${b.slot}`:''}</td>
                            <td style={{...TD,color:'#fbbf24',fontWeight:700}}>{fmtR(b.consultationFee||b.fee||0)}</td>
                            <td style={TD}><Badge s={b.status}/></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {supportResult.patients.length===0&&supportResult.bookings.length===0&&supportResult.emergencies.length===0&&(
                  <div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)'}}>
                    <div style={{fontSize:40,marginBottom:10}}>🔍</div>
                    <div style={{fontWeight:700,marginBottom:6}}>No records found</div>
                    <div style={{fontSize:13}}>Try searching by a different phone number or name</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── CAMPAIGNS (Push Notifications) ───────────────────────── */}
        {tab==='campaigns'&&<CampaignsTab toast_={toast_} S={S} H2={H2} TH={TH} TD={TD}/>}
        {false&&(()=>{
          const [campForm, setCampForm] = React.useState({title:'',body:'',segment:'all',city:''});
          const [sending, setSending] = React.useState(false);
          const [history, setHistory] = React.useState([]);
          const [histLoading, setHistLoading] = React.useState(true);

          React.useEffect(()=>{
            (async()=>{
              setHistLoading(true);
              const r = await adminAPI.getCampaignHistory();
              if(r.data?.campaigns) setHistory(r.data.campaigns);
              setHistLoading(false);
            })();
          },[]);

          const sendCampaign = async()=>{
            if(!campForm.title||!campForm.body) return toast_('Title and message body required',true);
            if(campForm.segment==='city'&&!campForm.city) return toast_('City name required for city segment',true);
            if(!window.confirm(`Send push notification to ${campForm.segment==='all'?'ALL users':campForm.segment==='city'?`users in "${campForm.city}"`:campForm.segment+' users'}?`)) return;
            setSending(true);
            const r = await adminAPI.sendCampaign(campForm);
            if(!r.error){
              toast_(`✅ Campaign sent! ${r.data?.sent||0} delivered, ${r.data?.failed||0} failed`);
              const r2 = await adminAPI.getCampaignHistory(); if(r2.data?.campaigns) setHistory(r2.data.campaigns);
            } else toast_(r.error,true);
            setSending(false);
          };

          return(
            <div>
              <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>📣 Push Campaigns</h1>
              <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:16}}>Send targeted push notifications to patient segments</p>

              {/* Send Campaign Form */}
              <div style={{...S,marginBottom:16,border:'1px solid rgba(99,102,241,0.2)'}}>
                <div style={{...H2,marginBottom:14}}>🚀 New Campaign</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
                  <div style={{gridColumn:'span 2'}}>
                    <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Notification Title *</label>
                    <input value={campForm.title} onChange={e=>setCampForm(p=>({...p,title:e.target.value}))} placeholder="🎉 Special Offer!" style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:15,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div style={{gridColumn:'span 2'}}>
                    <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Message Body *</label>
                    <textarea value={campForm.body} onChange={e=>setCampForm(p=>({...p,body:e.target.value}))} placeholder="Book your appointment today and get 20% off!" rows={3} style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box',resize:'vertical'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Target Segment</label>
                    <select value={campForm.segment} onChange={e=>setCampForm(p=>({...p,segment:e.target.value}))} style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}>
                      <option value="all"      style={{background:'#0F172A',color:'#fff'}}>All Users</option>
                      <option value="city"     style={{background:'#0F172A',color:'#fff'}}>By City</option>
                      <option value="recent"   style={{background:'#0F172A',color:'#fff'}}>Recent (active last 7 days)</option>
                      <option value="inactive" style={{background:'#0F172A',color:'#fff'}}>Inactive (30+ days)</option>
                    </select>
                  </div>
                  {campForm.segment==='city'&&(
                    <div>
                      <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>City Name *</label>
                      <input value={campForm.city} onChange={e=>setCampForm(p=>({...p,city:e.target.value}))} placeholder="Mumbai" style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                    </div>
                  )}
                </div>
                <button onClick={sendCampaign} disabled={sending}
                  style={{padding:'10px 24px',background:'linear-gradient(135deg,#6366f1,#4f46e5)',border:'none',borderRadius:10,color:'#fff',fontWeight:800,cursor:'pointer',fontSize:14,fontFamily:'inherit',opacity:sending?0.5:1}}>
                  {sending?'Sending…':'📣 Send Campaign'}
                </button>
              </div>

              {/* Campaign History */}
              <div style={S}>
                <div style={{...H2,marginBottom:12}}>📜 Campaign History</div>
                {histLoading?<div style={{textAlign:'center',padding:30,color:'var(--text-muted)'}}>Loading…</div>:
                history.length===0?<div style={{textAlign:'center',padding:'30px 0',color:'var(--text-muted)'}}>No campaigns sent yet</div>:(
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Title','Segment','Sent','Failed','Targets','Sent By','Date'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                      <tbody>
                        {history.map(c=>(
                          <tr key={c.campaignId||c.sentAt}>
                            <td style={{...TD,fontWeight:700,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.title}</td>
                            <td style={TD}><Badge s={c.segment==='all'?'active':c.segment==='city'?'pending':'inactive'} text={c.segment==='city'?`🏙️ ${c.city}`:c.segment}/></td>
                            <td style={{...TD,color:'#10b981',fontWeight:700,textAlign:'center'}}>{c.sentCount||0}</td>
                            <td style={{...TD,color:'#f87171',textAlign:'center'}}>{c.failedCount||0}</td>
                            <td style={{...TD,textAlign:'center'}}>{c.targetCount||0}</td>
                            <td style={{...TD,fontSize:11,color:'var(--text-muted)'}}>{c.sentBy||'—'}</td>
                            <td style={{...TD,fontSize:11}}>{c.sentAt?new Date(c.sentAt).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'}):'—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── PROMO CODES ────────────────────────────────────────────── */}
        {tab==='promos'&&<PromosTab toast_={toast_} S={S} H2={H2} TH={TH} TD={TD}/>}
        {false&&(()=>{
          const [promos, setPromos] = React.useState([]);
          const [promoLoading, setPromoLoading] = React.useState(true);
          const [showCreate, setShowCreate] = React.useState(false);
          const [creating, setCreating] = React.useState(false);
          const [pForm, setPForm] = React.useState({ code:'', discountType:'percentage', discountValue:'', maxUses:'', minOrderAmount:'', validFrom:'', validTo:'', description:'' });

          React.useEffect(()=>{
            (async()=>{
              setPromoLoading(true);
              const r = await adminAPI.getPromoCodes();
              if(r.data?.promoCodes) setPromos(r.data.promoCodes);
              setPromoLoading(false);
            })();
          },[]);

          const createPromo = async()=>{
            if(!pForm.code||!pForm.discountValue) return toast_('Code and discount value required',true);
            setCreating(true);
            const r = await adminAPI.createPromoCode({...pForm, discountValue:Number(pForm.discountValue), maxUses:Number(pForm.maxUses)||0, minOrderAmount:Number(pForm.minOrderAmount)||0});
            if(!r.error){
              toast_('✅ Promo code created!');
              setShowCreate(false);
              setPForm({ code:'', discountType:'percentage', discountValue:'', maxUses:'', minOrderAmount:'', validFrom:'', validTo:'', description:'' });
              const r2 = await adminAPI.getPromoCodes(); if(r2.data?.promoCodes) setPromos(r2.data.promoCodes);
            } else toast_(r.error,true);
            setCreating(false);
          };

          const toggleActive = async(code, active)=>{
            await adminAPI.updatePromoCode(code, {active});
            setPromos(p=>p.map(x=>x.code===code?{...x,active}:x));
            toast_(active?'Activated':'Deactivated');
          };

          const deletePromo = async(code)=>{
            if(!window.confirm(`Delete promo code ${code}?`)) return;
            await adminAPI.deletePromoCode(code);
            setPromos(p=>p.filter(x=>x.code!==code));
            toast_('Deleted');
          };

          if(promoLoading) return <div style={{textAlign:'center',padding:60,color:'var(--text-muted)'}}>Loading promo codes…</div>;

          return(
            <div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
                <div>
                  <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>🎟️ Promo Codes</h1>
                  <p style={{color:'var(--text-muted)',fontSize:13}}>Create and manage discount codes for patients</p>
                </div>
                <button onClick={()=>setShowCreate(!showCreate)} style={{padding:'10px 20px',background:'linear-gradient(135deg,#f59e0b,#d97706)',border:'none',borderRadius:10,color:'#000',fontWeight:800,fontSize:14,cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontFamily:'inherit'}}>
                  <MdAdd/> Create Code
                </button>
              </div>

              {showCreate&&(
                <div style={{...S,marginBottom:16,border:'1px solid rgba(245,158,11,0.3)'}}>
                  <div style={{...H2,marginBottom:14}}>✨ New Promo Code</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:12,marginBottom:14}}>
                    <div>
                      <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Code *</label>
                      <input value={pForm.code} onChange={e=>setPForm(p=>({...p,code:e.target.value.toUpperCase()}))} placeholder="HCEN50" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box',letterSpacing:'0.05em'}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Discount Type</label>
                      <select value={pForm.discountType} onChange={e=>setPForm(p=>({...p,discountType:e.target.value}))} style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}>
                        <option value="percentage">Percentage (%)</option>
                        <option value="flat">Flat Amount (₹)</option>
                      </select>
                    </div>
                    <div>
                      <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Discount Value *</label>
                      <input type="number" value={pForm.discountValue} onChange={e=>setPForm(p=>({...p,discountValue:e.target.value}))} placeholder={pForm.discountType==='percentage'?'10':'50'} style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Max Uses (0=unlimited)</label>
                      <input type="number" value={pForm.maxUses} onChange={e=>setPForm(p=>({...p,maxUses:e.target.value}))} placeholder="100" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Min Order ₹</label>
                      <input type="number" value={pForm.minOrderAmount} onChange={e=>setPForm(p=>({...p,minOrderAmount:e.target.value}))} placeholder="0" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Valid From</label>
                      <input type="date" value={pForm.validFrom} onChange={e=>setPForm(p=>({...p,validFrom:e.target.value}))} style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                    </div>
                    <div>
                      <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Valid To</label>
                      <input type="date" value={pForm.validTo} onChange={e=>setPForm(p=>({...p,validTo:e.target.value}))} style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                    </div>
                    <div style={{gridColumn:'span 2'}}>
                      <label style={{fontSize:11,color:'var(--text-muted)',marginBottom:3,display:'block'}}>Description</label>
                      <input value={pForm.description} onChange={e=>setPForm(p=>({...p,description:e.target.value}))} placeholder="First booking discount" style={{width:'100%',padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                    </div>
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button onClick={createPromo} disabled={creating} style={{padding:'8px 20px',background:'linear-gradient(135deg,#10b981,#059669)',border:'none',borderRadius:8,color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit',opacity:creating?0.5:1}}>{creating?'Creating…':'✅ Create Promo Code'}</button>
                    <button onClick={()=>setShowCreate(false)} style={{padding:'8px 16px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'var(--text-muted)',cursor:'pointer',fontSize:13,fontFamily:'inherit'}}>Cancel</button>
                  </div>
                </div>
              )}

              <div style={S}>
                {promos.length===0?(
                  <div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)'}}>
                    <div style={{fontSize:48,marginBottom:12}}>🎟️</div>
                    <div style={{fontWeight:700}}>No promo codes yet</div>
                    <div style={{fontSize:12,marginTop:6}}>Click "Create Code" to get started</div>
                  </div>
                ):(
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                      <thead><tr>{['Code','Type','Value','Used','Max Uses','Min Order','Valid From','Valid To','Status','Actions'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                      <tbody>
                        {promos.map(p=>(
                          <tr key={p.code}>
                            <td style={{...TD,fontWeight:700,fontFamily:'monospace',letterSpacing:'0.05em',color:'#fbbf24'}}>{p.code}</td>
                            <td style={TD}><Badge s={p.discountType==='percentage'?'active':'pending'} text={p.discountType==='percentage'?'%':'₹ Flat'}/></td>
                            <td style={{...TD,fontWeight:700,color:'#10b981'}}>{p.discountType==='percentage'?p.discountValue+'%':'₹'+p.discountValue}</td>
                            <td style={{...TD,textAlign:'center'}}>{p.usedCount||0}</td>
                            <td style={{...TD,textAlign:'center',color:'var(--text-muted)'}}>{p.maxUses>0?p.maxUses:'∞'}</td>
                            <td style={TD}>{p.minOrderAmount>0?'₹'+p.minOrderAmount:'—'}</td>
                            <td style={{...TD,fontSize:11}}>{p.validFrom||'—'}</td>
                            <td style={{...TD,fontSize:11}}>{p.validTo||'—'}</td>
                            <td style={TD}>
                              <span onClick={()=>toggleActive(p.code,!p.active)} style={{cursor:'pointer'}}>
                                {p.active?<Badge s="active" text="Active"/>:<Badge s="inactive" text="Inactive"/>}
                              </span>
                            </td>
                            <td style={TD}>
                              <div style={{display:'flex',gap:4}}>
                                <button onClick={()=>toggleActive(p.code,!p.active)} style={{padding:'3px 8px',background:p.active?'rgba(239,68,68,0.1)':'rgba(16,185,129,0.1)',border:`1px solid ${p.active?'rgba(239,68,68,0.3)':'rgba(16,185,129,0.3)'}`,borderRadius:6,color:p.active?'#f87171':'#34d399',cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>{p.active?'Disable':'Enable'}</button>
                                <button onClick={()=>deletePromo(p.code)} style={{padding:'3px 8px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:6,color:'#f87171',cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {promos.length>0&&<div style={{padding:'8px 14px',borderTop:'1px solid var(--surface-muted)',fontSize:11,color:'var(--border-color-hover)'}}>
                  {promos.length} promo code(s) · {promos.filter(p=>p.active).length} active · {promos.reduce((s,p)=>s+(p.usedCount||0),0)} total uses
                </div>}
              </div>
            </div>
          );
        })()}

        {/* ── PLATFORM SETTINGS (Commission, Payout Rules) ──────── */}
        {tab==='settings'&&<SettingsTab toast_={toast_} hospitals={hospitals} S={S} H2={H2} TH={TH} TD={TD}/>}
        {false&&(()=>{
          const [platCfg, setPlatCfg] = React.useState(null);
          const [hospComms, setHospComms] = React.useState([]);
          const [platLoading, setPlatLoading] = React.useState(true);
          const [saving, setSaving] = React.useState(false);
          const [editHosp, setEditHosp] = React.useState(null);
          const [hospSaving, setHospSaving] = React.useState(false);
          const [form, setForm] = React.useState({});

          React.useEffect(()=>{
            (async()=>{
              setPlatLoading(true);
              const [cfgRes, hcRes] = await Promise.all([
                adminAPI.getPlatformSettings(),
                adminAPI.getHospitalCommissions(),
              ]);
              if(cfgRes.data) { setPlatCfg(cfgRes.data); setForm(cfgRes.data); }
              if(hcRes.data?.hospitals) setHospComms(hcRes.data.hospitals);
              setPlatLoading(false);
            })();
          },[]);

          const savePlatform = async()=>{
            setSaving(true);
            const r = await adminAPI.updatePlatformSettings(form);
            if(r.data?.config) { setPlatCfg(r.data.config); setForm(r.data.config); toast_('✅ Platform settings saved!'); }
            else toast_(r.error||'Failed',true);
            setSaving(false);
          };

          const saveHospComm = async(hId, updates)=>{
            setHospSaving(true);
            const r = await adminAPI.updateHospitalCommission(hId, updates);
            if(!r.error){
              setHospComms(p=>p.map(h=>h.hospitalId===hId?{...h,...updates,customRate:updates.commissionPct!=null}:h));
              setEditHosp(null);
              toast_('✅ Hospital commission updated!');
            } else toast_(r.error,true);
            setHospSaving(false);
          };

          const resetHospComm = async(hId)=>{
            setHospSaving(true);
            const r = await adminAPI.updateHospitalCommission(hId, {commissionWaived:false,gstExempt:false,commissionNotes:''});
            if(!r.error){
              setHospComms(p=>p.map(h=>h.hospitalId===hId?{...h,commissionPct:platCfg?.defaultCommissionPct||2,gstPct:platCfg?.defaultGstPct||18,gstExempt:false,isWaived:false,customRate:false,commissionNotes:''}:h));
              setEditHosp(null);
              toast_('Reset to platform defaults');
            } else toast_(r.error,true);
            setHospSaving(false);
          };

          if(platLoading) return <div style={{textAlign:'center',padding:60,color:'var(--text-muted)'}}>Loading platform settings…</div>;

          return(
            <div>
              <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>⚙️ Platform Settings</h1>
              <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:20}}>Global commission, GST, and payout configuration</p>

              {/* Global Defaults Card */}
              <div style={S}>
                <div style={{...H2,marginBottom:16}}>💰 Global Commission Defaults</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:14,marginBottom:16}}>
                  <div>
                    <label style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,display:'block'}}>Platform Commission %</label>
                    <input type="number" step="0.1" min="0" max="100" value={form.defaultCommissionPct??''} onChange={e=>setForm(p=>({...p,defaultCommissionPct:e.target.value}))}
                      style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:16,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,display:'block'}}>GST on Commission %</label>
                    <input type="number" step="0.1" min="0" max="100" value={form.defaultGstPct??''} onChange={e=>setForm(p=>({...p,defaultGstPct:e.target.value}))}
                      style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:16,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,display:'block'}}>Min Payout Amount (₹)</label>
                    <input type="number" min="0" value={form.minPayoutAmount??''} onChange={e=>setForm(p=>({...p,minPayoutAmount:e.target.value}))}
                      style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:16,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,display:'block'}}>Commission Cap (₹, 0=none)</label>
                    <input type="number" min="0" value={form.commissionCap??''} onChange={e=>setForm(p=>({...p,commissionCap:e.target.value}))}
                      style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:16,fontWeight:700,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{fontSize:12,color:'var(--text-muted)',marginBottom:4,display:'block'}}>Payout Cycle</label>
                    <select value={form.payoutCycle||'on-demand'} onChange={e=>setForm(p=>({...p,payoutCycle:e.target.value}))}
                      style={{width:'100%',padding:'10px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}>
                      <option value="on-demand">On-Demand</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <button onClick={savePlatform} disabled={saving}
                    style={{padding:'10px 24px',background:'linear-gradient(135deg,#f59e0b,#d97706)',border:'none',borderRadius:10,color:'#000',fontWeight:800,cursor:'pointer',fontSize:14,fontFamily:'inherit',opacity:saving?0.5:1}}>
                    {saving?'Saving…':'💾 Save Global Settings'}
                  </button>
                  <span style={{fontSize:12,color:'var(--text-muted)'}}>Effective deduction: {Number(form.defaultCommissionPct||0)}% + {Number(form.defaultGstPct||0)}% GST = {((Number(form.defaultCommissionPct||0))+(Number(form.defaultCommissionPct||0)*Number(form.defaultGstPct||0)/100)).toFixed(2)}%</span>
                </div>
              </div>

              {/* Email Reports */}
              <div style={{...S,marginTop:16}}>
                <div style={{...H2,marginBottom:12}}>📧 Scheduled Email Reports</div>
                <p style={{fontSize:12,color:'var(--text-muted)',marginBottom:14}}>Send revenue & booking summaries to admin emails. Configure EventBridge rules for automation.</p>
                <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                  <button onClick={async()=>{toast_('Sending daily report…');const r=await adminAPI.sendDailyReport();if(!r.error)toast_('✅ Daily report sent!');else toast_(r.error,true);}}
                    style={{padding:'10px 20px',background:'linear-gradient(135deg,#6366f1,#4f46e5)',border:'none',borderRadius:10,color:'#fff',fontWeight:700,cursor:'pointer',fontSize:13,fontFamily:'inherit'}}>
                    📊 Send Daily Report Now
                  </button>
                  <button onClick={async()=>{toast_('Sending weekly report…');const r=await adminAPI.sendWeeklyReport();if(!r.error)toast_('✅ Weekly report sent!');else toast_(r.error,true);}}
                    style={{padding:'10px 20px',background:'linear-gradient(135deg,#10b981,#059669)',border:'none',borderRadius:10,color:'#fff',fontWeight:700,cursor:'pointer',fontSize:13,fontFamily:'inherit'}}>
                    📈 Send Weekly Report Now
                  </button>
                </div>
                <div style={{marginTop:12,fontSize:11,color:'var(--border-color-hover)'}}>
                  💡 Set Lambda env vars: <code style={{background:'var(--surface-muted)',padding:'2px 5px',borderRadius:4}}>REPORT_EMAILS</code> (comma-separated), <code style={{background:'var(--surface-muted)',padding:'2px 5px',borderRadius:4}}>SES_FROM_EMAIL</code>, <code style={{background:'var(--surface-muted)',padding:'2px 5px',borderRadius:4}}>SES_REGION</code>
                </div>
              </div>

              {/* Per-Hospital Commission Table */}
              <div style={{...S,marginTop:16}}>
                <div style={{...H2,marginBottom:12}}>🏥 Per-Hospital Commission Rates</div>
                <p style={{fontSize:12,color:'var(--text-muted)',marginBottom:14}}>Override commission for individual hospitals. Blank = uses global default ({platCfg?.defaultCommissionPct||2}%)</p>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr>{['Hospital','City','Status','Commission %','GST %','Waived','Custom','Notes','Actions'].map(c=><th key={c} style={TH}>{c}</th>)}</tr></thead>
                    <tbody>
                      {hospComms.filter(h=>h.status==='active').map(h=>{
                        const isEditing = editHosp?.hospitalId===h.hospitalId;
                        return(
                          <tr key={h.hospitalId} style={{background:isEditing?'rgba(245,158,11,0.06)':'transparent'}}>
                            <td style={{...TD,fontWeight:600}}>{h.name||h.hospitalId}</td>
                            <td style={TD}>{h.city||'—'}</td>
                            <td style={TD}><Badge s={h.status}/></td>
                            <td style={TD}>
                              {isEditing?<input type="number" step="0.1" min="0" max="100" value={editHosp.commissionPct??''} onChange={e=>setEditHosp(p=>({...p,commissionPct:e.target.value}))} style={{width:60,padding:'4px 6px',background:'var(--border-color-hover)',border:'1px solid rgba(245,158,11,0.4)',borderRadius:6,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none'}}/>
                              :<span style={{color:h.isWaived?'#34d399':h.customRate?'#818cf8':'var(--text-secondary)',fontWeight:h.customRate?700:400}}>{h.isWaived?'0 (waived)':h.commissionPct+'%'}</span>}
                            </td>
                            <td style={TD}>
                              {isEditing?<label style={{display:'flex',alignItems:'center',gap:4,fontSize:12}}><input type="checkbox" checked={editHosp.gstExempt||false} onChange={e=>setEditHosp(p=>({...p,gstExempt:e.target.checked}))}/> Exempt</label>
                              :<span style={{color:h.gstExempt?'#34d399':'var(--text-muted)'}}>{h.gstExempt?'Exempt':h.gstPct+'%'}</span>}
                            </td>
                            <td style={TD}>
                              {isEditing?<label style={{display:'flex',alignItems:'center',gap:4,fontSize:12}}><input type="checkbox" checked={editHosp.commissionWaived||false} onChange={e=>setEditHosp(p=>({...p,commissionWaived:e.target.checked}))}/> Waive</label>
                              :h.isWaived?<span style={{color:'#34d399',fontWeight:700}}>✓ Waived</span>:'—'}
                            </td>
                            <td style={TD}>{h.customRate?<span style={{color:'#818cf8',fontSize:11}}>Custom</span>:<span style={{color:'var(--text-muted)',fontSize:11}}>Default</span>}</td>
                            <td style={TD}>
                              {isEditing?<input value={editHosp.commissionNotes||''} onChange={e=>setEditHosp(p=>({...p,commissionNotes:e.target.value}))} placeholder="Reason…" style={{width:120,padding:'4px 6px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:6,color:'#fff',fontSize:12,fontFamily:'inherit',outline:'none'}}/>
                              :<span style={{fontSize:11,color:'var(--text-muted)'}}>{h.commissionNotes||'—'}</span>}
                            </td>
                            <td style={TD}>
                              {isEditing?(
                                <div style={{display:'flex',gap:4}}>
                                  <button onClick={()=>saveHospComm(h.hospitalId,{commissionPct:editHosp.commissionWaived?undefined:Number(editHosp.commissionPct),gstExempt:editHosp.gstExempt,commissionWaived:editHosp.commissionWaived,commissionNotes:editHosp.commissionNotes||''})} disabled={hospSaving} style={{padding:'4px 8px',background:'rgba(16,185,129,0.15)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:6,color:'#34d399',cursor:'pointer',fontSize:11,fontWeight:700,fontFamily:'inherit'}}>Save</button>
                                  <button onClick={()=>resetHospComm(h.hospitalId)} disabled={hospSaving} style={{padding:'4px 8px',background:'rgba(99,102,241,0.1)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:6,color:'#818cf8',cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>Reset</button>
                                  <button onClick={()=>setEditHosp(null)} style={{padding:'4px 8px',background:'var(--surface-subtle)',border:'1px solid var(--border-color-hover)',borderRadius:6,color:'var(--text-muted)',cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>Cancel</button>
                                </div>
                              ):(
                                <button onClick={()=>setEditHosp({...h})} style={{padding:'4px 10px',background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:6,color:'#fbbf24',cursor:'pointer',fontSize:11,fontWeight:600,fontFamily:'inherit'}}>
                                  <MdEdit style={{fontSize:13}}/> Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── TEAM LOGINS (owner-only) ──────────────────────────────── */}
        {tab==='team'&&isOwner&&<TeamTab S={S} toast={(m,type)=>toast_(m,type==='error')}/>}

        {/* ── MY SECURITY / MFA (every admin) ───────────────────────── */}
        {tab==='security'&&<SecurityTab S={S} admin={admin} toast={(m,type)=>toast_(m,type==='error')}/>}

        {/* ── AUDIT LOG (owner-only) ────────────────────────────────── */}
        {tab==='audit'&&isOwner&&<AuditTab S={S}/>}
        {false&&(()=>{
          const [auditLogs, setAuditLogs] = React.useState([]);
          const [auditLoading, setAuditLoading] = React.useState(true);
          const [auditFilter, setAuditFilter] = React.useState('');
          const [auditFrom, setAuditFrom] = React.useState('');

          React.useEffect(()=>{
            (async()=>{
              setAuditLoading(true);
              const params = {};
              if(auditFilter) params.action = auditFilter;
              if(auditFrom) params.from = auditFrom;
              const r = await adminAPI.getAuditLog(params);
              if(r.data?.logs) setAuditLogs(r.data.logs);
              setAuditLoading(false);
            })();
          },[auditFilter, auditFrom]);

          const ACTION_TYPES = ['LOGIN_SUCCESS','LOGIN_FAILED','HOSPITAL_CREATED','HOSPITAL_UPDATED','HOSPITAL_DELETED','SETTLEMENT_RECORDED','SETTLEMENT_REQUEST_DISBURSED','SETTLEMENT_REQUEST_REJECTED','SETTLEMENT_REQUEST_PROCESSING','PLATFORM_SETTINGS_UPDATED','REFUND_MODE_CHANGED'];
          const actionColor = (a) => {
            if(a.includes('FAILED')||a.includes('DELETED')||a.includes('REJECTED')) return '#f87171';
            if(a.includes('SUCCESS')||a.includes('CREATED')||a.includes('DISBURSED')) return '#34d399';
            if(a.includes('UPDATED')||a.includes('CHANGED')||a.includes('RECORDED')) return '#818cf8';
            return '#fbbf24';
          };
          const fmtDT = s => s ? new Date(s).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true}) : '—';
          const parseDetails = d => { try { return typeof d === 'string' ? JSON.parse(d) : d; } catch{ return d; } };

          return(
            <div>
              <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>🛡️ Audit Log</h1>
              <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:18}}>Complete history of all admin actions for accountability and security</p>

              <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
                <select value={auditFilter} onChange={e=>setAuditFilter(e.target.value)}
                  style={{padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none'}}>
                  <option value="">All Actions</option>
                  {ACTION_TYPES.map(a=><option key={a} value={a}>{a.replace(/_/g,' ')}</option>)}
                </select>
                <input type="date" value={auditFrom} onChange={e=>setAuditFrom(e.target.value)}
                  style={{padding:'8px 12px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:8,color:'#fff',fontSize:13,fontFamily:'inherit',outline:'none'}}/>
                {(auditFilter||auditFrom)&&<button onClick={()=>{setAuditFilter('');setAuditFrom('');}} style={{padding:'8px 12px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,color:'#f87171',cursor:'pointer',fontSize:12,fontFamily:'inherit'}}>Clear Filters</button>}
              </div>

              {auditLoading ? <div style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}>Loading audit logs…</div> : (
                <div style={S}>
                  {auditLogs.length===0 ? (
                    <div style={{textAlign:'center',padding:'40px 0',color:'var(--text-muted)'}}>
                      <div style={{fontSize:48,marginBottom:12}}>🛡️</div>
                      <div style={{fontWeight:700,fontSize:15}}>No audit logs yet</div>
                      <div style={{fontSize:12,marginTop:6}}>Admin actions will appear here once the HCEN_AuditLog table is created in DynamoDB</div>
                    </div>
                  ) : (
                    <div style={{maxHeight:'70vh',overflowY:'auto'}}>
                      {auditLogs.map((log,i) => {
                        const details = parseDetails(log.details);
                        return (
                          <div key={log.logId||i} style={{padding:'12px 14px',borderBottom:'1px solid var(--surface-subtle)',display:'flex',gap:14,alignItems:'flex-start'}}>
                            <div style={{width:8,height:8,borderRadius:'50%',background:actionColor(log.actionType||''),flexShrink:0,marginTop:6}}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                <span style={{padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:700,background:`${actionColor(log.actionType||'')}15`,color:actionColor(log.actionType||''),fontFamily:'monospace'}}>{(log.actionType||'UNKNOWN').replace(/_/g,' ')}</span>
                                <span style={{fontSize:11,color:'var(--text-muted)'}}>{fmtDT(log.createdAt)}</span>
                                <span style={{fontSize:11,color:'var(--border-color-hover)',marginLeft:'auto'}}>{log.adminEmail||'admin'}</span>
                              </div>
                              {details && typeof details === 'object' && (
                                <div style={{marginTop:6,fontSize:12,color:'var(--text-muted)',fontFamily:'monospace',lineHeight:1.5}}>
                                  {Object.entries(details).slice(0,6).map(([k,v])=>(
                                    <span key={k} style={{marginRight:14}}><span style={{color:'var(--text-muted)'}}>{k}:</span> <span style={{color:'#a5b4fc'}}>{typeof v==='object'?JSON.stringify(v):String(v)}</span></span>
                                  ))}
                                </div>
                              )}
                              {details && typeof details === 'string' && <div style={{marginTop:4,fontSize:12,color:'var(--text-muted)'}}>{details}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{padding:'10px 14px',borderTop:'1px solid var(--surface-muted)',fontSize:11,color:'var(--border-color-hover)'}}>
                    Showing {auditLogs.length} entries{auditFilter?` filtered by ${auditFilter}`:''}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </main>


      {/* ── CREATE HOSPITAL MODAL ─────────────────────────────── */}
      {showAdd&&(()=>{
        const STEPS = [{n:1,icon:'📋',label:'Basic Info'},{n:2,icon:'📍',label:'Location'},{n:3,icon:'🏥',label:'Departments'},{n:4,icon:'🔐',label:'Review'}];
        const stepOk = {
          1: addForm.name.trim() && addForm.email.trim() && addForm.phone.trim() && addForm.emergencyPhone.trim(),
          2: !!locSelected,
          3: selectedDepts.length > 0,
          4: true,
        };
        return (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.88)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:12}}>
            <div style={{background:'#0f172a',border:'1px solid rgba(245,158,11,0.3)',borderRadius:20,width:'100%',maxWidth:620,maxHeight:'94vh',overflowY:'auto',display:'flex',flexDirection:'column'}}>
              {/* Header */}
              <div style={{padding:'20px 24px 0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{color:'#fbbf24',fontWeight:800,fontSize:18}}>🏥 Register New Hospital</div>
                  <div style={{fontSize:12,color:'var(--text-muted)',marginTop:2}}>⚠️ Patient lives depend on accurate data. Verify all information carefully.</div>
                </div>
                <button onClick={()=>{setShowAdd(false);resetCreateForm();}} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:20,padding:4}}><MdClose/></button>
              </div>
              {/* Step indicator */}
              <div style={{display:'flex',alignItems:'center',padding:'16px 24px 0',gap:0}}>
                {STEPS.map((s,i) => (
                  <React.Fragment key={s.n}>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                      <div style={{width:30,height:30,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800,
                        background: createStep>s.n ? '#10b981' : createStep===s.n ? '#fbbf24' : 'var(--border-color)',
                        color: createStep>=s.n ? '#000' : 'var(--text-muted)',border: createStep===s.n ? '2px solid #fbbf24' : '1px solid transparent'}}>
                        {createStep>s.n ? '✓' : s.n}
                      </div>
                      <div style={{fontSize:9,color:createStep===s.n?'#fbbf24':'var(--text-muted)',fontWeight:createStep===s.n?700:400,whiteSpace:'nowrap'}}>{s.icon} {s.label}</div>
                    </div>
                    {i<3&&<div style={{flex:1,height:2,background:createStep>s.n?'#10b981':'var(--border-color)',margin:'0 4px',marginBottom:18}}/>}
                  </React.Fragment>
                ))}
              </div>
              {/* Step content */}
              <div style={{padding:'16px 24px',flex:1}}>
                {/* STEP 1: BASIC INFO */}
                {createStep===1&&(
                  <div>
                    <div style={{fontWeight:700,fontSize:15,marginBottom:12,color:'#fbbf24'}}>📋 Basic Hospital Information</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 14px'}}>
                      <div style={{gridColumn:'1/-1'}}><SInput label="Hospital Name *" value={addForm.name} onChange={e=>setAddForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Apollo Hospitals, Hyderabad" color="rgba(251,191,36,0.4)"/></div>
                      <div style={{gridColumn:'1/-1'}}><SInput label="Official Email Address *" value={addForm.email} onChange={e=>setAddForm(p=>({...p,email:e.target.value}))} type="email" placeholder="contact@apollohospitals.com" color="rgba(251,191,36,0.4)"/></div>
                      <SInput label="🚨 Emergency Phone * (critical!)" value={addForm.emergencyPhone} onChange={e=>setAddForm(p=>({...p,emergencyPhone:e.target.value}))} type="tel" placeholder="108 or +91-40-XXXX" note="This number is shown to patients for emergencies" color="rgba(239,68,68,0.5)"/>
                      <SInput label="Main Phone" value={addForm.phone} onChange={e=>setAddForm(p=>({...p,phone:e.target.value}))} type="tel" placeholder="+91-40-1234-5678"/>
                    </div>
                    <SLabel t="Hospital Type"/>
                    <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap'}}>
                      {['Government','Private','Trust / Charitable','Corporate','Teaching / Academic'].map(t=>(
                        <Chip key={t} label={t} selected={addForm.type===t} onClick={()=>setAddForm(p=>({...p,type:t}))} color="#6366f1"/>
                      ))}
                    </div>
                  </div>
                )}
                {/* STEP 2: LOCATION (interactive map picker) */}
                {createStep===2&&(
                  <div>
                    <div style={{fontWeight:700,fontSize:15,marginBottom:4,color:'#60a5fa'}}>📍 Hospital Location</div>
                    <div style={{fontSize:12,color:'rgba(239,68,68,0.8)',background:'rgba(239,68,68,0.07)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,padding:'8px 12px',marginBottom:12}}>
                      ⚠️ CRITICAL: The location you select determines which patients see this hospital in the HCEN app. Wrong location = patients cannot find this hospital. Use the map below — search, click, drag the pin, or use your current location.
                    </div>
                    <LocationMapPicker
                      value={locSelected ? { lat: locSelected.lat, lng: locSelected.lng, address: locSelected.display, city: locSelected.city, district: locSelected.district, state: locSelected.state } : null}
                      onChange={(loc) => {
                        if (!loc) { setLocSelected(null); setLocQuery(''); return; }
                        setLocSelected({
                          display: loc.address || loc.display || '',
                          short:   loc.address ? loc.address.split(',').slice(0,3).join(', ') : '',
                          lat:     loc.lat,
                          lng:     loc.lng,
                          city:    loc.city || '',
                          district:loc.district || '',
                          state:   loc.state || '',
                          country: 'India',
                        });
                        setLocQuery(loc.address || '');
                      }}
                      height={360}
                    />
                  </div>
                )}
                {/* STEP 3: DEPARTMENTS */}
                {createStep===3&&(
                  <div>
                    <div style={{fontWeight:700,fontSize:15,marginBottom:4,color:'#a855f7'}}>🏥 Medical Departments & Facilities</div>
                    <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:12}}>Select all applicable — shown to patients in the HCEN app. {selectedDepts.length} selected.</div>
                    <div style={{fontWeight:600,fontSize:12,color:'var(--text-secondary)',marginBottom:6}}>📋 DEPARTMENTS (select all that apply) *</div>
                    <div style={{display:'flex',flexWrap:'wrap',marginBottom:14}}>
                      {ALL_DEPTS.map(d=><Chip key={d} label={d} selected={selectedDepts.includes(d)} onClick={()=>toggleDept(d)} color="#a855f7"/>)}
                    </div>
                    <div style={{display:'flex',gap:8,marginBottom:14}}>
                      <input value={otherDept} onChange={e=>setOtherDept(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&otherDept.trim()){toggleDept(otherDept.trim());setOtherDept('');}}}
                        placeholder="Other department — type and press Enter..."
                        style={{flex:1,padding:'8px 12px',background:'var(--surface-subtle)',border:'1px solid rgba(168,85,247,0.3)',borderRadius:8,color:'#fff',fontSize:12,fontFamily:'inherit',outline:'none'}}/>
                      <button onClick={()=>{if(otherDept.trim()){toggleDept(otherDept.trim());setOtherDept('');}}}
                        style={{padding:'8px 14px',background:'#a855f7',border:'none',borderRadius:8,color:'#fff',fontWeight:700,cursor:'pointer'}}>+ Add</button>
                    </div>
                    <div style={{borderTop:'1px solid var(--surface-muted)',paddingTop:12,marginTop:4}}>
                      <div style={{fontWeight:600,fontSize:12,color:'var(--text-secondary)',marginBottom:6}}>🔬 KEY EQUIPMENT & FACILITIES</div>
                      <div style={{display:'flex',flexWrap:'wrap'}}>
                        {ALL_EQUIP.map(f=><Chip key={f} label={f} selected={selectedFacs.includes(f)} onClick={()=>toggleFacility(f)} color="#00d4aa"/>)}
                      </div>
                    </div>
                  </div>
                )}
                {/* STEP 4: REVIEW & SECURITY */}
                {createStep===4&&(
                  <div>
                    <div style={{fontWeight:700,fontSize:15,marginBottom:12,color:'#34d399'}}>🔐 Security & Final Review</div>
                    <SInput label="Login Password (leave blank to auto-generate)" value={addForm.loginPassword}
                      onChange={e=>setAddForm(p=>({...p,loginPassword:e.target.value}))}
                      placeholder="Leave blank → auto-generates e.g. HCEN@Apollo2026"
                      note="Auto-generated passwords are shown after creation. Staff can change later."/>
                    <div style={{background:'rgba(0,0,0,0.3)',border:'1px solid var(--border-color)',borderRadius:12,padding:14,marginTop:12,fontSize:12}}>
                      <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:'#fbbf24'}}>📋 Review Summary</div>
                      {[
                        ['Name', addForm.name||'—'],['Email', addForm.email||'—'],
                        ['Emergency', addForm.emergencyPhone||'—'],['Phone', addForm.phone||'—'],
                        ['Type', addForm.type],
                        ['Location', locSelected?.short||'—'],
                        ['Coordinates', locSelected?`${locSelected.lat?.toFixed(4)}, ${locSelected.lng?.toFixed(4)}`:'—'],
                        ['Departments', selectedDepts.length+' selected'],
                        ['Facilities / Equipment', selectedFacs.length+' selected'],
                      ].map(([l,v])=>(
                        <div key={l} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid var(--surface-subtle)',fontSize:12}}>
                          <span style={{color:'var(--text-muted)'}}>{l}</span>
                          <span style={{color:'#f1f5f9',fontWeight:500,textAlign:'right',maxWidth:'65%'}}>{v}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{padding:'10px 14px',background:'rgba(239,68,68,0.06)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:10,marginTop:12,fontSize:12,color:'#fca5a5',lineHeight:1.5}}>
                      ⚠️ <strong>Patient Safety Notice:</strong> Wrong location data, incorrect emergency numbers, or missing departments can directly harm patients. Please verify all details before creating.
                    </div>
                  </div>
                )}
              </div>
              {/* Navigation buttons */}
              <div style={{padding:'14px 24px 20px',display:'flex',gap:10,borderTop:'1px solid var(--surface-muted)'}}>
                {createStep>1&&(
                  <button onClick={()=>setCreateStep(p=>p-1)} style={{flex:1,padding:11,background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:10,color:'#94a3b8',cursor:'pointer',fontFamily:'inherit',fontWeight:600}}>
                    ← Back
                  </button>
                )}
                {createStep<4&&(
                  <button onClick={()=>{if(!stepOk[createStep]){toast_('Please fill all required fields for this step',true);return;}setCreateStep(p=>p+1);}}
                    style={{flex:2,padding:11,background:stepOk[createStep]?'linear-gradient(135deg,#6366f1,#4f46e5)':'var(--surface-muted)',border:'none',borderRadius:10,color:stepOk[createStep]?'#fff':'var(--text-muted)',fontWeight:700,cursor:'pointer',fontFamily:'inherit',fontSize:14,transition:'all 0.15s'}}>
                    Continue →
                  </button>
                )}
                {createStep===4&&(
                  <button onClick={createHospital} disabled={loading}
                    style={{flex:2,padding:11,background:'linear-gradient(135deg,#f59e0b,#d97706)',border:'none',borderRadius:10,color:'#000',fontWeight:800,cursor:'pointer',fontFamily:'inherit',fontSize:14}}>
                    {loading?'Creating Hospital...':'🏥 Create Hospital'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── CREATED CREDENTIALS BOX ───────────────────────────── */}
      {createdCreds&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.88)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{background:'#0f172a',border:'2px solid rgba(16,185,129,0.5)',borderRadius:20,padding:32,width:'100%',maxWidth:460,boxShadow:'0 0 40px rgba(16,185,129,0.12)'}}>
            <div style={{textAlign:'center',marginBottom:20}}>
              <div style={{fontSize:42,marginBottom:6}}>🏥</div>
              <h2 style={{color:'#34d399',fontWeight:800,fontSize:22,marginBottom:4}}>Hospital Created!</h2>
              <div style={{color:'#fbbf24',fontWeight:700,fontSize:15,marginBottom:6}}>{createdCreds.name}</div>
              <div style={{color:'var(--text-muted)',fontSize:12}}>Save & share these credentials with hospital staff</div>
            </div>
            <div style={{background:'rgba(0,0,0,0.3)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:14,padding:20,marginBottom:18}}>
              {[
                {l:'Hospital Name',v:createdCreds.name,c:'#f1f5f9'},
                {l:'Hospital ID',v:createdCreds.hospitalId,c:'#94a3b8'},
                {l:'Login Email / Username',v:createdCreds.email,c:'#60a5fa'},
                {l:'Login Password',v:createdCreds.loginPassword,c:'#fbbf24',big:true},
              ].map(({l,v,c,big})=>(
                <div key={l} style={{marginBottom:12}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:3}}>{l}</div>
                  <div onClick={()=>{try{navigator.clipboard.writeText(v||'');}catch(e){}}}
                    title="Click to copy"
                    style={{fontFamily:'monospace',fontSize:big?18:13,color:c,background:'var(--surface-subtle)',padding:'9px 13px',borderRadius:8,cursor:'pointer',wordBreak:'break-all',border:'1px solid var(--surface-muted)',transition:'background 0.15s'}}
                    onMouseOver={e=>e.currentTarget.style.background='var(--surface-muted)'}
                    onMouseOut={e=>e.currentTarget.style.background='var(--surface-subtle)'}
                  >{v||'—'} <span style={{fontSize:10,color:'var(--border-color-hover)',marginLeft:6}}>📋 copy</span></div>
                </div>
              ))}
            </div>
            <div style={{padding:'10px 14px',background:'rgba(245,158,11,0.07)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:10,marginBottom:18,fontSize:12,color:'#fcd34d',lineHeight:1.6}}>
              ⚠️ Staff log in at the <strong style={{color:'#fff'}}>HCEN Partner Website / Dashboard</strong> using above credentials. Password can be changed later by staff. <strong>Click any field to copy it.</strong>
            </div>
            <button onClick={()=>setCreatedCreds(null)} style={{width:'100%',padding:13,background:'linear-gradient(135deg,#10b981,#059669)',border:'none',borderRadius:12,color:'#fff',fontWeight:800,cursor:'pointer',fontFamily:'inherit',fontSize:15}}>
              ✅ Done — I Copied the Credentials
            </button>
          </div>
        </div>
      )}

      {showAddDoc&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{background:'#0f172a',border:'1px solid rgba(0,212,170,0.25)',borderRadius:16,padding:24,width:'100%',maxWidth:460,maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <h2 style={{color:'#00d4aa',fontWeight:800,fontSize:18}}>Add Doctor 👨‍⚕️</h2>
              <button onClick={()=>setShowAddDoc(false)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:20}}><MdClose/></button>
            </div>
            <div style={{fontSize:12,color:'var(--text-muted)',marginBottom:14}}>Adding to: <strong style={{color:'#f1f5f9'}}>{selectedH?.name}</strong></div>
            <form onSubmit={createDoctor}>
              <Inp label="Doctor Name"         value={docForm.name}           onChange={e=>setDocForm(p=>({...p,name:e.target.value}))}           placeholder="Dr. Ramesh Kumar" required/>
              <Inp label="Specialization"      value={docForm.specialization} onChange={e=>setDocForm(p=>({...p,specialization:e.target.value}))} placeholder="Cardiologist" required/>
              <Inp label="Phone"               value={docForm.phone}          onChange={e=>setDocForm(p=>({...p,phone:e.target.value}))}          type="tel" placeholder="+91 98765 43210"/>
              <Inp label="Experience"          value={docForm.experience}     onChange={e=>setDocForm(p=>({...p,experience:e.target.value}))}     placeholder="10 years"/>
              <Inp label="Consultation Fee (₹)" value={docForm.consultationFee} onChange={e=>setDocForm(p=>({...p,consultationFee:e.target.value}))} type="number" placeholder="500" required/>
              <div style={{display:'flex',gap:8}}>
                <button type="button" onClick={()=>setShowAddDoc(false)} style={{flex:1,padding:10,background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:8,color:'#94a3b8',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
                <button type="submit" style={{flex:2,padding:10,background:'linear-gradient(135deg,#00d4aa,#059669)',border:'none',borderRadius:8,color:'#000',fontWeight:800,cursor:'pointer',fontFamily:'inherit',fontSize:14}}>
                  👨‍⚕️ Add Doctor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── CONFIRM DELETE MODAL ──────────────────────────────── */}
      {confirmDel&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div style={{background:'#0f172a',border:'1px solid rgba(239,68,68,0.3)',borderRadius:15,padding:26,width:'100%',maxWidth:370,textAlign:'center'}}>
            <div style={{fontSize:40,marginBottom:10}}>{confirmDel.type==='ipunlink'?'🔓':'⚠️'}</div>
            <h3 style={{color:'#f87171',fontWeight:800,fontSize:17,marginBottom:7}}>{confirmDel.type==='ipunlink'?'Confirm Unlink':'Confirm Delete'}</h3>
            <p style={{color:'var(--text-muted)',fontSize:13,marginBottom:18}}>
              {confirmDel.type==='ipunlink'
                ?<>Unlink <strong style={{color:'#f1f5f9'}}>{confirmDel.item?.name||confirmDel.item?.hospitalId}</strong> from its paired IP hospital? Patients will lose cross-network routing.</>
                :<>Delete <strong style={{color:'#f1f5f9'}}>{confirmDel.item?.name||confirmDel.item?.patientName||'this item'}</strong>? This cannot be undone.</>}
            </p>
            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>setConfirmDel(null)} style={{flex:1,padding:10,background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:8,color:'#94a3b8',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
              <button onClick={doDelete} style={{flex:1,padding:10,background:'rgba(239,68,68,0.9)',border:'none',borderRadius:8,color:'#fff',fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>{confirmDel.type==='ipunlink'?'Unlink':'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// AdminOffersTab — Health Offers management for admin
// ═══════════════════════════════════════════════════════════════════
const INDIA_STATES_OFF = [
  'All States','Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
  'Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala',
  'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha',
  'Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh',
  'Uttarakhand','West Bengal','Delhi','Jammu and Kashmir','Ladakh',
];
const BADGE_OPTS_OFF = [
  {v:'FREE',label:'🆓 FREE',color:'#00b894'},
  {v:'DISCOUNT',label:'💰 DISCOUNT',color:'#f59e0b'},
  {v:'CAMP',label:'🏕️ CAMP',color:'#4f8ef7'},
  {v:'EVENT',label:'📅 EVENT',color:'#a855f7'},
  {v:'OFFER',label:'🎁 OFFER',color:'#ec4899'},
];
const badgeColorOff=(b)=>BADGE_OPTS_OFF.find(x=>x.v===b)?.color||'#888';

function AdminOffersTab(){
  const[offers,setOffers]=React.useState([]);
  const[loading,setLoading]=React.useState(true);
  const[loadError,setLoadError]=React.useState(null);
  const[filterSt,setFilterSt]=React.useState('all');
  const[filterCity,setFilterCity]=React.useState('');
  const[filterState,setFilterState]=React.useState('All States');
  const[creating,setCreating]=React.useState(false);
  const[newOffer,setNewOffer]=React.useState({title:'',description:'',imageUrl:'',badge:'FREE',city:'',district:'',state:'Andhra Pradesh',hospitalName:'',validUntil:''});
  const[saving,setSaving]=React.useState(false);
  const[toast,setToast]=React.useState('');
  const[approving,setApproving]=React.useState({});
  const[rejectNote,setRejectNote]=React.useState({});
  const[editing,setEditing]=React.useState(null);    // offer object being edited
  const[editForm,setEditForm]=React.useState(null);  // local edit-modal state
  const[editSaving,setEditSaving]=React.useState(false);
  const[lightbox,setLightbox]=React.useState(null);  // imageUrl for full-screen view
  const showToast=(msg)=>{setToast(msg);setTimeout(()=>setToast(''),3500);};
  const openEdit=(o)=>{setEditing(o);setEditForm({title:o.title||'',description:o.description||'',imageUrl:o.imageUrl||'',badge:o.badge||'FREE',city:o.city||'',district:o.district||'',state:o.state||'Andhra Pradesh',hospitalName:o.hospitalName||'',validUntil:o.validUntil||'',displayOrder:o.displayOrder??999});};
  const saveEdit=async()=>{
    if(!editing||!editForm)return;
    setEditSaving(true);
    const badge=BADGE_OPTS_OFF.find(b=>b.v===editForm.badge);
    const r=await adminOffersAPI.update(editing.offerId,{...editForm,badgeColor:badge?.color||'#00b894'});
    if(r.error){showToast(`❌ ${r.error}`);}else{showToast('✅ Offer updated');setEditing(null);setEditForm(null);load();}
    setEditSaving(false);
  };
  const revertToPending=async(id)=>{
    if(!window.confirm('Move this offer back to "Pending Review"? It will be hidden from patients until you approve again.'))return;
    const r=await adminOffersAPI.update(id,{status:'pending',isActive:false,adminNote:''});
    if(r.error){showToast(`❌ ${r.error}`);return;}
    showToast('↩️ Moved back to Pending');load();
  };
  const load=React.useCallback(async()=>{
    setLoading(true);setLoadError(null);
    const params={};
    if(filterSt!=='all')params.status=filterSt;
    if(filterCity)params.city=filterCity;
    if(filterState!=='All States')params.state=filterState;
    const r=await adminOffersAPI.list(params);
    if(r.error){
      setLoadError(r.error);
      setOffers([]);
    }else{
      setOffers(Array.isArray(r.data?.offers)?r.data.offers:[]);
    }
    setLoading(false);
  },[filterSt,filterCity,filterState]);
  React.useEffect(()=>{load();},[load]);
  const approve=async(id)=>{
    setApproving(p=>({...p,[id]:'a'}));
    const r=await adminOffersAPI.update(id,{status:'approved',isActive:true});
    if(r.error){showToast(`❌ ${r.error}`);}else{showToast('✅ Approved & published!');load();}
    setApproving(p=>({...p,[id]:null}));
  };
  const reject=async(id)=>{
    setApproving(p=>({...p,[id]:'r'}));
    const r=await adminOffersAPI.update(id,{status:'rejected',isActive:false,adminNote:rejectNote[id]||'Not suitable'});
    if(r.error){showToast(`❌ ${r.error}`);}else{showToast('Rejected');load();}
    setApproving(p=>({...p,[id]:null}));
  };
  const toggleActive=async(id,cur)=>{
    const r=await adminOffersAPI.update(id,{isActive:!cur});
    if(r.error){showToast(`❌ ${r.error}`);return;}
    showToast(!cur?'Visible to patients':'Hidden');load();
  };
  const setDisplayOrder=async(id,order)=>{
    const n=Math.max(1,Math.min(999,Number(order)||999));
    const r=await adminOffersAPI.update(id,{displayOrder:n});
    if(r.error){showToast(`❌ ${r.error}`);return;}
    showToast(`📌 Hero position set to #${n}`);load();
  };
  const del=async(id)=>{
    if(!window.confirm('Delete?'))return;
    const r=await adminOffersAPI.remove(id);
    if(r.error){showToast(`❌ ${r.error}`);return;}
    showToast('Deleted');load();
  };
  const create=async(e)=>{
    e.preventDefault();
    if(!newOffer.title||!newOffer.city){showToast('Title & City required');return;}
    setSaving(true);
    const badge=BADGE_OPTS_OFF.find(b=>b.v===newOffer.badge);
    const r=await adminOffersAPI.create({...newOffer,badgeColor:badge?.color||'#00b894'});
    if(r.error){showToast(`❌ ${r.error}`);setSaving(false);return;}
    showToast('✅ Published!');setCreating(false);
    setNewOffer({title:'',description:'',imageUrl:'',badge:'FREE',city:'',district:'',state:'Andhra Pradesh',hospitalName:'',validUntil:''});
    load();setSaving(false);
  };
  const pending=offers.filter(o=>o.status==='pending');
  const approved=offers.filter(o=>o.status==='approved');
  const rejected=offers.filter(o=>o.status==='rejected');
  const I={width:'100%',padding:'9px 12px',background:'var(--surface-muted)',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:12,fontFamily:'inherit',boxSizing:'border-box'};
  const LBL={fontSize:10,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'};
  return(
    <div>
      {toast&&<div style={{position:'fixed',top:20,right:20,background:'#1e293b',border:'1px solid rgba(0,212,170,0.4)',borderRadius:10,padding:'12px 20px',color:'var(--text-primary)',fontSize:13,zIndex:9999,boxShadow:'0 8px 24px rgba(0,0,0,0.4)'}}>{toast}</div>}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:800,marginBottom:3}}>📣 Health Offers</h1>
          <p style={{color:'var(--text-muted)',fontSize:13}}>{pending.length} pending · {approved.length} live · {rejected.length} rejected</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={load} style={{padding:'8px 14px',background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:8,color:'#94a3b8',cursor:'pointer',display:'flex',alignItems:'center',gap:5,fontSize:13,fontFamily:'inherit'}}><MdRefresh/>Refresh</button>
          <button onClick={()=>setCreating(p=>!p)} style={{padding:'8px 16px',background:'linear-gradient(135deg,#00d4aa,#00b894)',border:'none',borderRadius:8,color:'#000',fontWeight:700,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',gap:5,fontFamily:'inherit'}}><MdCampaign/>{creating?'Cancel':'+ Create Offer'}</button>
        </div>
      </div>
      {loadError && (
        <div style={{ padding: '12px 16px', marginBottom: 14, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, color: '#fca5a5', fontSize: 13 }}>
          Could not load offers: {loadError}
          <button onClick={load} style={{ marginLeft: 12, padding: '4px 10px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 6, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 }}>Retry</button>
        </div>
      )}
      {creating&&(
        <div style={{background:'rgba(0,212,170,0.04)',border:'1px solid rgba(0,212,170,0.2)',borderRadius:14,padding:22,marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:14,color:'#00d4aa'}}>✨ Create & Publish Offer (Admin — No Review Needed)</div>
          <form onSubmit={create} style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <div style={{gridColumn:'1/-1'}}><div style={LBL}>Title *</div><input value={newOffer.title} onChange={e=>setNewOffer(p=>({...p,title:e.target.value}))} required placeholder="e.g. Free General Checkup Camp" style={I}/></div>
            <div style={{gridColumn:'1/-1'}}><div style={LBL}>Description</div><textarea value={newOffer.description} onChange={e=>setNewOffer(p=>({...p,description:e.target.value}))} rows={2} placeholder="Walk-in welcome · No appointment needed..." style={{...I,resize:'vertical'}}/></div>
            <div><div style={LBL}>Badge</div><select value={newOffer.badge} onChange={e=>setNewOffer(p=>({...p,badge:e.target.value}))} style={{...I,background:'#1e293b'}}>{BADGE_OPTS_OFF.map(b=><option key={b.v} value={b.v}>{b.label}</option>)}</select></div>
            <div><div style={LBL}>Hospital Name</div><input value={newOffer.hospitalName} onChange={e=>setNewOffer(p=>({...p,hospitalName:e.target.value}))} placeholder="Leave blank for HCEN" style={I}/></div>
            <div><div style={LBL}>City *</div><input value={newOffer.city} onChange={e=>setNewOffer(p=>({...p,city:e.target.value}))} required placeholder="e.g. Anantapur" style={I}/></div>
            <div><div style={LBL}>District</div><input value={newOffer.district} onChange={e=>setNewOffer(p=>({...p,district:e.target.value}))} placeholder="e.g. Anantapur" style={I}/></div>
            <div><div style={LBL}>State</div><select value={newOffer.state} onChange={e=>setNewOffer(p=>({...p,state:e.target.value}))} style={{...I,background:'#1e293b'}}>{INDIA_STATES_OFF.filter(s=>s!=='All States').map(s=><option key={s} value={s}>{s}</option>)}</select></div>
            <div><div style={LBL}>Valid Until</div><input type="date" value={newOffer.validUntil} onChange={e=>setNewOffer(p=>({...p,validUntil:e.target.value}))} style={I}/></div>
            <div style={{gridColumn:'1/-1'}}><div style={LBL}>Banner Image URL</div><input value={newOffer.imageUrl} onChange={e=>setNewOffer(p=>({...p,imageUrl:e.target.value}))} placeholder="https://..." style={I}/></div>
            <div style={{gridColumn:'1/-1'}}><button type="submit" disabled={saving} style={{padding:'10px 22px',background:'linear-gradient(135deg,#00d4aa,#00b894)',border:'none',borderRadius:8,color:'#000',fontWeight:800,fontSize:14,cursor:saving?'not-allowed':'pointer',fontFamily:'inherit'}}>{saving?'⏳ Publishing...':'🚀 Publish Offer Now'}</button></div>
          </form>
        </div>
      )}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:18,alignItems:'center'}}>
        {[{v:'all',lbl:`All (${offers.length})`,bg:'#4f8ef7'},{v:'pending',lbl:`⏳ Pending (${pending.length})`,bg:'#f59e0b'},{v:'approved',lbl:`✅ Live (${approved.length})`,bg:'#00d4aa'},{v:'rejected',lbl:`❌ Rejected (${rejected.length})`,bg:'#ef4444'}].map(s=>(
          <button key={s.v} onClick={()=>setFilterSt(s.v)} style={{padding:'6px 13px',borderRadius:20,border:'none',cursor:'pointer',fontFamily:'inherit',fontSize:12,fontWeight:700,background:filterSt===s.v?s.bg:'var(--surface-muted)',color:filterSt===s.v?'#000':'#94a3b8'}}>{s.lbl}</button>
        ))}
        <div style={{marginLeft:'auto',display:'flex',gap:8}}>
          <input value={filterCity} onChange={e=>setFilterCity(e.target.value)} placeholder="Filter city..." style={{padding:'6px 11px',background:'var(--surface-muted)',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:12,fontFamily:'inherit',width:130}}/>
          <select value={filterState} onChange={e=>setFilterState(e.target.value)} style={{padding:'6px 10px',background:'#1e293b',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:12,fontFamily:'inherit'}}>{INDIA_STATES_OFF.map(s=><option key={s} value={s}>{s}</option>)}</select>
        </div>
      </div>
      {loading?<div style={{textAlign:'center',padding:60,color:'var(--text-muted)'}}>Loading...</div>:offers.length===0?<div style={{textAlign:'center',padding:60,color:'var(--text-muted)'}}><MdCampaign style={{fontSize:48,display:'block',margin:'0 auto 12px',opacity:0.3}}/><div style={{fontSize:15,fontWeight:600}}>No offers found</div><div style={{fontSize:13,marginTop:6}}>Hospitals submit offers for review, or create one above.</div></div>:(
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:13}}>
          {offers.map(o=>{
            const color=badgeColorOff(o.badge);
            const isPending=o.status==='pending',isApproved=o.status==='approved',isRejected=o.status==='rejected';
            return(
              <div key={o.offerId} style={{background:'var(--surface-subtle)',border:`1px solid ${color}22`,borderRadius:13,overflow:'hidden',borderLeft:`3px solid ${color}`,display:'flex',flexDirection:'column'}}>
                {/* Image with click-to-zoom + graceful placeholder fallback */}
                <div onClick={()=>o.imageUrl&&setLightbox(o.imageUrl)} style={{position:'relative',height:130,background:`linear-gradient(135deg, ${color}22, ${color}05)`,cursor:o.imageUrl?'zoom-in':'default',overflow:'hidden'}}>
                  {o.imageUrl?(
                    <img src={o.imageUrl} alt={o.title} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}
                      onError={e=>{e.target.style.display='none';e.target.parentElement.dataset.failed='1';}}/>
                  ):null}
                  {!o.imageUrl&&(
                    <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:5,color:`${color}aa`}}>
                      <MdImageNotSupported style={{fontSize:34}}/>
                      <span style={{fontSize:10,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase'}}>No poster uploaded</span>
                    </div>
                  )}
                  {o.imageUrl&&<div style={{position:'absolute',top:6,right:6,background:'rgba(0,0,0,0.6)',color:'#fff',fontSize:9,fontWeight:700,padding:'3px 7px',borderRadius:4,letterSpacing:'0.04em'}}>🔍 ZOOM</div>}
                </div>
                <div style={{padding:'12px 14px',display:'flex',flexDirection:'column',flex:1}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:7,gap:6}}>
                    <span style={{background:color,color:'#000',fontSize:10,fontWeight:800,padding:'2px 9px',borderRadius:20}}>{o.badge}</span>
                    <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,background:isPending?'rgba(245,158,11,0.15)':isApproved?'rgba(0,212,170,0.15)':'rgba(239,68,68,0.15)',color:isPending?'#f59e0b':isApproved?'#00d4aa':'#ef4444'}}>
                      {isPending?'⏳ REVIEW':isApproved?o.isActive?'✅ LIVE':'🔇 HIDDEN':'❌ REJECTED'}
                    </span>
                  </div>
                  <div style={{fontWeight:700,fontSize:13,color:'#f1f5f9',marginBottom:3}}>{o.title}</div>
                  {o.description&&<div style={{fontSize:11,color:'var(--text-muted)',marginBottom:6,lineHeight:1.5}}>{o.description}</div>}
                  <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:9}}>
                    <span style={{fontSize:10,color:'var(--text-muted)'}}>🏥 {o.hospitalName||'HCEN'}</span>
                    {o.city&&<span style={{fontSize:10,background:'var(--surface-subtle)',padding:'1px 7px',borderRadius:10,color:'var(--text-muted)'}}>📍 {o.city}{o.district?` · ${o.district}`:''}{o.state&&o.state!=='All States'?`, ${o.state}`:''}</span>}
                    {o.validUntil&&<span style={{fontSize:10,color:'var(--text-muted)'}}>Until {o.validUntil}</span>}
                    <span style={{fontSize:10,color:'var(--border-color-hover)'}}>via {o.postedBy==='admin'?'Admin':'Hospital'}</span>
                  </div>
                  {isRejected&&o.adminNote&&<div style={{fontSize:10,color:'#f87171',background:'rgba(239,68,68,0.08)',padding:'5px 9px',borderRadius:6,marginBottom:7}}>ℹ️ {o.adminNote}</div>}
                  <div style={{flex:1}}/>
                  {/* Always-available actions: Edit + status-specific buttons */}
                  {isPending&&(
                    <div>
                      <input value={rejectNote[o.offerId]||''} onChange={e=>setRejectNote(p=>({...p,[o.offerId]:e.target.value}))} placeholder="Rejection reason (optional)..." style={{width:'100%',padding:'6px 9px',background:'var(--surface-subtle)',border:'1px solid var(--surface-muted)',borderRadius:6,color:'#f1f5f9',fontSize:11,fontFamily:'inherit',marginBottom:7,boxSizing:'border-box'}}/>
                      <div style={{display:'flex',gap:6,marginBottom:6}}>
                        <button onClick={()=>approve(o.offerId)} disabled={approving[o.offerId]==='a'} style={{flex:1,padding:'7px',background:'rgba(0,212,170,0.15)',border:'1px solid rgba(0,212,170,0.4)',borderRadius:7,color:'#00d4aa',fontWeight:700,cursor:'pointer',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',gap:4,fontFamily:'inherit'}}><MdThumbUp/>{approving[o.offerId]==='a'?'...':'Approve & Publish'}</button>
                        <button onClick={()=>reject(o.offerId)} disabled={approving[o.offerId]==='r'} style={{flex:1,padding:'7px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:7,color:'#ef4444',fontWeight:700,cursor:'pointer',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',gap:4,fontFamily:'inherit'}}><MdThumbDown/>{approving[o.offerId]==='r'?'...':'Reject'}</button>
                      </div>
                      <button onClick={()=>openEdit(o)} style={{width:'100%',padding:'7px',background:'rgba(79,142,247,0.1)',border:'1px solid rgba(79,142,247,0.3)',borderRadius:7,color:'#4f8ef7',fontWeight:600,cursor:'pointer',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',gap:5,fontFamily:'inherit'}}><MdEdit/>Modify Before Approving</button>
                    </div>
                  )}
                  {isApproved&&(
                    <>
                      {o.isActive&&(
                        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8,padding:'8px 10px',background:'rgba(79,142,247,0.08)',borderRadius:8,border:'1px solid rgba(79,142,247,0.2)'}}>
                          <span style={{fontSize:10,color:'var(--text-muted)',fontWeight:700}}>HERO #</span>
                          <span style={{fontSize:13,fontWeight:800,color:'#4f8ef7',minWidth:28,textAlign:'center'}}>{o.displayOrder??'—'}</span>
                          <button onClick={()=>setDisplayOrder(o.offerId,1)} title="Pin as first banner" style={{padding:'4px 8px',background:'rgba(0,212,170,0.15)',border:'1px solid rgba(0,212,170,0.35)',borderRadius:6,color:'#00d4aa',fontWeight:700,cursor:'pointer',fontSize:10,fontFamily:'inherit'}}>Pin #1</button>
                          <button onClick={()=>setDisplayOrder(o.offerId,(o.displayOrder||999)-1)} style={{padding:'4px 8px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:6,color:'#94a3b8',cursor:'pointer',fontSize:12,fontFamily:'inherit'}}>↑</button>
                          <button onClick={()=>setDisplayOrder(o.offerId,(o.displayOrder||999)+1)} style={{padding:'4px 8px',background:'var(--surface-muted)',border:'1px solid var(--surface-strong)',borderRadius:6,color:'#94a3b8',cursor:'pointer',fontSize:12,fontFamily:'inherit'}}>↓</button>
                        </div>
                      )}
                      <div style={{display:'flex',gap:6,marginBottom:6}}>
                        <button onClick={()=>toggleActive(o.offerId,o.isActive)} style={{flex:1,padding:'7px',background:o.isActive?'rgba(245,158,11,0.1)':'rgba(0,212,170,0.1)',border:`1px solid ${o.isActive?'rgba(245,158,11,0.3)':'rgba(0,212,170,0.3)'}`,borderRadius:7,color:o.isActive?'#f59e0b':'#00d4aa',fontWeight:600,cursor:'pointer',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',gap:4,fontFamily:'inherit'}}>{o.isActive?<><MdPublicOff/>Hide</>:<><MdPublic/>Show</>}</button>
                        <button onClick={()=>openEdit(o)} style={{flex:1,padding:'7px',background:'rgba(79,142,247,0.1)',border:'1px solid rgba(79,142,247,0.3)',borderRadius:7,color:'#4f8ef7',fontWeight:600,cursor:'pointer',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',gap:4,fontFamily:'inherit'}}><MdEdit/>Edit</button>
                      </div>
                      <div style={{display:'flex',gap:6}}>
                        <button onClick={()=>revertToPending(o.offerId)} style={{flex:1,padding:'7px',background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:7,color:'#f59e0b',fontWeight:600,cursor:'pointer',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',gap:4,fontFamily:'inherit'}}><MdRefresh/>Revert</button>
                        <button onClick={()=>del(o.offerId)} style={{padding:'7px 12px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.15)',borderRadius:7,color:'#ef4444',cursor:'pointer',fontSize:13,display:'flex',alignItems:'center',fontFamily:'inherit'}}><MdDelete/></button>
                      </div>
                    </>
                  )}
                  {isRejected&&(
                    <>
                      <div style={{display:'flex',gap:6,marginBottom:6}}>
                        <button onClick={()=>approve(o.offerId)} style={{flex:1,padding:'7px',background:'rgba(0,212,170,0.12)',border:'1px solid rgba(0,212,170,0.3)',borderRadius:7,color:'#00d4aa',fontWeight:700,cursor:'pointer',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',gap:4,fontFamily:'inherit'}}><MdThumbUp/>Reconsider & Approve</button>
                        <button onClick={()=>openEdit(o)} style={{padding:'7px 12px',background:'rgba(79,142,247,0.1)',border:'1px solid rgba(79,142,247,0.3)',borderRadius:7,color:'#4f8ef7',fontWeight:600,cursor:'pointer',fontSize:11,display:'flex',alignItems:'center',fontFamily:'inherit'}}><MdEdit/></button>
                      </div>
                      <div style={{display:'flex',gap:6}}>
                        <button onClick={()=>revertToPending(o.offerId)} style={{flex:1,padding:'7px',background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:7,color:'#f59e0b',fontWeight:600,cursor:'pointer',fontSize:11,fontFamily:'inherit'}}>↩️ Move to Pending</button>
                        <button onClick={()=>del(o.offerId)} style={{padding:'7px 12px',background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.15)',borderRadius:7,color:'#ef4444',cursor:'pointer',fontSize:13,display:'flex',alignItems:'center',fontFamily:'inherit'}}><MdDelete/></button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* ── Image Lightbox ── */}
      {lightbox&&(
        <div onClick={()=>setLightbox(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',cursor:'zoom-out',padding:20}}>
          <button onClick={()=>setLightbox(null)} style={{position:'absolute',top:18,right:18,background:'var(--border-color-hover)',border:'1px solid var(--text-muted)',borderRadius:'50%',width:40,height:40,color:'#fff',cursor:'pointer',fontSize:20,display:'flex',alignItems:'center',justifyContent:'center'}}><MdClose/></button>
          <img src={lightbox} alt="Poster" style={{maxWidth:'95%',maxHeight:'95%',objectFit:'contain',borderRadius:8,boxShadow:'0 20px 60px rgba(0,0,0,0.8)'}} onClick={e=>e.stopPropagation()}/>
        </div>
      )}
      {/* ── Edit Offer Modal ── */}
      {editing&&editForm&&(
        <div onClick={()=>!editSaving&&setEditing(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9998,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'40px 20px',overflowY:'auto'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#0f1a2e',border:'1px solid rgba(0,212,170,0.3)',borderRadius:14,padding:24,width:'100%',maxWidth:680,boxShadow:'0 20px 60px rgba(0,0,0,0.6)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
              <div>
                <div style={{fontSize:17,fontWeight:800,color:'#f1f5f9',display:'flex',alignItems:'center',gap:8}}><MdEdit style={{color:'#4f8ef7'}}/>Edit Offer</div>
                <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3}}>Modify any field before approval or to fix a published offer.</div>
              </div>
              <button onClick={()=>!editSaving&&setEditing(null)} style={{background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:'50%',width:32,height:32,color:'#94a3b8',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}><MdClose/></button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:11}}>
              <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'}}>Title *</div><input value={editForm.title} onChange={e=>setEditForm(p=>({...p,title:e.target.value}))} style={{width:'100%',padding:'9px 12px',background:'var(--surface-muted)',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:13,fontFamily:'inherit',boxSizing:'border-box'}}/></div>
              <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'}}>Description</div><textarea value={editForm.description} onChange={e=>setEditForm(p=>({...p,description:e.target.value}))} rows={3} style={{width:'100%',padding:'9px 12px',background:'var(--surface-muted)',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:13,fontFamily:'inherit',boxSizing:'border-box',resize:'vertical'}}/></div>
              <div><div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'}}>Badge</div><select value={editForm.badge} onChange={e=>setEditForm(p=>({...p,badge:e.target.value}))} style={{width:'100%',padding:'9px 12px',background:'#1e293b',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:13,fontFamily:'inherit',boxSizing:'border-box'}}>{BADGE_OPTS_OFF.map(b=><option key={b.v} value={b.v}>{b.label}</option>)}</select></div>
              <div><div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'}}>Hospital Name</div><input value={editForm.hospitalName} onChange={e=>setEditForm(p=>({...p,hospitalName:e.target.value}))} style={{width:'100%',padding:'9px 12px',background:'var(--surface-muted)',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:13,fontFamily:'inherit',boxSizing:'border-box'}}/></div>
              <div><div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'}}>City *</div><input value={editForm.city} onChange={e=>setEditForm(p=>({...p,city:e.target.value}))} style={{width:'100%',padding:'9px 12px',background:'var(--surface-muted)',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:13,fontFamily:'inherit',boxSizing:'border-box'}}/></div>
              <div><div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'}}>District</div><input value={editForm.district} onChange={e=>setEditForm(p=>({...p,district:e.target.value}))} style={{width:'100%',padding:'9px 12px',background:'var(--surface-muted)',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:13,fontFamily:'inherit',boxSizing:'border-box'}}/></div>
              <div><div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'}}>State</div><select value={editForm.state} onChange={e=>setEditForm(p=>({...p,state:e.target.value}))} style={{width:'100%',padding:'9px 12px',background:'#1e293b',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:13,fontFamily:'inherit',boxSizing:'border-box'}}>{INDIA_STATES_OFF.filter(s=>s!=='All States').map(s=><option key={s} value={s}>{s}</option>)}</select></div>
              <div><div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'}}>Valid Until</div><input type="date" value={editForm.validUntil} onChange={e=>setEditForm(p=>({...p,validUntil:e.target.value}))} style={{width:'100%',padding:'9px 12px',background:'var(--surface-muted)',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:13,fontFamily:'inherit',boxSizing:'border-box'}}/></div>
              <div style={{gridColumn:'1/-1'}}><div style={{fontSize:10,color:'var(--text-muted)',marginBottom:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'}}>Poster Image URL</div><input value={editForm.imageUrl} onChange={e=>setEditForm(p=>({...p,imageUrl:e.target.value}))} placeholder="https://..." style={{width:'100%',padding:'9px 12px',background:'var(--surface-muted)',border:'1px solid var(--border-color)',borderRadius:8,color:'#f1f5f9',fontSize:13,fontFamily:'inherit',boxSizing:'border-box'}}/></div>
              {editForm.imageUrl&&(
                <div style={{gridColumn:'1/-1'}}>
                  <div style={{fontSize:10,color:'var(--text-muted)',marginBottom:6,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em'}}>Poster Preview</div>
                  <img src={editForm.imageUrl} alt="Preview" style={{width:'100%',maxHeight:180,objectFit:'cover',borderRadius:8,border:'1px solid var(--border-color)'}} onError={e=>{e.target.style.opacity='0.2';}}/>
                </div>
              )}
            </div>
            <div style={{display:'flex',gap:9,marginTop:18,justifyContent:'flex-end'}}>
              <button onClick={()=>!editSaving&&setEditing(null)} disabled={editSaving} style={{padding:'10px 20px',background:'var(--surface-muted)',border:'1px solid var(--border-color-hover)',borderRadius:8,color:'#94a3b8',cursor:editSaving?'not-allowed':'pointer',fontWeight:600,fontSize:13,fontFamily:'inherit'}}>Cancel</button>
              <button onClick={saveEdit} disabled={editSaving||!editForm.title} style={{padding:'10px 22px',background:'linear-gradient(135deg,#4f8ef7,#3b82f6)',border:'none',borderRadius:8,color:'#fff',fontWeight:800,fontSize:13,cursor:editSaving?'not-allowed':'pointer',fontFamily:'inherit',display:'flex',alignItems:'center',gap:6}}><MdSave/>{editSaving?'Saving...':'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
